// Noyau — le juge de la feuille de brouillon : juste ? justifiée ? utile ? (CONTRACT §5)
//
// • juste      : la ligne est équivalente à la référence du modèle (le départ si c'est une relation,
//                sinon la réponse, les grandeurs données fixées à leur valeur) — equiv.js ;
// • justifiée  : une chaîne de 1 à 3 gestes (règles `rewrite` + builtins du moteur compilé) mène d'une
//                ligne gardée à la ligne écrite, comparaison en forme légère — recherche en faisceau
//                bornée (profondeur 3, faisceau, budget de nœuds), candidats ordonnés par distance ;
// • utile      : ligne du corrigé, ou réponse, ou « dette » (mesure ci-dessous) strictement plus petite
//                que celle de la ligne d'où elle vient.
// Aucune règle métier ici : tout vient de engine.rules / engine.builtins et du modèle de l'exercice.

import {
  parse,
  toText,
  toLatex,
  ParseError,
  withFunctions,
  withIdentifiers,
  withoutConstants,
} from './syntax.js';
import { num, symbols, isLeaf, Q } from './ast.js';
import {
  light,
  keyOf,
  rewriteAll,
  features,
  freeCandidates,
  foldAll,
  prepareRule,
  STRATEGIES,
  roles,
  isDer,
  isLim,
  hasInf,
  hasSide,
  calcNorm,
  opsCount,
  rulePreimages,
  rewriteOnce,
  calcCollectKey,
  intGap,
  visit,
} from './rewrite.js';
import {
  applyAdvice,
  toolChoicesFor,
  toolsFor,
  paletteFor,
  PROBES,
  probe,
  neutralPairFor,
  atomsOf as numAtomsOf,
  chunksOf,
} from './pedagogy.js';
import {
  equivRelation,
  isRelation,
  relationParts,
  makePred,
  evaluate,
  toF,
  exactValue,
  hasExtended,
  holds,
} from './equiv.js';
import { expandModelPreds } from './model-preds.js';
import {
  createTableSheet,
  judgeTableLine,
  tableDecisions,
  tableStartText,
} from './table-sheets.js';
import { createAdvState, advStartText, judgeAdvLine, advDecisions } from './progress-sheets.js';
import { createMatrixSheet, judgeMatrixLine, matrixStartText } from './matrix-sheets.js';
import {
  createPythonSheet,
  judgePythonLine,
  pythonHasAnswer,
  pythonStartText,
} from './python-sheets.js';
import {
  LEGACY_ANALYSIS_PLUGINS,
  createLegacyAnalysisSheet,
  judgeLegacyAnalysisLine,
  legacyAnalysisHasAnswer,
  legacyAnalysisStartText,
} from './analysis-legacy-sheets.js';
import {
  judgeLegacyReplayLine,
  legacyReplaySort,
  legacyReplayStartText,
} from './legacy-replay-sheets.js';
import { adaptAnalysisModel, definitionOf, allHeads } from './analysis-format.js';
import {
  isSignTableText,
  parseSignTable,
  expectedSignTable,
  expectedVarTable,
  checkSignTable,
  checkVarRow,
  claimTruth,
  decisionsOf,
} from './sign-tables.js';
import {
  splitModulus,
  splitChain,
  splitAssignments,
  intValue,
  modValue,
  isPrimeBig,
  isPrimeProduct,
  isEuclid,
  mod as imod,
} from './integers.js';
import {
  parseProp,
  makeFigure,
  metricTable,
  propTrue,
  sameProp,
  ruleFor,
  computeJustified,
} from './geometry.js';
import { cvalue, ceq, splitOr, CX_PROBES, complexGesture, opsOf as cxOps } from './complexes.js';
import {
  splitLimitLine,
  extractSmallO,
  sameLimit,
  trueLimit,
  opsLimit,
  limitPoint,
  holdsNear,
  opsOf,
  occurrences,
  limNum,
  opsLimitEnv,
} from './limits.js';
import {
  unitsOf,
  unitIdentifiers,
  groupJuxtaposed,
  quantityOf,
  sameDim,
  sameQuantity,
  dimensionName,
  unitSignature,
  signatureKey,
  ratioHint,
} from './quantities.js';
import { indeterminateOf, varOf, limitSym, pointOf } from './calculus.js';
import {
  readClaims,
  isNumeric,
  relHolds,
  claimDecisions,
  claimMemberKeys,
  knownValues,
  atInstance,
  speaksOfAt,
  fnEqual,
  divisorNumbers,
  divisorPairKeys,
  divByZero,
} from './claims.js';

const now = () => (globalThis.performance?.now ? performance.now() : Date.now());
const KEPT = new Set(['step', 'goal', 'useless', 'unjustified']);
// depth : profondeur des chaînes quand le moteur ne déclare pas de niveaux ; maxDepth : plafond du noyau
// (gestes implicites compris) quand il en déclare (CONTRACT §11).
export const LIMITS = { depth: 3, maxDepth: 4, beam: 14, beamDeep: 10, budget: 3000, horaCap: 12 };

const MSG = {
  goal: 'Tu tiens la réponse !',
  dup: 'Rien de nouveau : cette ligne est déjà écrite.',
  noProgress: 'Juste, mais ça ne t’avance pas.',
  unjustified: 'Juste, mais d’où ça vient ? Une étape à la fois.',
  unjustifiedGoal: 'C’est la réponse, mais d’où vient-elle ? Écris les étapes.',
  notRelation: 'Écris une ligne complète, avec « = » (ou <, ≤…).',
  wrong: {
    solve: 'Ce n’est pas juste : cette ligne n’a pas les mêmes solutions.',
    simplify: 'Ce n’est pas égal à l’expression de départ.',
    evaluate: 'Ce n’est pas la bonne valeur. Vérifie ton calcul.',
    default: 'Ce n’est pas juste.',
  },
};

// ── Parties ───────────────────────────────────────────────────────────────
// Un exercice « lignes » en plusieurs parties : model.kernel est la partie 0, model.parts[p − 1] la partie p
// ({ title, kernel, traps, preds, symbols }). partExercise → l'exercice vu par cette partie (à passer à createSheet).
export const partsCount = (exercise) => 1 + ((exercise.model ?? exercise).parts?.length || 0);
export function partExercise(exercise, p) {
  if (!p) return exercise;
  const model = exercise.model ?? exercise;
  const part = model.parts[p - 1];
  // symboles : ceux de l'exercice (f fonction, données) complétés / précisés par ceux de la partie
  const m = {
    ...model,
    kernel: part.kernel,
    traps: part.traps ?? [],
    preds: part.preds ?? [],
    symbols: { ...(model.symbols || {}), ...(part.symbols || {}) },
  };
  return exercise.model ? { ...exercise, model: m } : m;
}

// ── Feuille ───────────────────────────────────────────────────────────────
export function createSheet(engine, exercise, opts = {}) {
  // systèmes d'équations, hérédité d'une récurrence, encadrements : l'ancien moteur complet, par rejeu
  const raw = exercise.model ?? exercise;
  if (legacyReplaySort(raw)) return createReplaySheet(engine, raw);
  // format publié des feuilles d'analyse → notation native (« f′(x) = (E)′ »)
  const model = adaptAnalysisModel(raw);
  // calcul matriciel (plugin matrix) : sa propre lecture des matrices « [1, 2 ; 3, 4] », avant tout le reste
  if (model.kernel?.plugin === 'matrix') return createMatSheet(engine, model);
  // trace d'un programme Python (plugin python) : son propre lecteur de lignes et son interpréteur
  if (model.kernel?.plugin === 'python') return createProgSheet(engine, model);
  // équations différentielles, intégrales, dérivées partielles, sommes de Riemann : juges de l'ancien moteur
  if (LEGACY_ANALYSIS_PLUGINS.includes(model.kernel?.plugin)) return createAnaSheet(engine, model);
  // fonctions abstraites de l'exercice (symbols.f.kind = "function", lot E2) : la feuille a sa propre vue du
  // moteur, dont la table lit « f(x) » comme un appel ; toutes les API la reprennent via state.ctx.engine
  const fnNames = Object.entries(model.symbols ?? {})
    .filter(([, s]) => s?.kind === 'function')
    .map(([n]) => n);
  if (fnNames.length) engine = { ...engine, table: withFunctions(engine.table, fnNames) };
  // une grandeur de l'exercice qui porte le nom d'une constante (« e ») se lit comme une lettre ordinaire
  const constNames = Object.keys(model.symbols ?? {}).filter(
    (n) => engine.table.syntax.constants[n],
  );
  if (constNames.length) engine = { ...engine, table: withoutConstants(engine.table, constNames) };
  // noms de plusieurs lettres de l'exercice (« AC », « Ec », « P(A∩B) », « f(2) ») : un identifiant chacun
  const idNames = modelIdentifiers(model, engine.table);
  // feuille d'analyse : toute tête « u(x) », « F′(x) » se lit comme un nom (l'élève peut nommer ce qu'il veut)
  if (model.kernel?.analysis?.heads) idNames.push(...model.kernel.analysis.heads);
  // feuille « grandeurs et unités » (lot M5) : les unités du pack se lisent comme des noms
  const units = model.units || model.kernel?.goal?.kind === 'convert' ? unitsOf(engine) : null;
  if (units) idNames.push(...unitIdentifiers(units));
  if (idNames.length) engine = { ...engine, table: withIdentifiers(engine.table, idNames) };
  const table = engine.table;
  const lv = resolveLevel(engine, opts.level ?? exercise.level);
  const kernel = model.kernel ?? {};
  const goal = kernel.goal ?? { kind: 'solve', target: model.goal?.[0] };
  const target = goal.target ?? model.goal?.[0] ?? null;
  // feuille « remplir un tableau » (plugin tables, lot I8) : un tableau « ⊟{…} » et des affirmations
  if (kernel.plugin === 'tables') return createTabSheet(engine, model, kernel, goal, target);
  // tableau d'avancement (plugin avancement, lot P2) : une réaction, le tableau « ⊡{…} », des affirmations
  if (kernel.plugin === 'avancement') return createAdvSheet(engine, model, kernel, goal, target);
  // étude de fonction (plugin variations, lot I9) : dériver f, puis le tableau de variations « ⊞{…} »
  if (kernel.plugin === 'variations') return createVarSheet(engine, model, kernel, opts);
  // analyse en un point (lot I11) : tangente, primitive avec une condition
  if (
    kernel.plugin === 'analysis' &&
    (kernel.goal?.kind === 'tangent' ||
      (kernel.goal?.kind === 'antiderivative' && kernel.condition))
  )
    return createPointSheet(engine, model, kernel, opts);
  // une hypothèse peut porter sur une expression (« p − 0,5 < 0 ») : on la garde lue (`expr`)
  const hyps = (kernel.hypotheses ?? []).map((h) => {
    if (!/[^\p{L}\p{N}₀-₉′]/u.test(String(h.of ?? ''))) return h;
    try {
      return { ...h, expr: parse(String(h.of), table) };
    } catch {
      return h;
    }
  });
  // angle d'un triangle (hypothèse acute / angle) : mesuré en DEGRÉS, dans ]0 ; 90[ ou ]0 ; 180[
  for (const h of kernel.hypotheses ?? []) {
    if (h.of !== kernel.goal?.target || (h.kind !== 'acute' && h.kind !== 'angle')) continue;
    try {
      hyps.push({
        kind: 'domain',
        of: h.of,
        expr: parse(`0 < ${h.of} < ${h.kind === 'acute' ? 90 : 180}`, table),
      });
    } catch {
      /* nom d'angle illisible */
    }
  }
  // intervalle d'étude de l'énoncé (« 0 ≤ x < 2π ») : les solutions se comparent dedans
  if (kernel.domain && kernel.goal?.target) {
    try {
      hyps.push({
        kind: 'domain',
        of: kernel.goal.target,
        expr: parse(String(kernel.domain), table),
      });
    } catch {
      /* intervalle illisible : étude sur ℝ */
    }
  }
  const pred = makePred(table, hyps);
  const P = (s) => parse(units ? groupJuxtaposed(String(s), units) : String(s), table);
  const L = (a) => light(a, table);
  const tryP = (s) => {
    try {
      return P(s);
    } catch {
      return null;
    }
  };

  // grandeurs données (feuille « formule ») : valeur exacte de symbols[].exact[0] ou symbols[].value
  const given = {},
    givenAlt = {},
    fixed = {};
  for (const [name, s] of Object.entries(model.symbols ?? {})) {
    if (!s || !s.given) continue;
    let v = Array.isArray(s.exact) && s.exact.length ? tryP(s.exact[0]) : null;
    if (!v && typeof s.value === 'number') v = numFromJs(s.value);
    if (!v && typeof s.value === 'string') v = tryP(s.value);
    if (!v) continue;
    given[name] = L(v);
    const ev = evaluate(v, {}, table);
    if (ev !== undefined && Number.isFinite(toF(ev))) {
      fixed[name] = ev;
      // valeur exacte écrite en fraction (« 35 ÷ 100 ») qui tombe juste : « Remplacer » accepte aussi son
      // écriture décimale (0,35), celle de l'élève — sinon il faudrait remplacer puis calculer
      const f = String(toF(ev));
      const d = /^-?\d+(\.\d{1,6})?$/.test(f) ? tryP(f.replace('.', ',').replace('-', '−')) : null;
      if (d && keyOf(L(d)) !== keyOf(given[name])) givenAlt[name] = L(d);
    }
  }
  // « monde » d'une feuille « formule » : la valeur de CHAQUE grandeur (données, grandeurs intermédiaires, cible),
  // quand l'exercice la donne (symbols[].value / values / exact, une seule valeur). Une ligne sur une grandeur
  // intermédiaire (« CM = V₁ ÷ V₀ » sur une feuille qui cherche t) est vraie si elle vaut dans ce monde.
  const world = { ...fixed };
  const inexact = {};
  for (const [name, s] of Object.entries(model.symbols ?? {})) {
    if (!s || name in world) continue;
    const vals =
      Array.isArray(s.exact) && s.exact.length
        ? s.exact
        : Array.isArray(s.values)
          ? s.values
          : s.value != null
            ? [s.value]
            : [];
    if (vals.length !== 1) continue;
    // un nombre à virgule flottante non exact (2433.3058048000003) : laissé aux formules de la carte, qui le
    // calculent exactement plus bas
    if (typeof vals[0] === 'number' && /\.\d{12,}/.test(String(vals[0]))) {
      inexact[name] = vals[0];
      continue;
    }
    const v = typeof vals[0] === 'number' ? numFromJs(vals[0]) : tryP(vals[0]);
    const ev = v ? evaluate(v, {}, table) : undefined;
    if (ev !== undefined && Number.isFinite(toF(ev))) world[name] = ev;
  }
  // outils structurés du format publié (Pythagore, second degré, baisse) → lignes de formule + pièges
  const expanded = expandModelPreds(model.preds);
  const preds = expanded.preds
    .map((p) => {
      const a = tryP(p.line);
      return (
        a && { line: p.line, L: L(a), key: keyOf(L(a)), label: p.label, why: p.why, hora: p.hora }
      );
    })
    .filter(Boolean);
  // feuille « formule » dont la grandeur cherchée est un angle en degrés (« angle a (°) ») calculé par arccos,
  // arcsin ou arctan : le monde des degrés, comme pour un angle de triangle (hypothèse acute / angle)
  const angleDeg =
    goal.kind === 'evaluate' &&
    /°/.test(model.symbols?.[target]?.meaning ?? '') &&
    [...(model.preds ?? []).map((p) => p.line ?? ''), ...(kernel.path ?? [])].some((l) =>
      /arc(cos|sin|tan)/.test(String(l)),
    );
  const Wd = (n) => (angleDeg ? degreeWorld(n, { table, target }) : n);
  // grandeurs intermédiaires sans valeur dans l'exercice (« CMₘ », « CM ») : calculées par les formules de la
  // carte (« CMₘ = 1 + tₘ ») à partir des grandeurs déjà connues, jusqu'à ce que plus rien ne bouge
  for (let changed = true; changed; ) {
    changed = false;
    for (const p of preds) {
      const a = p.L;
      if (a.t !== 'Eq' || a.k[0].t !== 'Sym' || a.k[0].name in world) continue;
      const name = a.k[0].name;
      if (!(name in (model.symbols ?? {}))) continue;
      if (![...symbols(a.k[1])].every((n) => n in world || table.syntax.constants[n])) continue;
      const ev = evaluate(Wd(a.k[1]), world, table);
      if (ev === undefined || !Number.isFinite(toF(ev))) continue;
      world[name] = ev;
      changed = true;
    }
  }
  // pas de formule qui la calcule (Pythagore, grandeur définie implicitement) : la valeur approchée de l'exercice
  // (gardée en flottant : les comparaisons se font alors à 1e−9 près, jamais exactes)
  for (const [name, x] of Object.entries(inexact)) if (!(name in world)) world[name] = x;
  // feuille « formule » : une grandeur CHERCHÉE de valeur positive (une longueur…) est positive pour les gestes
  // (« FG² = 169 → FG = √169 »). Les prédicats lisent ce tableau à chaque appel. Pas les données : rendues
  // positives, elles ouvriraient des règles gardées (ln, racines…) qui éparpillent la recherche.
  if (goal.kind === 'evaluate') {
    // et une grandeur déclarée longueur (`kind: 'length'`) : « 16 = h² → h = √16 » pour une hauteur
    const lengths = Object.entries(model.symbols ?? {})
      .filter(([, sy]) => sy?.kind === 'length' && !sy.given)
      .map(([n]) => n);
    for (const name of [...(model.goal ?? [target]), ...lengths]) {
      const v = world[name];
      if (v !== undefined && toF(v) > 0 && !hyps.some((h) => h.of === name))
        hyps.push({ of: name, kind: 'positive' });
    }
  }
  const traps = [
    ...(model.traps ?? []),
    ...expanded.traps.filter((t) => !(model.traps ?? []).some((m) => m.line === t.line)),
  ]
    .map((t) => {
      const a = tryP(t.line);
      return a && { ...t, key: keyOf(L(a)) };
    })
    .filter(Boolean);
  const path = (kernel.path ?? [])
    .map((text) => {
      const a = tryP(text);
      return a && { text, ast: a, L: L(a), key: keyOf(L(a)) };
    })
    .filter(Boolean);
  const start =
    kernel.plugin === 'geometry'
      ? (tryP(kernel.start) ?? { t: 'Sym', name: '□' })
      : P(kernel.start);
  // feuille d'affirmations : la réponse est une liste de lettres (« A, B »), pas une écriture
  const answer =
    goal.kind === 'decide' ||
    ['limit', 'congruence', 'arith', 'complex', 'geometry'].includes(kernel.plugin)
      ? null
      : kernel.answer
        ? P(kernel.answer)
        : (path.at(-1)?.ast ?? null);
  const answerL = answer ? L(answer) : null;

  const ctx = {
    table,
    model,
    kernel,
    goal,
    target,
    form: kernel.form,
    // conditions d'existence (« x > 0 » pour ln(x)) : une fois écrites, elles restreignent l'étude
    conds: (kernel.conditions ?? []).flatMap((c) => {
      try {
        return [light(P(c), table)];
      } catch {
        return [];
      }
    }),
    // inconnue auxiliaire (« X = eˣ ») : une ligne en X se lit en x pour savoir si elle est juste
    aux:
      kernel.auxiliary?.name && target
        ? {
            name: kernel.auxiliary.name,
            sub: parse(kernel.auxiliary.of === 'ln' ? `ln(${target})` : `e^${target}`, table),
          }
        : null,
    hyps,
    degrees:
      angleDeg ||
      (kernel.hypotheses ?? []).some(
        (h) => h.of === target && (h.kind === 'acute' || h.kind === 'angle'),
      ),
    given,
    fixed,
    world,
    tryP,
    preds,
    traps,
    path,
    pathKeys: new Set(path.map((p) => p.key)),
    answer,
    answerL,
    answerKey: answerL ? keyOf(answerL) : null,
    ref: isRelation(start, table) ? start : answer,
    truth: new Map(),
    expand: new Map(),
    K: {
      pred,
      target,
      given,
      givenAlt,
      preds,
      hyps,
      table,
      world,
      rounding: wantsRounding(model, answer, path, table),
      ...(angleDeg ? { degreeWorld: Wd } : {}),
      foldFractions: fromLevel(engine.builtins?.fold?.options?.fractionSumsFrom, lv),
    },
    vectors: !!(engine.builtins?.chasles || engine.builtins?.vector_opposite),
    level: lv.level,
    maxGestures: lv.maxGestures,
    levelIndex: lv.index,
    levelOrder: lv.order,
    engine: fnNames.length || idNames.length ? engine : null,
  };
  ctx.answerMeasure = answerL ? measure(answerL, ctx) : 0;
  // feuille « formule » : comparaisons qui rapportent des HORA (« Δ > 0 », model.checks)
  ctx.checks = (model.checks ?? [])
    .map((c) => {
      const a = tryP(c.line);
      return a && CMP_FLIP[a.t] ? { ...c, keys: cmpKeys(a, table) } : null;
    })
    .filter(Boolean);
  // feuille « vérifier chaque affirmation » (goal.kind = 'decide', lot M3)
  ctx.claims = readClaims(kernel, table, tryP);
  ctx.single = !!kernel.single;
  if (ctx.claims) ctx.K = { ...ctx.K, unfold: true };
  // feuille de limites au format publié (« E → ℓ », lot I1)
  if (kernel.plugin === 'limit' && goal.kind === 'limit') {
    const ans = splitLimitLine(kernel.answer ?? '');
    ctx.limit = {
      P: limitPoint(kernel, table),
      answer: ans ? { op: ans.op, right: tryP(ans.right) } : dlAnswer(kernel.answer, tryP, table),
    };
  }
  // feuille « géométrie déductive » (lot I6) : faits = départ + données ; figure = coordonnées du modèle
  if (kernel.plugin === 'geometry' && engine.geometry) {
    const fig = makeFigure(kernel.figure?.points);
    const G = {
      fig,
      table,
      mtable: metricTable(table, fig.names),
      names: fig.names,
      rules: engine.geometry.rules,
    };
    const links = (t0) => {
      const ch = splitChain(t0);
      return ch.ops.length > 1 && ch.ops.every((o) => o === '=')
        ? ch.members.slice(1).map((m, i) => `${ch.members[i]} = ${m}`)
        : [t0];
    };
    const facts = [kernel.start, ...(model.givens ?? [])]
      .flatMap(links)
      .map((t0) => ({ text: t0, prop: parseProp(t0) }))
      .filter((f) => f.prop);
    const answer = kernel.answer ? links(kernel.answer).map(parseProp) : [];
    ctx.geo = { G, facts, answer: answer.every(Boolean) ? answer : [] };
  }
  // feuille « nombres complexes » (lot I5)
  if (kernel.plugin === 'complex') {
    const cm = model.complex ?? {};
    const env = {};
    const known = new Set();
    const givenAsts = [];
    for (const g0 of model.givens ?? []) {
      const ch = splitChain(g0);
      const a0 = tryP(ch.members[0]),
        b0 = tryP(ch.members.at(-1));
      if (a0?.t === 'Sym' && b0) {
        const v = cvalue(b0, table, env);
        if (v) {
          env[a0.name] = v;
          known.add(a0.name);
          givenAsts.push(b0);
        }
      }
    }
    const defs = new Set();
    for (const [name, ex] of Object.entries(cm.defs ?? {})) {
      const e0 = tryP(ex);
      const v = e0 && cvalue(e0, table, env);
      if (v) {
        env[name] = v;
        defs.add(name);
      }
    }
    const ansParts = splitOr(kernel.answer ?? '')
      .map((t) => tryP(splitChain(t).members.at(-1)))
      .filter(Boolean);
    const startParts = splitOr(kernel.start ?? '').map((t) => splitChain(t).members.map(tryP));
    ctx.cx = {
      env,
      known,
      defs,
      givenAsts,
      unknown: cm.unknown ?? null,
      solutions: (cm.solutions ?? [])
        .map((t0) => {
          const e0 = tryP(t0);
          return e0 && cvalue(e0, table, env);
        })
        .filter(Boolean),
      startParts,
      ansKeys: ansParts.map((a0) => keyOf(light(a0, table))),
      startKey:
        startParts.length === 1 && startParts[0].length === 1 && startParts[0][0]
          ? keyOf(light(startParts[0][0], table))
          : null,
      startAst: startParts[0]?.[0] ?? null,
    };
  }
  // feuille « entiers » : congruences et arithmétique (lots I7, I13)
  if (kernel.plugin === 'congruence' || kernel.plugin === 'arith') {
    const m0 = model.congruence?.mod ?? splitModulus(kernel.answer ?? '').mod;
    const ans = splitModulus(kernel.answer ?? '');
    const chain = splitChain(ans.text);
    const eqs = [];
    for (const g0 of model.givens ?? []) {
      const sm = splitModulus(g0),
        ch = splitChain(sm.text);
      const ms = ch.members.map((t) => tryP(t));
      if (ms.every(Boolean))
        eqs.push({ members: ms, ops: ch.ops, mod: sm.mod ?? (m0 != null ? BigInt(m0) : null) });
    }
    ctx.ints = {
      kind: kernel.plugin,
      mod: m0 != null ? BigInt(m0) : null,
      x: /^[a-z]$/u.test(goal.target ?? '') ? goal.target : null,
      answerLast: tryP(chain.members.at(-1)),
      assign: splitAssignments(kernel.answer ?? ''),
      eqs,
      // lettres données par l'énoncé (« n ≡ 1 [2] ») : les remplacer par leur reste est un geste « Remplacer »
      known: new Set(
        eqs
          .filter(
            (e) => e.members[0].t === 'Sym' && e.members.length === 2 && e.members[1].t === 'Num',
          )
          .map((e) => e.members[0].name),
      ),
    };
  }
  // affirmations de limites (« x³ ÷ eˣ → 0 en +∞ ») : chacune tranchée par une ligne « expr → ℓ » vraie
  if (kernel.plugin === 'limclaims' && Array.isArray(kernel.lclaims)) {
    const claims = kernel.lclaims.map((c) => {
      const E = tryP(c.expr),
        V = tryP(c.value);
      const P = limitPoint({ point: c.point, goal: { target: c.x } }, table);
      return E && V
        ? {
            key: keyOf(light(E, table)),
            P,
            value: limNum(evaluate(V.t === 'Right' || V.t === 'Left' ? V.k[0] : V, {}, table)),
          }
        : null;
    });
    ctx.limit = {
      P: claims.find(Boolean)?.P ?? limitPoint(kernel, table),
      answer: null,
      claims,
      single: !!kernel.single,
    };
  }
  ctx.quantities = units ? { units } : null;
  const sl = L(start);
  const lines = [
    {
      index: 0,
      text: toText(start, table),
      ast: start,
      L: sl,
      key: keyOf(sl),
      status: 'start',
      kind: null,
      from: null,
      gestures: [],
      hora: 0,
    },
  ];
  // données de l'énoncé (model.givens : « 2x − 7 = 9 » sur une feuille qui cherche x) : des lignes de départ de
  // plus, d'où une ligne peut venir comme de la ligne de départ. Une donnée illisible est ignorée.
  for (const text of model.givens ?? []) {
    const a = tryP(String(text));
    if (!a) continue;
    const gl = L(a),
      key = keyOf(gl);
    if (lines.some((l) => l.key === key)) continue;
    lines.push({
      index: lines.length,
      text: toText(a, table),
      ast: a,
      L: gl,
      key,
      status: 'given',
      kind: null,
      from: null,
      gestures: [],
      hora: 0,
    });
  }
  if (ctx.geo) lines[0].text = String(kernel.start);
  return { ctx, lines, done: false };
}

// Niveau de la feuille (CONTRACT §11) : profil = le premier dont l'intervalle contient le niveau.
// Noms de l'exercice qui ne se lisent pas lettre à lettre : plusieurs lettres (« AC », « Ec », « CMₘ ») ou une
// notation fonctionnelle (« P(A∩B) », « f(2) », « E(X) »). Une lettre seule (avec indice, prime, accent) se lit déjà.
function modelIdentifiers(model, table) {
  const letter = new RegExp(`[${table.lex.letters}]`, 'gu');
  if (model.kernel?.goal?.kind === 'decide') return []; // feuille d'affirmations : que des nombres
  if (model.kernel?.plugin === 'geometry') return []; // géométrie : les segments sont lus par geometry.js
  const names = new Set(
    [...Object.keys(model.symbols ?? {}), ...(model.goal ?? []), model.kernel?.goal?.target].filter(
      Boolean,
    ),
  );
  return [...names].filter(
    (n) =>
      typeof n === 'string' &&
      !/\s/.test(n) &&
      !table.lex.names[n] &&
      ((n.match(letter) ?? []).length > 1 || /\(/.test(n)),
  );
}

// Une feuille attend un arrondi quand sa réponse ou son corrigé écrit « ≈ », ou que le modèle donne la précision
// (`rounding`). Ailleurs le geste « Arrondir » n'est jamais proposé : il ferait perdre la recherche.
function wantsRounding(model, answer, path, table) {
  if (model.rounding || model.kernel?.rounding) return true;
  const approx = (a) => !!a && table.nodes[a.t]?.relation === 'approx';
  return approx(answer) || path.some((p) => approx(p.ast));
}

// La feuille est-elle au niveau `from` ou au-delà ? (faux sans niveaux déclarés ou sans niveau connu)
function fromLevel(from, lv) {
  if (!from || !lv?.order || lv.index < 0) return false;
  const j = lv.order.indexOf(from);
  return j >= 0 && lv.index >= j;
}

function resolveLevel(engine, wanted) {
  const L = engine.levels;
  if (!L?.order?.length)
    return { level: wanted ?? null, maxGestures: LIMITS.depth, index: -1, order: null };
  const order = L.order;
  const level = order.includes(wanted) ? wanted : order.at(-1);
  const i = order.indexOf(level);
  const prof = (L.profiles || []).find(
    (p) => order.indexOf(p.from) <= i && i <= order.indexOf(p.to),
  );
  return { level, maxGestures: prof?.maxGestures ?? LIMITS.depth, index: i, order };
}
export function levelOf(state) {
  return { level: state.ctx.level, maxGestures: state.ctx.maxGestures };
}
// geste implicite à ce niveau : rule.implicitFrom ≤ niveau de la feuille
// une règle déjà employée dans la ligne, ou de la même famille de gestes (« develop ») : ne coûte rien de plus
function usedHas(used, rule, group) {
  return (
    used.includes(rule) || (group && !!rule.gesture && used.some((r) => r.gesture === rule.gesture))
  );
}

// les règles employées et, avec la famille, les autres règles de leur famille de gestes
function withMates(engine, used, group) {
  if (!group || !used.some((r) => r.gesture)) return used;
  const fams = new Set(used.map((r) => r.gesture).filter(Boolean));
  return [...used, ...engine.rules.filter((r) => fams.has(r.gesture) && !used.includes(r))];
}

// nombre de termes du dernier membre de la ligne d'origine qui ne se retrouvent pas dans la ligne visée
function changedTerms(from, to, table) {
  const last = (L) => (table.nodes[L.t]?.relation ? L.k.at(-1) : L);
  const terms = (n) => (table.nodes[n.t]?.eval === 'add' ? n.k : [n]);
  const bag = new Map();
  for (const t of terms(last(to))) bag.set(keyOf(t), (bag.get(keyOf(t)) ?? 0) + 1);
  let n = 0;
  for (const t of terms(last(from))) {
    const k = keyOf(t),
      c = bag.get(k) ?? 0;
    if (c) bag.set(k, c - 1);
    else n++;
  }
  return n;
}

function isImplicit(rule, ctx) {
  if (!rule.implicitFrom || !ctx.levelOrder) return false;
  const j = ctx.levelOrder.indexOf(rule.implicitFrom);
  return j >= 0 && ctx.levelIndex >= j;
}

function numFromJs(v) {
  if (Number.isInteger(v)) return num(v);
  const s = String(v);
  const m = /^(-?)(\d+)\.(\d+)$/.exec(s);
  if (!m) return null;
  const q = 10 ** m[3].length;
  return num((m[1] ? -1 : 1) * (parseInt(m[2], 10) * q + parseInt(m[3], 10)), q);
}

// ── Jugement ──────────────────────────────────────────────────────────────
export function judge(engine, state, lineText) {
  engine = state?.ctx?.engine ?? engine;
  const t0 = now();
  const { ctx } = state;
  const table = engine.table;
  // conseils des packs (CONTRACT §9.2) appliqués à tout verdict, puis le temps
  const done = (v) => {
    const full = { gestures: [], hora: 0, from: null, ...v };
    const fromLine = state.lines[full.from ?? state.lines.length - 1] ?? state.lines.at(-1);
    const { target, hyps, fixed, answer } = ctx;
    const out = applyAdvice(engine, full, {
      from: fromLine?.ast,
      equiv: (a, b) => equivRelation(a, b, { table, target, hyps, fixed, answer }),
    });
    return { ...out, ms: Math.round((now() - t0) * 100) / 100 };
  };

  if (ctx.tabs) return judgeTabLine(state, lineText, t0);
  if (ctx.adv) return judgeAdvSheetLine(state, lineText, t0);
  if (ctx.mats) return judgeMatLine(state, lineText, t0);
  if (ctx.progs) return judgeProgLine(state, lineText, t0);
  if (ctx.ana) return judgeAnaLine(state, lineText, t0);
  if (ctx.replay) return judgeReplayLine(state, lineText, t0);
  if (ctx.vars) return judgeVarLine(engine, state, lineText, t0);
  if (ctx.point) return judgePointLine(engine, state, lineText, t0);
  if (ctx.limit) return judgeLimitLine(engine, state, lineText, done);
  if (ctx.ints) return judgeIntegerLine(engine, state, lineText, done);
  if (ctx.cx) return judgeComplexLine(engine, state, lineText, done);
  if (ctx.geo) return judgeGeometryLine(engine, state, lineText, done);
  // feuille « formule » de physique (lot P1) : une donnée convertie dans l'unité de la formule
  if (ctx.world && ctx.kernel?.units && engine.quantities) {
    const cv = judgeConversionLine(engine, state, lineText, done);
    if (cv) return cv;
  }
  // tableau de signes d'une inéquation (lot I9) : la dernière ligne comparée à 0
  if (ctx.target && isSignTableText(lineText))
    return judgeSolveSignTable(engine, state, lineText, done);
  let ast;
  try {
    ast = parse(ctx.quantities ? groupJuxtaposed(lineText, ctx.quantities.units) : lineText, table);
  } catch (e) {
    if (!(e instanceof ParseError)) throw e;
    return done({
      status: 'parse_error',
      kind: null,
      text: lineText,
      latex: null,
      ast: null,
      message: e.message,
      error: { pos: e.pos, len: e.len, msg: e.message },
    });
  }
  if (ctx.claims) return judgeClaimLine(engine, state, ast, done);
  if (ctx.quantities) return judgeQuantityLine(engine, state, ast, done);
  if (ctx.kernel.analysis) {
    const pv = judgeAnalysisPart(engine, state, ast, done);
    if (pv) return pv;
  }
  ast = wrapExpression(ast, ctx, table);
  const Lx = light(ast, table),
    key = keyOf(Lx);
  // feuille d'analyse : une ligne qui écrit des parties nommées (« u′(x) × v(x) ») se juge avec ce qu'elles désignent
  const inAux = !!ctx.aux && symbols(ast).has(ctx.aux.name);
  const astT = ctx.kernel.analysis
    ? substituteHeads(ast, state, table)
    : inAux
      ? replaceSym(ast, ctx.aux.name, ctx.aux.sub)
      : ast;
  const LxT = astT === ast ? Lx : light(astT, table),
    keyT = keyOf(LxT);
  const base = { text: toText(ast, table), latex: toLatex(ast, table), ast };
  const lastKept = state.lines.length - 1;

  // 1. déjà écrite (même écriture à l'ordre près) — sauf un calcul que la forme légère confond (« 1 × (−1) » → « −1 » :
  // moins d'opérations écrites)
  const rawOps = opsCount(ast);
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.key === key && !(l.ast && rawOps < opsCount(l.ast)))
      return done({ ...base, status: 'useless', kind: 'kept', from: i, message: MSG.dup });
  }
  // 2. piège du modèle
  const trap = ctx.traps.find((t) => t.key === key);
  if (trap) {
    return done({
      ...base,
      status: 'trap',
      kind: null,
      from: lastKept,
      trap: {
        title: trap.title,
        why: trap.why,
        ...(trap.misconception_id ? { misconception_id: trap.misconception_id } : {}),
      },
      message: trap.why || trap.title,
    });
  }
  if (!isRelation(ast, table))
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: MSG.notRelation });

  // 2 bis. valeur approchée d'une inconnue déjà trouvée (feuille « résoudre ») : « c ≈ 7,07 » = Arrondir
  if (ctx.goal.kind === 'solve' && ctx.target) {
    const r =
      rankTest(engine, state, ast, Lx, key, ctx) ??
      approxSolution(engine, state, ast, Lx, key, ctx);
    if (r) return done({ ...base, from: lastKept, ...r });
    // 2 quater. condition d'existence écrite (« x > 0 »)
    const cl = conditionLine(engine, state, Lx, ctx);
    if (cl) return done({ ...base, from: lastKept, ...cl });
    // 2 ter. second degré : « Δ = … », « x₁ = … » (grandeurs du trinôme de la dernière équation)
    const q = quadLine(engine, state, ast, ctx);
    if (q) return done({ ...base, from: lastKept, ...q });
  }
  // 2 ter. comparaison d'une feuille « formule » (« Δ > 0 ») : vraie avec les valeurs établies
  if (ctx.goal.kind === 'evaluate' && CMP_FLIP[ast.t]) {
    const r = comparisonLine(state, ast, ctx);
    if (r) return done({ ...base, from: lastKept, ...r });
  }
  // 3. juste ?
  const where = conditionsOf(state);
  if (!isTrue(astT, keyT, ctx, where)) {
    // forme indéterminée (+∞ − ∞, 0 × ∞, ∞ ÷ ∞, 0 ÷ 0) : le message du pack d'analyse
    const fi = indeterminateLine(ast, ctx);
    if (fi)
      return done({
        ...base,
        status: 'trap',
        kind: null,
        from: lastKept,
        trap: fi,
        message: fi.why,
      });
    const tr = search(engine, state, Lx, key, { trap: true });
    if (tr) {
      const r = tr.chain[0].rule;
      return done({
        ...base,
        status: 'trap',
        kind: null,
        from: tr.from,
        gestures: tr.chain.map((c) => gestureOf(c, ctx)),
        trap: {
          title: r.title || r.label,
          why: r.why,
          ...(r.misconception_id ? { misconception_id: r.misconception_id } : {}),
        },
        message: r.why || r.title,
      });
    }
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      from: lastKept,
      message: MSG.wrong[ctx.goal.kind] || MSG.wrong.default,
    });
  }

  // 4. justifiée ?
  const goal0 = multiGoal(ctx)
    ? allGoalsFound(state, Lx, ctx)
    : ctx.kernel.analysis
      ? analysisGoal(Lx, key, astT !== ast, ctx)
      : isGoal(Lx, key, ctx);
  // « R = 1 × (−1) » a la forme légère de la réponse « R = −1 » mais il reste un calcul
  const goal =
    goal0 &&
    key === ctx.answerKey &&
    ctx.answer &&
    rawOps > opsCount(ctx.answer) &&
    numericOnly(ast, table)
      ? false
      : goal0;
  // une ligne en X se justifie depuis les lignes en X (telle qu'écrite)
  const res = inAux ? search(engine, state, Lx, key, {}) : search(engine, state, LxT, keyT, {});
  if (!res && ctx.aux) {
    const av = auxGesture(engine, state, Lx, key, inAux, ctx);
    if (av) return done({ ...base, ...av });
  }
  if (!res && where.length) {
    const ck = conditionKeep(engine, state, Lx, key, where, ctx);
    if (ck) return done({ ...base, ...ck });
  }
  if (!res && ctx.goal.kind === 'solve' && ctx.target) {
    const qc = quadConclusion(engine, state, Lx, key, ctx);
    if (qc) return done({ ...base, ...qc });
    const tg =
      trigGesture(engine, state, Lx, key, ctx) ?? angleGesture(engine, state, Lx, key, ctx);
    if (tg) return done({ ...base, ...tg });
  }
  if (!res) {
    // utilité AVANT justification : juste, hors corrigé, et la dette ne baisse pas par rapport à la dernière
    // ligne gardée → « ça ne t'avance pas », même si sa chaîne dépasse le niveau (rien n'a été « sauté »)
    if (
      !goal &&
      !ctx.pathKeys.has(key) &&
      measure(Lx, ctx) >= measure(state.lines[lastKept].L, ctx) - 1e-9
    ) {
      return done({
        ...base,
        status: 'useless',
        kind: 'kept',
        from: lastKept,
        message: MSG.noProgress,
      });
    }
    return done({
      ...base,
      status: 'unjustified',
      kind: 'kept',
      from: lastKept,
      message: goal ? MSG.unjustifiedGoal : MSG.unjustified,
    });
  }
  const gestures = res.chain.map((c) => gestureOf(c, ctx));

  // 5. utile ?
  const fromLine = state.lines[res.from];
  const useful =
    goal ||
    ctx.pathKeys.has(key) ||
    measure(Lx, ctx) < measure(fromLine.L, ctx) - 1e-9 ||
    // équation de degré ≥ 2 : une forme qui avance d'un cran du cours (tout d'un côté, factorisée…) est un progrès
    shapeAdvances(fromLine.L, Lx, ctx) ||
    // feuille d'analyse : remplacer les parties nommées par leur expression avance (la ligne s'allonge)
    (!!ctx.kernel.analysis && namedHeads(fromLine.L, ctx) > namedHeads(Lx, ctx));
  if (!useful)
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: res.from,
      gestures,
      message: MSG.noProgress,
    });

  const hora = Math.min(
    LIMITS.horaCap,
    gestures.reduce((s, g) => s + (g.implicit ? 0 : g.hora || 0), 0),
  );
  return done({
    ...base,
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from: res.from,
    gestures,
    hora,
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
  });
}

// ── Feuille « vérifier chaque affirmation » (lot M3) ───────────────────────
// Une ligne = une chaîne de calculs sur des nombres (« 2 × 3 ÷ 2 = 6 ÷ 2 = 3 ») ou une comparaison. Chaque maillon
// doit être vrai et se justifier en un geste depuis le maillon d'avant (même recherche, mêmes niveaux que les
// autres feuilles). Une ligne juste qui saute des étapes est gardée sans HORA (elle tranche quand même).
const CLAIM_MSG = {
  notNumeric: 'Ici, on calcule sur des nombres.',
  notEqual: 'Ce n’est pas égal : essaie avec une valeur des lettres.',
  badValue: 'Ce n’est pas la valeur proposée : remplace la lettre par la valeur de l’affirmation.',
  wrong: 'Ce n’est pas juste : vérifie ce calcul.',
  aside: 'Juste, mais à côté : calcule ce qu’affirment les propositions.',
  decided: 'Affirmation tranchée.',
  all: 'Tout est tranché !',
  divZero: 'On ne divise jamais par 0 : valeur interdite.',
  pairAgain: 'Cette paire de diviseurs est déjà écrite : cherche-en une autre.',
  pair: (a, b, n) => (a === b ? `${a} divise ${n}.` : `${a} et ${b} divisent ${n}.`),
};

function judgeClaimLine(engine, state, ast0, done) {
  const { ctx } = state;
  const table = engine.table;
  // valeur proposée : « 4x − 7 = 4 × 3 − 7 = … » se juge avec le premier membre remplacé (« Remplacer »)
  let ast = ast0,
    pre = null;
  if (isRelation(ast0, table) && !isNumeric(ast0, table) && speaksOfAt(ctx.claims, ast0, table)) {
    const inst = atInstance(ctx.claims, ast0, table);
    if (!inst || !isNumeric(inst.ast, table))
      return done({
        text: toText(ast0, table),
        latex: toLatex(ast0, table),
        ast: ast0,
        status: 'wrong',
        kind: null,
        from: state.lines.length - 1,
        message: CLAIM_MSG.badValue,
      });
    ast = inst.ast;
    const sub = light(ast.k[0], table),
      nextL = light(ast.k[1], table);
    pre =
      keyOf(sub) === keyOf(nextL) && engine.builtins?.substitute
        ? { rule: engine.builtins.substitute, ast: sub }
        : null;
    // « 4x − 7 = 4 × 3 − 7 » : le premier maillon est le remplacement lui-même
    if (pre)
      ast = { ...ast, k: ast.k.slice(1).length >= 1 ? [ast.k[0], ...ast.k.slice(2)] : ast.k };
    if (ast.k.length < 2) ast = inst.ast;
  }
  const base = {
    text: toText(ast0, table),
    latex: toLatex(ast0, table),
    ast,
    ...(pre ? {} : {}),
  };
  const lastKept = state.lines.length - 1;
  // les affirmations sont affichées en tête : les recopier pour les calculer n'est pas « déjà écrit »
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.text === base.text && l.status !== 'start' && l.status !== 'given')
      return done({ ...base, status: 'useless', kind: 'kept', from: i, message: MSG.dup });
  }
  if (!isRelation(ast, table))
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: MSG.notRelation });
  const letters = !isNumeric(ast, table);
  if (letters && !ctx.claims.some((c) => c.ok && c.identity))
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      from: lastKept,
      message: CLAIM_MSG.notNumeric,
    });
  // valeur interdite : « (2 + 1) ÷ (2 − 2) = 3 ÷ 0 » se calcule (mêmes numérateurs) ; une écriture interdite n'est
  // égale à aucun nombre
  const undef = !letters ? ast.k.map((m) => divByZero(m, table)) : [];
  if (undef.some((u) => u !== null)) {
    const same =
      table.nodes[ast.t]?.relation === 'eq' &&
      undef.every((u) => u !== null && Math.abs(u - undef[0]) <= 1e-9 * Math.max(1, Math.abs(u)));
    if (!same)
      return done({
        ...base,
        status: 'wrong',
        kind: null,
        from: lastKept,
        message: CLAIM_MSG.divZero,
      });
  }
  // en lettres : chaque maillon doit être égal pour toutes les valeurs des lettres
  const holds =
    undef.some((u) => u !== null) ||
    (letters
      ? table.nodes[ast.t]?.relation === 'eq' &&
        ast.k.every((m, i) => i === 0 || fnEqual(ast.k[i - 1], m, table))
      : relHolds(ast, table));
  if (!holds)
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      from: lastKept,
      message: letters ? CLAIM_MSG.notEqual : CLAIM_MSG.wrong,
    });

  const members = ast.k.map((m) => light(m, table));
  const { written } = knownValues(state.lines, table);
  const about = claimMemberKeys(ctx.claims);
  // diviseurs d'un entier : les paires que la ligne montre (« 2 × 6 = 12 », « 12 ÷ 3 = 4 »)
  const divNs = divisorNumbers(ctx.claims);
  const pairsBefore = divisorPairKeys(divNs, state.lines, table);
  const linePairs = [...divisorPairKeys(divNs, [{ ast, status: 'step' }], table)];
  const newPairs = linePairs.filter((p) => !pairsBefore.has(p));
  const before = claimDecisions(ctx.claims, state.lines, table, ctx.single);
  const after = claimDecisions(
    ctx.claims,
    [...state.lines, { ast, status: 'step' }],
    table,
    ctx.single,
  );
  const newly = after.filter((d, i) => d !== null && before[i] === null).length;
  // à côté : rien des affirmations, rien de déjà écrit, aucune paire, rien de tranché (« (2 + 1) × (1 + 1) = 6 »
  // tranche le nombre de diviseurs une fois la décomposition écrite)
  if (
    !newPairs.length &&
    !newly &&
    !members.some((m) => about.has(keyOf(m)) || written.has(keyOf(m)))
  ) {
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: lastKept,
      message: CLAIM_MSG.aside,
    });
  }
  // chaque maillon d'une égalité se justifie depuis le précédent ; une comparaison se lit (pas de geste)
  const gestures = pre ? [gestureOf(pre, ctx)] : [];
  let jumped = false;
  if (table.nodes[ast.t]?.relation === 'eq') {
    for (let i = 1; i < members.length; i++) {
      const from = members[i - 1],
        to = members[i];
      // même forme normalisée, écriture différente : la normalisation applique d'elle-même la règle des signes
      // (« 7 + (−7) = 7 − 7 », « (−1) × (−1) = 1 ») ; un simple changement d'ordre ne rapporte rien
      if (keyOf(from) === keyOf(to)) {
        const a = toText(ast.k[i - 1], table),
          b = toText(ast.k[i], table);
        const sameChars =
          [...a.replace(/\s/g, '')].sort().join('') === [...b.replace(/\s/g, '')].sort().join('');
        if (!sameChars && engine.builtins?.signs)
          gestures.push(gestureOf({ rule: engine.builtins.signs, ast: to }, ctx));
        continue;
      }
      // « (2 + 1) ÷ (2 − 2) = 3 ÷ 0 » : le numérateur et le dénominateur calculés
      if (undef[i] !== null && undef[i] !== undefined && engine.builtins?.fold) {
        gestures.push(gestureOf({ rule: engine.builtins.fold, ast: to }, ctx));
        continue;
      }
      const mini = { ctx, lines: [{ index: 0, L: from, key: keyOf(from), ast: null }] };
      const res = search(engine, mini, to, keyOf(to), {});
      if (!res) {
        jumped = true;
        continue;
      }
      gestures.push(...res.chain.map((c) => gestureOf(c, ctx)));
    }
  }
  const all = after.every((d) => d !== null);
  const decisions = after;
  // une paire déjà écrite (« 6 × 2 = 12 » après « 2 × 6 = 12 ») n'apprend rien
  if (linePairs.length && !newPairs.length && !newly)
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: lastKept,
      message: CLAIM_MSG.pairAgain,
    });
  const pairSaid = newPairs
    .map((p) => {
      const [n, a] = p.split(':').map(Number);
      return CLAIM_MSG.pair(a, n / a, n);
    })
    .join(' ');
  if (jumped) {
    return done({
      ...base,
      status: all ? 'goal' : 'unjustified',
      kind: 'kept',
      from: lastKept,
      gestures,
      hora: 0,
      decisions,
      message: all ? CLAIM_MSG.all : MSG.unjustified,
    });
  }
  // une paire de diviseurs trouvée vaut au moins 1 HORA (« 1 × 12 = 12 » n'a qu'un geste implicite)
  const gestureHora = gestures.reduce((s, g) => s + (g.implicit ? 0 : g.hora || 0), 0);
  const hora = Math.min(LIMITS.horaCap, Math.max(gestureHora, newPairs.length) + newly);
  const said0 = gestures.length ? `Juste : ${labelsOf(gestures)}.` : 'Juste.';
  const said = pairSaid ? `${said0} ${pairSaid}` : said0;
  return done({
    ...base,
    status: all ? 'goal' : 'step',
    kind: 'step',
    from: lastKept,
    gestures,
    hora,
    decisions,
    message: all ? CLAIM_MSG.all : newly ? `${said} ${CLAIM_MSG.decided}` : said,
  });
}

// ── Feuille d'analyse au format publié (lot M7) : parties nommées ─────────────
// « u(x) = x² − 1 » nomme une partie de f ; « u′(x) = 2x » écrit sa dérivée. Une ligne qui écrit ces noms se juge
// avec ce qu'ils désignent (u′(x) → (x² − 1)′), et une partie déjà connue se remplace ensuite (« Remplacer »).
const PART_HEAD = /^([A-Za-z])(′*)\(([a-z])\)$/u;
const PART_MSG = {
  notPart: 'Ce n’est pas une partie de la fonction : choisis un morceau de son écriture.',
  nameFirst: (n) => `Nomme d’abord ${n}(x).`,
  wrongDerivative: 'Ce n’est pas la bonne dérivée.',
  changed: (h) => `${h} est déjà nommé autrement.`,
  notSame: 'Ce n’est pas la même fonction.',
  notPrimitive: 'Ce n’est pas une primitive : dérive ta ligne pour vérifier.',
};

// nombre de têtes de parties nommées (« u′(x) ») dans une écriture, hors la cible
function namedHeads(n, ctx) {
  if (n.t === 'Sym') return n.name !== ctx.target && PART_HEAD.test(n.name) ? 1 : 0;
  return isLeaf(n) ? 0 : n.k.reduce((s, c) => s + namedHeads(c, ctx), 0);
}

function initialPart(ctx) {
  const A = ctx.kernel.analysis;
  if (!ctx._initialPart) {
    const table = ctx.table;
    const ast = parse(A.expr, table),
      L = light(ast, table);
    ctx._initialPart = {
      head: `${A.name}${'′'.repeat(A.primes ?? 0)}(${A.v})`,
      ast,
      L,
      key: keyOf(L),
    };
  }
  return ctx._initialPart;
}

function partOf(lines, head, ctx) {
  for (let i = lines.length - 1; i >= 0; i--)
    if (lines[i].part?.head === head) return lines[i].part;
  if (ctx?.kernel?.analysis) {
    const p0 = initialPart(ctx);
    if (p0.head === head) return p0;
  }
  return null;
}

/** Parties connues (tête → expression), pour le geste « Remplacer ». */
function analysisParts(lines) {
  const out = {};
  for (let i = lines.length - 1; i >= 0; i--) {
    const p = lines[i].part;
    if (p && !(p.head in out)) out[p.head] = p.L;
  }
  return Object.keys(out).length ? out : null;
}

const derNode = (table) => Object.entries(table.nodes).find(([, sp]) => sp.eval === 'derive')?.[0];
const derN = (e, n, table) => {
  let x = e;
  for (let i = 0; i < n; i++) x = { t: derNode(table), k: [x] };
  return x;
};

/** Les noms de parties d'une ligne remplacés par ce qu'ils désignent : u(x) → son écriture, u′(x) → (u)′. */
function substituteHeads(ast, state, table) {
  const ctx = state.ctx;
  let changed = false;
  const go = (n) => {
    if (n.t === 'Sym') {
      const m = PART_HEAD.exec(n.name);
      if (!m) return n;
      const base = partOf(state.lines, `${m[1]}(${m[3]})`, ctx);
      if (!base) return n;
      changed = true;
      return derN(base.ast, m[2].length, table);
    }
    return isLeaf(n) ? n : { ...n, k: n.k.map(go) };
  };
  // la tête de la ligne (« f′(x) = … ») reste telle quelle
  const out = isRelation(ast, table)
    ? { ...ast, k: [ast.k[0], ...ast.k.slice(1).map(go)] }
    : go(ast);
  return changed ? out : ast;
}

// R est-il un morceau de E ? un sous-arbre, ou une partie des facteurs d'un produit / des termes d'une somme
function isPartOf(RL, E, table) {
  const want = keyOf(RL);
  let found = false;
  visitTree(E, (n) => {
    if (found || isLeaf(n)) {
      if (!found && keyOf(n) === want) found = true;
      return;
    }
    if (keyOf(n) === want) {
      found = true;
      return;
    }
    const ev = table.nodes[n.t]?.eval;
    if ((ev === 'mul' || ev === 'add') && !isLeaf(RL) && RL.t === n.t) {
      const pool = n.k.map(keyOf);
      if (
        RL.k.every((c) => {
          const i = pool.indexOf(keyOf(c));
          if (i < 0) return false;
          pool.splice(i, 1);
          return true;
        })
      )
        found = true;
    }
  });
  return found;
}

function same(a, b, ctx) {
  const tmp = { t: 'Sym', name: '__part' };
  const eq = Object.entries(ctx.table.nodes).find(([, sp]) => sp.relation === 'eq')?.[0];
  return equivRelation(
    { t: eq, k: [tmp, a] },
    { t: eq, k: [tmp, b] },
    { table: ctx.table, target: '__part', hyps: ctx.hyps, fixed: ctx.fixed },
  );
}

function partSearch(engine, ctx, sources, RL) {
  const lines = sources.map((L, index) => ({ index, L, key: keyOf(L), ast: L }));
  return search(engine, { ctx, lines }, RL, keyOf(RL), {});
}

// Feuille d'analyse : la réponse est l'écriture attendue (aux calculs et à l'ordre près), jamais une ligne qui
// écrit encore des parties nommées.
function analysisGoal(Lx, key, hasNames, ctx) {
  if (hasNames || !ctx.answerL) return false;
  return (
    key === ctx.answerKey ||
    calcCollectKey(Lx, ctx.table) === calcCollectKey(ctx.answerL, ctx.table)
  );
}

function judgeAnalysisPart(engine, state, ast, done) {
  const { ctx } = state;
  const table = engine.table,
    A = ctx.kernel.analysis;
  if (table.nodes[ast.t]?.relation !== 'eq' || ast.k.length !== 2 || ast.k[0].t !== 'Sym')
    return null;
  const head = ast.k[0].name,
    m = PART_HEAD.exec(head);
  if (!m) return null;
  if (A.kind === 'anti' && head === ctx.target) return judgeAntiLine(engine, state, ast, done);
  if (head === ctx.target) return null;
  const R = ast.k[1],
    RL = light(R, table);
  const base = { text: toText(ast, table), latex: toLatex(ast, table), ast };
  const lastKept = state.lines.length - 1;
  const wrong = (message) =>
    done({ ...base, status: 'wrong', kind: null, from: lastKept, message });
  const plain = `${m[1]}(${m[3]})`;
  const ok = (res, part, extra = {}) => {
    const gestures = res ? res.chain.map((c) => gestureOf(c, ctx)) : [];
    const hora = Math.min(
      LIMITS.horaCap,
      gestures.reduce((s2, g) => s2 + (g.implicit ? 0 : g.hora || 0), 0),
    );
    return done({
      ...base,
      status: res ? 'step' : 'unjustified',
      kind: res ? 'step' : 'kept',
      from: lastKept,
      gestures,
      hora: res ? hora : 0,
      message: res
        ? gestures.length
          ? `Juste : ${labelsOf(gestures)}.`
          : 'Juste.'
        : MSG.unjustified,
      part,
      ...extra,
    });
  };
  const p0 = initialPart(ctx);
  const own = partOf(state.lines, head, ctx);
  // la fonction elle-même réécrite (« f(x) = x + 1 ÷ x ») : même fonction, et elle devient le nouveau départ
  if (head === p0.head) {
    if (!same(RL, p0.L, ctx)) return wrong(PART_MSG.notSame);
    const res = partSearch(engine, ctx, [own.L], RL);
    const extra =
      A.kind === 'derive'
        ? { searchAst: { t: ast.t, k: [{ t: 'Sym', name: ctx.target }, derN(R, A.order, table)] } }
        : {};
    return ok(res, { head, ast: R, L: RL, key: keyOf(RL) }, extra);
  }
  if (!m[2] && m[1] !== A.name) {
    // nommer une partie : un morceau de l'écriture de f
    if (own)
      return own.key === keyOf(RL)
        ? done({ ...base, status: 'useless', kind: 'kept', from: lastKept, message: MSG.dup })
        : wrong(PART_MSG.changed(head));
    if (!isPartOf(RL, partOf(state.lines, p0.head, ctx).L, table)) return wrong(PART_MSG.notPart);
    const rule = engine.builtins?.name_part;
    return ok(rule ? { chain: [{ rule, ast: R }] } : { chain: [] }, {
      head,
      ast: R,
      L: RL,
      key: keyOf(RL),
    });
  }
  // dérivée d'une partie nommée (ou de la fonction : « f′(x) = … » quand on cherche f′′)
  const def = partOf(state.lines, plain, ctx);
  if (!def) return wrong(PART_MSG.nameFirst(m[1]));
  const target = derN(def.ast, m[2].length, table);
  if (!same(RL, target, ctx)) return wrong(PART_MSG.wrongDerivative);
  const sources = [light(target, table)];
  if (own && own !== p0) sources.push(own.L);
  // une dérivée de la fonction (« f′(x) = R » quand on cherche f′′) sert ensuite de départ « f′′(x) = (R)′ »
  const rest =
    A.kind === 'derive' && m[1] === A.name ? (A.order ?? 1) + (A.primes ?? 0) - m[2].length : 0;
  const extra =
    rest > 0
      ? { searchAst: { t: ast.t, k: [{ t: 'Sym', name: ctx.target }, derN(R, rest, table)] } }
      : {};
  return ok(partSearch(engine, ctx, sources, RL), { head, ast: R, L: RL, key: keyOf(RL) }, extra);
}

// Primitive : « F(x) = R » est juste si R a pour dérivée la fonction donnée (une constante C est permise).
function judgeAntiLine(engine, state, ast, done) {
  const { ctx } = state;
  const table = engine.table;
  const R = ast.k[1],
    RL = light(R, table);
  const base = { text: toText(ast, table), latex: toLatex(ast, table), ast };
  const lastKept = state.lines.length - 1;
  const p0 = initialPart(ctx);
  // une primitive écrite avec des parties nommées (« F(x) = exp(u(x)) ») : on remplace avant de dériver
  const RS = substituteHeads(ast, state, table).k?.[1] ?? R;
  if (!same(derN(RS, 1, table), p0.ast, ctx))
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      from: lastKept,
      message: PART_MSG.notPrimitive,
    });
  const prev = partOf(state.lines, ctx.target, ctx);
  let res = prev ? partSearch(engine, ctx, [prev.L], RL) : null;
  if (!res && engine.builtins?.antiderive)
    res = { chain: [{ rule: engine.builtins.antiderive, ast: R }] };
  const goal =
    !!ctx.answerL &&
    (keyOf(light(ast, table)) === ctx.answerKey ||
      calcCollectKey(light(ast, table), table) === calcCollectKey(ctx.answerL, table));
  const gestures = res ? res.chain.map((c) => gestureOf(c, ctx)) : [];
  const hora = Math.min(
    LIMITS.horaCap,
    gestures.reduce((s2, g) => s2 + (g.implicit ? 0 : g.hora || 0), 0),
  );
  return done({
    ...base,
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from: lastKept,
    gestures,
    hora,
    message: goal ? MSG.goal : gestures.length ? `Juste : ${labelsOf(gestures)}.` : 'Juste.',
    part: { head: ctx.target, ast: R, L: RL, key: keyOf(RL) },
  });
}

// ── Feuille « géométrie déductive » (lot I6) ────────────────────────────────
const GEO_MSG = {
  unread:
    'Écris une propriété (« (IJ) // (BC) », « I milieu de [AB] »…) ou une relation entre longueurs.',
  unknownPoint: 'Ce point n’est pas sur la figure.',
  wrong: 'Ce n’est pas vrai sur la figure.',
  given: 'C’est une donnée de l’énoncé.',
  unjustified: 'Juste, mais d’où vient-elle ? Quelle propriété du cours l’utilise ?',
};

function judgeGeometryLine(engine, state, text, done) {
  const { ctx } = state;
  const { G, facts: given, answer } = ctx.geo;
  const lastKept = state.lines.length - 1;
  const base = { text, latex: null, ast: state.lines[0].ast };
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.text === text && l.status !== 'start' && l.status !== 'given')
      return done({ ...base, status: 'useless', kind: 'kept', from: i, message: MSG.dup });
  }
  // une relation « a = b = c » : chaque maillon est un fait
  const chain = splitChain(text);
  const parts =
    chain.ops.length > 1 && chain.ops.every((o) => o === '=')
      ? chain.members.slice(1).map((m, i) => `${chain.members[i]} = ${m}`)
      : [text];
  const props = parts.map(parseProp);
  if (props.some((p) => !p))
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: GEO_MSG.unread });
  for (const p of props) {
    const t = propTrue(p, G);
    if (t === null)
      return done({
        ...base,
        status: 'wrong',
        kind: null,
        from: lastKept,
        message: GEO_MSG.unknownPoint,
      });
    if (!t)
      return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: GEO_MSG.wrong });
  }
  const facts = [...given, ...state.lines.flatMap((l) => l.geo?.facts ?? [])];
  if (props.every((p) => facts.some((f) => sameProp(p, f.prop, G))))
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: lastKept,
      message: GEO_MSG.given,
      geo: { facts: [] },
    });
  const gestures = [];
  let jumped = false;
  const known = [...facts];
  for (const p of props) {
    if (known.some((f) => sameProp(p, f.prop, G))) continue;
    const rule = ruleFor(p, known, G);
    if (rule)
      gestures.push(
        gestureOf(
          {
            rule: { ...rule, why: rule.card ? `${rule.label} (${rule.card}).` : rule.label },
            ast: null,
          },
          ctx,
        ),
      );
    else if (p.k === 'metric' && computeJustified(p, known, G)) {
      const r = engine.builtins?.geo_compute;
      if (r) gestures.push(gestureOf({ rule: r, ast: null }, ctx));
    } else jumped = true;
    known.push({ text: p.text, prop: p });
  }
  const newFacts = props.map((p) => ({ text: p.text, prop: p }));
  const goal =
    answer.length > 0 &&
    props.some((p) => answer.some((a) => sameProp(p, a, G))) &&
    answer.every((a) => [...facts.map((f) => f.prop), ...props].some((p) => sameProp(p, a, G)));
  if (jumped)
    return done({
      ...base,
      status: goal ? 'goal' : 'unjustified',
      kind: 'kept',
      from: lastKept,
      gestures,
      hora: 0,
      message: goal ? MSG.unjustifiedGoal : GEO_MSG.unjustified,
      geo: { facts: newFacts },
    });
  const hora = Math.min(
    LIMITS.horaCap,
    gestures.reduce((s2, x) => s2 + (x.implicit ? 0 : x.hora || 0), 0),
  );
  return done({
    ...base,
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from: lastKept,
    gestures,
    hora,
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
    geo: { facts: newFacts },
  });
}

// ── Feuille « nombres complexes » (lot I5) ──────────────────────────────────
const CX_MSG = {
  notEqual: 'Ce n’est pas égal : vérifie ce calcul.',
  notSame: 'Cette équation n’a pas les mêmes solutions.',
  noValue: 'Écris un calcul sur des nombres complexes.',
  aside: 'Juste, mais à côté de la question.',
};

function judgeComplexLine(engine, state, text, done) {
  const { ctx } = state;
  const table = engine.table,
    X = ctx.cx;
  const lastKept = state.lines.length - 1;
  const g = (name) => engine.builtins?.[name];
  const perr = (e) =>
    done({
      status: 'parse_error',
      kind: null,
      text,
      latex: null,
      ast: null,
      message: e.message,
      error: { pos: e.pos, len: e.len, msg: e.message },
    });
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.text === text && l.status !== 'start' && l.status !== 'given')
      return done({
        text,
        latex: null,
        ast: l.ast,
        status: 'useless',
        kind: 'kept',
        from: i,
        message: MSG.dup,
      });
  }
  const parts = [];
  for (const pt of splitOr(text)) {
    const ms = [];
    for (const t of splitChain(pt).members) {
      try {
        ms.push(parse(t, table));
      } catch (e) {
        if (!(e instanceof ParseError)) throw e;
        return perr(e);
      }
    }
    parts.push(ms);
  }
  const base = { text, latex: null, ast: parts[0][0] };
  if (parts.some((ms) => ms.length < 2))
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: MSG.notRelation });
  const keyL = (n) => keyOf(light(n, table));
  const z = X.unknown;
  const hasZ = (n) => !!z && symbolsIn(n).has(z);
  const val = (n, env = X.env) => cvalue(n, table, env);
  const gestures = [];
  const push = (name, ast) => {
    const rule = g(name);
    if (rule) gestures.push(gestureOf({ rule, ast }, ctx));
  };
  const doneOk = (goal, extra = {}) => {
    const hora = Math.min(
      LIMITS.horaCap,
      gestures.reduce((s2, x) => s2 + (x.implicit ? 0 : x.hora || 0), 0),
    );
    return done({
      ...base,
      status: goal ? 'goal' : 'step',
      kind: 'step',
      from: lastKept,
      gestures,
      hora,
      message: goal ? MSG.goal : gestures.length ? `Juste : ${labelsOf(gestures)}.` : 'Juste.',
      ...extra,
    });
  };
  // ─ équation en z : mêmes solutions que l'équation de départ
  if (z && (parts.length > 1 || parts.some((ms) => ms.some(hasZ)))) {
    const holds = (pts, v) =>
      pts.some((ms) =>
        ms.every(
          (m, k) =>
            k === 0 || ceq(val(ms[k - 1], { ...X.env, [z]: v }), val(m, { ...X.env, [z]: v })),
        ),
      );
    for (const s0 of X.solutions)
      if (!holds(parts, s0))
        return done({
          ...base,
          status: 'wrong',
          kind: null,
          from: lastKept,
          message: CX_MSG.notSame,
        });
    for (const r of CX_PROBES)
      if (holds(parts, r) !== holds(X.startParts, r))
        return done({
          ...base,
          status: 'wrong',
          kind: null,
          from: lastKept,
          message: CX_MSG.notSame,
        });
    const solved = parts.every(
      (ms) => ms[0].t === 'Sym' && ms[0].name === z && ms.slice(1).every((m) => !hasZ(m)),
    );
    const prevSolved = state.lines.at(-1)?.cx?.solved;
    if (solved) {
      push(prevSolved ? 'fold' : 'cx_solve', parts[0][1]);
      for (const ms of parts)
        for (let k = 2; k < ms.length; k++)
          push(complexGesture(ms[k - 1], ms[k], table) ?? 'fold', ms[k]);
    } else push('cx_equation', parts[0][0]);
    const lastKeys = parts.map((ms) => keyL(ms.at(-1)));
    const goal =
      solved &&
      lastKeys.length === X.ansKeys.length &&
      X.ansKeys.every((k) => lastKeys.includes(k));
    return doneOk(goal, { cx: { solved } });
  }
  // ─ calcul : chaque maillon de même valeur, et UN geste
  const ms = parts[0];
  const vals = ms.map((m) => val(m));
  if (vals.some((v) => v === null))
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: CX_MSG.noValue });
  for (let k = 1; k < ms.length; k++)
    if (!ceq(vals[k - 1], vals[k]))
      return done({
        ...base,
        status: 'wrong',
        kind: null,
        from: lastKept,
        message: CX_MSG.notEqual,
      });
  const fnOf = (n) =>
    table.nodes[n.t]?.eval === 'call' ? table.syntax.functions[n.name]?.eval : null;
  let jumped = false;
  for (let k = 1; k < ms.length; k++) {
    const a = ms[k - 1],
      b = ms[k];
    let name;
    if (a.t === 'Sym' && X.defs.has(a.name)) name = 'cx_delta';
    else if (
      has0(a, (x) => x.t === 'Sym' && X.known.has(x.name)) &&
      !has0(b, (x) => x.t === 'Sym' && X.known.has(x.name))
    )
      name = 'substitute';
    else if (fnOf(a) === 'arg') name = 'cx_arg';
    else name = complexGesture(a, b, table) ?? (cxOps(b) < cxOps(a) ? 'fold' : 'unfold');
    if (name === 'fold' && cxOps(a) - cxOps(b) > 7) jumped = true;
    push(name, b);
  }
  // la ligne doit parler de la question : départ, morceau d'une écriture déjà là, nom donné, ou son argument
  const seen = new Set();
  const collect = (n) => {
    seen.add(keyL(n));
    if (!isLeaf(n)) n.k.forEach(collect);
  };
  if (X.startAst) collect(X.startAst);
  for (const l of state.lines) for (const k0 of l.cx?.keys ?? []) seen.add(k0);
  const first = ms[0];
  for (const g0 of X.givenAsts) collect(g0);
  const speaks =
    seen.has(keyL(first)) ||
    has0(
      first,
      (x) =>
        (x.t === 'Sym' && (X.defs.has(x.name) || X.known.has(x.name))) ||
        (!isLeaf(x) && seen.has(keyL(x))),
    );
  const keys = ms.map(keyL);
  if (!speaks)
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: lastKept,
      gestures,
      hora: 0,
      message: CX_MSG.aside,
      cx: { keys },
    });
  // but : l'écriture de départ reliée (par des « = ») au résultat attendu
  const parent = new Map();
  const find = (k0) => {
    if (!parent.has(k0)) return k0;
    while (parent.get(k0) !== k0) k0 = parent.get(k0);
    return k0;
  };
  const unite = (list) => {
    list.forEach((k0) => {
      if (!parent.has(k0)) parent.set(k0, k0);
    });
    for (let i = 1; i < list.length; i++) parent.set(find(list[i]), find(list[0]));
  };
  for (const l of state.lines) if (l.cx?.chain) unite(l.cx.chain);
  unite(keys);
  const goal =
    !!X.startKey &&
    X.ansKeys.length === 1 &&
    parent.has(X.ansKeys[0]) &&
    find(X.ansKeys[0]) === find(X.startKey);
  if (jumped)
    return done({
      ...base,
      status: goal ? 'goal' : 'unjustified',
      kind: 'kept',
      from: lastKept,
      gestures,
      hora: 0,
      message: goal ? MSG.unjustifiedGoal : MSG.unjustified,
      cx: { keys, chain: keys },
    });
  return doneOk(goal, { cx: { keys, chain: keys } });
}

function has0(n, test) {
  return test(n) || (!isLeaf(n) && n.k.some((c) => has0(c, test)));
}

// ── Feuille « entiers » : congruences et arithmétique (lots I7, I13) ─────────
const INT_MSG = {
  notInt: 'Écris des calculs sur des entiers.',
  notEqual: 'Ce n’est pas égal : vérifie ce calcul.',
  notCong: (n) => `Ce n’est pas congru modulo ${n}.`,
  notSolution: 'Ces valeurs ne vérifient pas l’équation.',
};

function judgeIntegerLine(engine, state, text, done) {
  const { ctx } = state;
  const table = engine.table,
    I = ctx.ints;
  const lastKept = state.lines.length - 1;
  const g = (name) => engine.builtins?.[name];
  const parseOr = (t) => {
    try {
      return { ast: parse(t, table) };
    } catch (e) {
      if (!(e instanceof ParseError)) throw e;
      return { err: e };
    }
  };
  const perr = (e) =>
    done({
      status: 'parse_error',
      kind: null,
      text,
      latex: null,
      ast: null,
      message: e.message,
      error: { pos: e.pos, len: e.len, msg: e.message },
    });
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.text === text && l.status !== 'start' && l.status !== 'given')
      return done({
        text,
        latex: null,
        ast: l.ast,
        status: 'useless',
        kind: 'kept',
        from: i,
        message: MSG.dup,
      });
  }
  const start = state.lines[0].ast;
  // affectations « u = 2 ; v = −1 » : elles doivent vérifier l'équation de départ
  const asg = splitAssignments(text);
  if (asg) {
    const env = {};
    for (const [n, t] of asg) {
      const p0 = parseOr(t);
      if (p0.err) return perr(p0.err);
      const v = intValue(p0.ast, table);
      if (v === null)
        return done({
          text,
          latex: null,
          ast: p0.ast,
          status: 'wrong',
          kind: null,
          from: lastKept,
          message: INT_MSG.notInt,
        });
      env[n] = v;
    }
    const ok =
      isRelation(start, table) &&
      start.k.length === 2 &&
      intValue(start.k[0], table, env) !== null &&
      intValue(start.k[0], table, env) === intValue(start.k[1], table, env);
    const base = { text, latex: null, ast: start };
    if (!ok)
      return done({
        ...base,
        status: 'wrong',
        kind: null,
        from: lastKept,
        message: INT_MSG.notSolution,
      });
    const rule = g('fold');
    return done({
      ...base,
      status: 'goal',
      kind: 'step',
      from: lastKept,
      gestures: rule ? [gestureOf({ rule, ast: start }, ctx)] : [],
      hora: 1,
      message: MSG.goal,
    });
  }
  const sm = splitModulus(text);
  const n = sm.mod ?? I.mod;
  const ch = splitChain(sm.text);
  const ms = [];
  for (const t of ch.members) {
    const p0 = parseOr(t);
    if (p0.err) return perr(p0.err);
    ms.push(p0.ast);
  }
  const base = { text, latex: null, ast: ms[0] };
  if (ms.length < 2)
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: MSG.notRelation });
  // lettres de la ligne : chacune parcourt les restes modulo n compatibles avec les données et les lignes écrites
  const letters = [...new Set(ms.flatMap((m) => [...symbolsIn(m)]))];
  const hasX = (a) => symbolsIn(a).size > 0;
  const lineHasX = letters.length > 0;
  let envs = [{}];
  if (lineHasX && n && n ** BigInt(letters.length) <= 4096n) {
    const eqs = [...I.eqs, ...state.lines.filter((l) => l.ints?.eq).map((l) => l.ints.eq)];
    const holds = (e, env) =>
      e.members.every((m, k) => {
        if (k === 0) return true;
        const mm = e.mod ?? n,
          p0 = e.members[k - 1];
        if (e.ops[k - 1] === '≡') {
          const a = modValue(m, table, mm, env),
            b = modValue(p0, table, mm, env);
          return a === null || b === null || a === b;
        }
        const a = intValue(m, table, env),
          b = intValue(p0, table, env);
        return a === null || b === null || a === b;
      });
    envs = [];
    const total = Number(n ** BigInt(letters.length));
    for (let i = 0; i < total; i++) {
      const env = {};
      let r = BigInt(i);
      for (const L of letters) {
        env[L] = r % n;
        r /= n;
      }
      if (eqs.every((e) => holds(e, env))) envs.push(env);
    }
    if (!envs.length) envs = [Object.fromEntries(letters.map((L) => [L, 0n]))];
  }
  for (let k = 1; k < ms.length; k++) {
    for (const env of envs) {
      if (ch.ops[k - 1] === '≡') {
        if (!n)
          return done({
            ...base,
            status: 'wrong',
            kind: null,
            from: lastKept,
            message: INT_MSG.notCong('?'),
          });
        const a = modValue(ms[k - 1], table, n, env),
          b = modValue(ms[k], table, n, env);
        if (a === null || b === null)
          return done({
            ...base,
            status: 'wrong',
            kind: null,
            from: lastKept,
            message: INT_MSG.notInt,
          });
        if (a !== b)
          return done({
            ...base,
            status: 'wrong',
            kind: null,
            from: lastKept,
            message: INT_MSG.notCong(n),
          });
      } else {
        const lift = n && (hasX(ms[k - 1]) || hasX(ms[k]));
        const a = lift ? null : intValue(ms[k - 1], table, env),
          b = lift ? null : intValue(ms[k], table, env);
        if (lift) {
          const ok = [0n, 1n, 2n].every((t) => {
            const e2 = Object.fromEntries(Object.entries(env).map(([L, v]) => [L, v + t * n]));
            return intValue(ms[k - 1], table, e2) === intValue(ms[k], table, e2);
          });
          if (!ok)
            return done({
              ...base,
              status: 'wrong',
              kind: null,
              from: lastKept,
              message: INT_MSG.notEqual,
            });
          continue;
        }
        if (a === null || b === null) {
          // un PGCD, une puissance énorme : on compare modulo n quand il y a un module
          if (
            n &&
            modValue(ms[k - 1], table, n, env) !== null &&
            modValue(ms[k - 1], table, n, env) === modValue(ms[k], table, n, env)
          )
            continue;
          return done({
            ...base,
            status: 'wrong',
            kind: null,
            from: lastKept,
            message: INT_MSG.notInt,
          });
        }
        if (a !== b)
          return done({
            ...base,
            status: 'wrong',
            kind: null,
            from: lastKept,
            message: INT_MSG.notEqual,
          });
      }
    }
  }
  // un maillon « ≡ » doit être UN geste : réduire un petit nombre, retirer un multiple du modulo écrit, ou remplacer
  // sur place un morceau par un reste (petit nombre, ou congruence déjà établie) sans toucher aux exposants
  const small = (v) => v !== null && (v < 0n ? -v : v) <= (n && n * 10n > 100n ? n * 10n : 100n);
  const established = new Set();
  for (const l of state.lines) if (l.ints?.keys) l.ints.keys.forEach((k0) => established.add(k0));
  const isMultipleOfN = (x) => {
    const v = intValue(x, table);
    return v !== null && n && v % n === 0n;
  };
  const reducible = (a) => {
    if (small(intValue(a, table))) return true;
    if (table.nodes[a.t]?.eval === 'add')
      return (
        a.k.some((c) => isMultipleOfN(c) && !small(intValue(c, table))) ||
        a.k.some(
          (c) => table.nodes[c.t]?.eval === 'mul' && c.k.some((f) => intValue(f, table) === n),
        )
      );
    return false;
  };
  const inPlace = (a, b) => {
    if (keyOf(light(a, table)) === keyOf(light(b, table))) return true;
    if (a.t === b.t && !isLeaf(a) && !isLeaf(b) && a.k.length === b.k.length) {
      if (
        table.nodes[a.t]?.eval === 'pow' &&
        keyOf(light(a.k[1], table)) !== keyOf(light(b.k[1], table))
      )
        return false;
      return a.k.every((c, i) => inPlace(c, b.k[i]));
    }
    return small(intValue(a, table)) || established.has(keyOf(light(a, table)));
  };
  let jumped = false;
  // gestes, maillon par maillon
  const gestures = [];
  const callIn = (a) => {
    let f = false;
    visitTree(a, (x) => {
      if (table.nodes[x.t]?.eval === 'call') f = true;
    });
    return f;
  };
  for (let k = 1; k < ms.length; k++) {
    const a = ms[k - 1],
      b = ms[k];
    let rule;
    if (ch.ops[k - 1] === '≡') {
      const pw = table.nodes[a.t]?.eval === 'pow' ? a : null;
      const e = pw ? intValue(pw.k[1], table) : null;
      if (pw && n && isPrimeBig(n) && e === n - 1n && intValue(b, table) === 1n)
        rule = g('mod_fermat');
      else if (hasX(a) && !hasX(b) && [...symbolsIn(a)].every((L) => I.known.has(L)))
        rule = g('substitute');
      else if (lineHasX && !(a.t === 'Sym' && a.name === I.x)) rule = g('relation_mul');
      else {
        rule = g('mod_reduce');
        if (!hasX(a) && n && !reducible(a) && !inPlace(a, b)) jumped = true;
      }
    } else if (isEuclid(a, b, table)) rule = g('euclid');
    else if (isPrimeProduct(b, table) && intValue(a, table) !== null && !isPrimeProduct(a, table))
      rule = g('factorize_int');
    else if (callIn(a) || callIn(b)) rule = g('gcd_read');
    else rule = g('fold');
    if (rule) gestures.push(gestureOf({ rule, ast: b }, ctx));
  }
  const hora = Math.min(
    LIMITS.horaCap,
    gestures.reduce((s2, x) => s2 + (x.implicit ? 0 : x.hora || 0), 0),
  );
  // but : l'expression de départ (ou une écriture qui lui est reliée) écrite égale ou congrue au résultat attendu
  const keys = ms.map((m) => keyOf(light(m, table)));
  const parent = new Map();
  const find = (k) => {
    if (!parent.has(k)) return k;
    while (parent.get(k) !== k) k = parent.get(k);
    return k;
  };
  const unite = (list) => {
    list.forEach((k) => {
      if (!parent.has(k)) parent.set(k, k);
    });
    for (let i = 1; i < list.length; i++) parent.set(find(list[i]), find(list[0]));
  };
  for (const l of state.lines) if (l.ints?.keys) unite(l.ints.keys);
  unite(keys);
  const startKey = keyOf(light(isRelation(start, table) ? start.k[0] : start, table));
  const want = I.answerLast ? keyOf(light(I.answerLast, table)) : null;
  const allKeys = [...parent.keys()];
  const goal =
    !!want &&
    allKeys.some((k) => k === want && find(k) === find(startKey)) &&
    (I.kind !== 'congruence' ||
      (() => {
        const v = intValue(I.answerLast, table);
        return v !== null && (!n || (v >= 0n && v < n));
      })());
  if (jumped)
    return done({
      ...base,
      status: goal ? 'goal' : 'unjustified',
      kind: 'kept',
      from: lastKept,
      gestures,
      hora: 0,
      message: goal ? MSG.unjustifiedGoal : MSG.unjustified,
      ints: { keys, eq: null },
    });
  const eq =
    I.x && symbolsIn(ms[0]).has(I.x) && !(ms[0].t === 'Sym' && ms[0].name === I.x)
      ? { members: ms, ops: ch.ops, mod: n }
      : null;
  return done({
    ...base,
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from: lastKept,
    gestures,
    hora,
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
    ints: { keys, eq },
  });
}

function symbolsIn(n, out = new Set()) {
  if (n.t === 'Sym') out.add(n.name);
  else if (!isLeaf(n)) n.k.forEach((c) => symbolsIn(c, out));
  return out;
}

// ── Feuille « limites » au format publié (lot I1) ──────────────────────────
const LIM_MSG = {
  wrongLimit: 'Ce n’est pas la bonne limite.',
  notEqual: 'Ce n’est pas la même fonction : vérifie ta réécriture.',
  notEquiv: 'Ces deux fonctions ne sont pas équivalentes au point.',
  notNegligible: 'Le reste n’est pas négligeable : vérifie ton développement.',
  bound: 'Cet encadrement n’est pas vrai près du point.',
  aside: 'Juste, mais à côté : travaille sur l’expression de l’énoncé.',
};

function limitState(state, table) {
  // classes d'écritures égales (lignes « A = B »), limites établies (« E → ℓ »), restes (lignes avec o), encadrements
  const parent = new Map();
  const find = (k) => {
    if (!parent.has(k)) return k;
    while (parent.get(k) !== k) k = parent.get(k);
    return k;
  };
  const add = (k) => {
    if (!parent.has(k)) parent.set(k, k);
  };
  const known = new Map(),
    dl = new Set(),
    bounds = [];
  for (const l of state.lines) {
    const lim = l.limit;
    if (!lim) {
      if (l.L && !isRelation(l.L, table)) add(keyOf(l.L));
      continue;
    }
    if (lim.kind === 'eq') {
      lim.keys.forEach(add);
      for (let i = 1; i < lim.keys.length; i++) parent.set(find(lim.keys[i]), find(lim.keys[0]));
      if (lim.o) dl.add(lim.keys[0]);
    } else if (lim.kind === 'to') {
      add(lim.key);
      known.set(lim.key, lim.value);
    } else if (lim.kind === 'bound') bounds.push(lim);
  }
  return { find, known, dl, bounds, has: (k) => parent.has(k) || known.has(k) };
}

// Réponse « A = P + o(g) » (développement limité demandé) : la ligne de DL de l'expression de départ, à l'ordre g.
function dlAnswer(answer, tryP, table) {
  if (!answer || !/\bo\(/.test(answer)) return null;
  const o = extractSmallO(answer);
  const ast = tryP(o.text),
    rest = o.rests.length ? tryP(o.rests.at(-1)) : null;
  if (!ast || !rest || table.nodes[ast.t]?.relation !== 'eq' || ast.k.length < 2) return null;
  return {
    op: 'dl',
    key: keyOf(light(ast, table)),
    last: ast.k.at(-1),
    rest,
    restKey: keyOf(light(rest, table)),
  };
}

function judgeLimitLine(engine, state, text, done) {
  const { ctx } = state;
  const table = engine.table;
  let P = ctx.limit.P;
  const lastKept = state.lines.length - 1;
  const g = (name) => engine.builtins?.[name];
  const verdict = (base, rule, extra = {}) => {
    const gestures = rule ? [gestureOf({ rule, ast: base.ast }, ctx)] : [];
    const hora = gestures.reduce((s2, x) => s2 + (x.implicit ? 0 : x.hora || 0), 0);
    return done({
      ...base,
      status: extra.goal ? 'goal' : rule ? 'step' : 'unjustified',
      kind: rule ? 'step' : 'kept',
      from: lastKept,
      gestures,
      hora: rule ? hora : 0,
      message: extra.goal ? MSG.goal : rule ? `Juste : ${labelsOf(gestures)}.` : MSG.unjustified,
      ...extra,
    });
  };
  const parseOr = (t) => {
    try {
      return { ast: parse(t, table) };
    } catch (e) {
      if (!(e instanceof ParseError)) throw e;
      return { err: e };
    }
  };
  const S = limitState(state, table);
  const startKey = keyOf(light(state.lines[0].ast, table));
  // morceaux des écritures déjà là (départ, membres écrits) : une réécriture d'un morceau parle de la question
  const pieces = new Set();
  for (const l of state.lines)
    if (l.ast)
      visitTree(light(l.ast, table), (n) => {
        if (!isLeaf(n)) pieces.add(keyOf(n));
      });
  const linked = (k) => S.find(k) === S.find(startKey) || k === startKey;
  const split = splitLimitLine(text);

  if (split) {
    const L = parseOr(split.left),
      R = parseOr(split.right);
    if (L.err || R.err) {
      const e = L.err ?? R.err;
      return done({
        status: 'parse_error',
        kind: null,
        text,
        latex: null,
        ast: null,
        message: e.message,
        error: { pos: e.pos, len: e.len, msg: e.message },
      });
    }
    const E = L.ast,
      EL = light(E, table),
      ek = keyOf(EL);
    const base = { text, latex: null, ast: E };
    if (split.op === 'to') {
      const claim = ctx.limit.claims?.find((c) => c && c.key === ek);
      if (claim) P = claim.P;
      const want = limNum(
        evaluate(R.ast.t === 'Right' || R.ast.t === 'Left' ? R.ast.k[0] : R.ast, {}, table),
      );
      const truth = trueLimit(E, P, table);
      if (!sameLimit(truth, want))
        return done({
          ...base,
          status: 'wrong',
          kind: null,
          from: lastKept,
          message: LIM_MSG.wrongLimit,
        });
      const lim = { kind: 'to', key: ek, value: want };
      if (ctx.limit.claims) {
        const decided = new Map([...S.known, [ek, want]]);
        const all = ctx.limit.claims.every((c) => !c || decided.has(c.key));
        const trues = ctx.limit.claims.filter(
          (c) => c && decided.has(c.key) && sameLimit(decided.get(c.key), c.value),
        ).length;
        const done1 = all || (ctx.limit.single && trues > 0);
        return verdict(
          base,
          claim
            ? opsLimit(E, P, table, false) !== null
              ? g('limit_ops')
              : g('limit_growth')
            : null,
          { goal: done1, limit: lim },
        );
      }
      const goal =
        !!ctx.limit.answer &&
        ctx.limit.answer.op === 'to' &&
        linked(ek) &&
        sameLimit(
          want,
          limNum(
            evaluate(
              ctx.limit.answer.right?.t === 'Right' || ctx.limit.answer.right?.t === 'Left'
                ? ctx.limit.answer.right.k[0]
                : ctx.limit.answer.right,
              {},
              table,
            ),
          ),
        );
      if (S.known.has(ek))
        return done({
          ...base,
          status: goal ? 'goal' : 'useless',
          kind: 'kept',
          from: lastKept,
          message: goal ? MSG.goal : MSG.dup,
          limit: lim,
        });
      // gestes : référence, opérations, croissances comparées, gendarmes, lecture sur une écriture égale
      let rule = null;
      // un morceau dont la limite est établie (« (−1)ⁿ ÷ n → 0 ») compte comme connu pour les opérations
      const env = {};
      let kn = 0;
      const known = (n) => {
        if (isLeaf(n)) return n;
        const k = keyOf(light(n, table));
        if (k !== ek && S.known.has(k)) {
          const name = `__lim${kn++}`;
          env[name] = S.known.get(k);
          return { t: 'Sym', name };
        }
        return { ...n, k: n.k.map(known) };
      };
      const Ek = kn === 0 ? known(E) : E;
      const withKnown = kn ? opsLimitEnv(Ek, P, table, env) : null;
      const ops =
        withKnown !== null && sameLimit(withKnown, want) ? withKnown : opsLimit(E, P, table, false);
      if (ops !== null && sameLimit(ops, want))
        rule = occurrences(E, P.v) <= 1 && opsOf(E) <= 2 ? g('limit_ref') : g('limit_ops');
      else if (opsLimit(E, P, table, true) !== null) rule = g('limit_growth');
      else if (S.bounds.some((b) => b.mid === ek)) rule = g('limit_squeeze');
      else if (S.find(ek) !== ek || [...S.known.keys()].some((k) => S.find(k) === S.find(ek)))
        rule =
          S.dl.has(S.find(ek)) || [...S.dl].some((k) => S.find(k) === S.find(ek))
            ? g('limit_read')
            : g('substitute');
      else if ([...S.dl].length) rule = g('limit_read');
      // limite remarquable (sin x ÷ x, (eˣ − 1) ÷ x…) : courte, une seule fonction
      else if (opsOf(E) <= 5)
        rule = /exp|ln|e\^/.test(text) && P.point === Infinity ? g('limit_growth') : g('limit_ref');
      return verdict(base, rule, { goal, limit: lim });
    }
    // équivalents
    const B = R.ast;
    const q = trueLimit(
      { t: Object.entries(table.nodes).find(([, sp]) => sp.eval === 'div')[0], k: [E, B] },
      P,
      table,
    );
    // deux écritures de la même fonction (« (x + x²) − x ∼ x² ») : le quotient vaut 1, même là où le calcul
    // machine perd les petits termes près du point
    if (!sameLimit(q, 1) && !fnEqual(E, B, table))
      return done({
        ...base,
        status: 'wrong',
        kind: null,
        from: lastKept,
        message: LIM_MSG.notEquiv,
      });
    const ans = ctx.limit.answer;
    const goal =
      !!ans &&
      ans.op === 'equiv' &&
      linked(ek) &&
      !!ans.right &&
      keyOf(light(B, table)) === keyOf(light(ans.right, table));
    return verdict(base, g('limit_equiv'), { goal, limit: { kind: 'eq', keys: [ek] } });
  }

  // relation ordinaire : réécriture, développement limité, encadrement
  const o = extractSmallO(text);
  const parsed = parseOr(o.text);
  if (parsed.err) {
    const e = parsed.err;
    return done({
      status: 'parse_error',
      kind: null,
      text,
      latex: null,
      ast: null,
      message: e.message,
      error: { pos: e.pos, len: e.len, msg: e.message },
    });
  }
  const ast = parsed.ast;
  const base = { text, latex: null, ast };
  if (!isRelation(ast, table))
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: MSG.notRelation });
  const rel = table.nodes[ast.t]?.relation;
  const conn = table.nodes[ast.t]?.connective;
  if (conn === 'and' || ['lt', 'le', 'gt', 'ge'].includes(rel)) {
    if (!holdsNear(ast, P, table))
      return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: LIM_MSG.bound });
    const terms = conn === 'and' ? ast.k.flatMap((c) => c.k) : ast.k;
    const mid = terms.length >= 3 ? keyOf(light(terms[1], table)) : null;
    return verdict(base, g('limit_bound'), { limit: { kind: 'bound', mid } });
  }
  if (rel !== 'eq')
    return done({ ...base, status: 'wrong', kind: null, from: lastKept, message: MSG.notRelation });
  const members = ast.k;
  const div = Object.entries(table.nodes).find(([, sp]) => sp.eval === 'div')[0];
  const add = Object.entries(table.nodes).find(([, sp]) => sp.eval === 'add')[0];
  const neg = Object.entries(table.nodes).find(([, sp]) => sp.eval === 'neg')[0];
  for (let i = 1; i < members.length; i++) {
    const a = members[i - 1],
      b = members[i];
    if (o.rests.length) {
      // A = P + o(g) : (A − P) ÷ g → 0 (le reste est écrit dans le dernier membre)
      const gp = parseOr(o.rests.at(-1));
      if (gp.err)
        return done({
          ...base,
          status: 'wrong',
          kind: null,
          from: lastKept,
          message: LIM_MSG.notNegligible,
        });
      const diff = { t: div, k: [{ t: add, k: [a, { t: neg, k: [b] }] }, gp.ast] };
      if (!sameLimit(trueLimit(diff, P, table), 0))
        return done({
          ...base,
          status: 'wrong',
          kind: null,
          from: lastKept,
          message: LIM_MSG.notNegligible,
        });
    } else if (!holdsNear({ t: ast.t, k: [a, b] }, P, table)) {
      return done({
        ...base,
        status: 'wrong',
        kind: null,
        from: lastKept,
        message: LIM_MSG.notEqual,
      });
    }
  }
  const keys = members.map((m) => keyOf(light(m, table)));
  if (!keys.some((k) => S.has(k) || k === startKey || pieces.has(k)) && !o.rests.length) {
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: lastKept,
      message: LIM_MSG.aside,
      limit: { kind: 'eq', keys, o: false },
    });
  }
  // développement limité demandé : la ligne qui écrit le DL de l'expression de départ à l'ordre de la réponse
  const ans = ctx.limit.answer;
  let goal = false,
    dlKey = null;
  if (o.rests.length) {
    const rest = parseOr(o.rests.at(-1));
    dlKey = rest.err ? null : keyOf(light(rest.ast, table));
    if (ans?.op === 'dl' && dlKey === ans.restKey && linked(keys[0])) {
      const diff = {
        t: div,
        k: [{ t: add, k: [members.at(-1), { t: neg, k: [ans.last] }] }, ans.rest],
      };
      goal = sameLimit(trueLimit(diff, P, table), 0);
    }
  }
  return verdict(base, o.rests.length ? g('limit_dl') : g('limit_rewrite'), {
    goal,
    limit: { kind: 'eq', keys, o: !!o.rests.length, dl: dlKey ? keyOf(light(ast, table)) : null },
  });
}

// ── Feuille « grandeurs et unités » (lot M5) ───────────────────────────────
// Une ligne = une chaîne de grandeurs reliées par « = ». Chaque maillon : même nature et même valeur (unités de
// base), puis justifié — même écriture d'unités des deux côtés → « Calculer », sinon → « Convertir ».
const QTY_MSG = {
  notQuantity: 'Écris des grandeurs : un nombre et son unité.',
  wrong: 'Ce n’est pas juste : vérifie ce calcul.',
  minutes: 'Une heure fait 60 minutes, pas 100.',
  aside: 'Juste, mais à côté : calcule la grandeur demandée.',
};

function judgeQuantityLine(engine, state, ast, done) {
  const { ctx } = state;
  const table = engine.table,
    units = ctx.quantities.units;
  const base = { text: toText(ast, table), latex: toLatex(ast, table), ast };
  const lastKept = state.lines.length - 1;
  const wrong = (message) =>
    done({ ...base, status: 'wrong', kind: null, from: lastKept, message });
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.text === base.text && l.status !== 'start' && l.status !== 'given')
      return done({ ...base, status: 'useless', kind: 'kept', from: i, message: MSG.dup });
  }
  if (table.nodes[ast.t]?.relation !== 'eq') return wrong(MSG.notRelation);
  const qs = ast.k.map((m) => quantityOf(m, units, table));
  for (const q of qs) {
    if (!q) return wrong(QTY_MSG.notQuantity);
    if (q.mismatch)
      return wrong(
        `On n’ajoute pas ${dimensionName(q.mismatch[0], engine)} et ${dimensionName(q.mismatch[1], engine)}.`,
      );
  }
  for (let i = 1; i < qs.length; i++) {
    if (!sameDim(qs[i - 1].dim, qs[i].dim)) {
      return wrong(
        `Pas la même nature : à gauche ${dimensionName(qs[i - 1].dim, engine)}, à droite ${dimensionName(qs[i].dim, engine)}.`,
      );
    }
    if (!sameQuantity(qs[i - 1], qs[i])) {
      const h = ratioHint(qs[i].v, qs[i - 1].v);
      if (h?.minutes) return wrong(QTY_MSG.minutes);
      if (h?.factor)
        return wrong(
          `C’est ${String(h.factor).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} fois trop ${h.bigger ? 'grand' : 'petit'} : vérifie la conversion.`,
        );
      return wrong(QTY_MSG.wrong);
    }
  }
  // la ligne parle de la question : un membre écrit plus haut (ou un morceau), ou la même grandeur
  const known = [],
    knownKeys = new Set();
  for (const l of state.lines) {
    if (!l.ast) continue;
    const roots = isRelation(l.ast, table) ? l.ast.k : [l.ast];
    for (const r of roots)
      visitTree(r, (n) => {
        knownKeys.add(keyOf(light(n, table)));
        const q = quantityOf(n, units, table);
        if (q && !q.mismatch && !isLeaf(n)) known.push(q);
      });
  }
  const talks = ast.k.some(
    (m, i) => knownKeys.has(keyOf(light(m, table))) || known.some((q) => sameQuantity(q, qs[i])),
  );
  if (!talks)
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      from: lastKept,
      message: QTY_MSG.aside,
    });
  // gestes
  const gestures = [];
  for (let i = 1; i < ast.k.length; i++) {
    const a = ast.k[i - 1],
      b = ast.k[i];
    if (keyOf(light(a, table)) === keyOf(light(b, table))) continue;
    const same =
      signatureKey(unitSignature(a, units, table)) === signatureKey(unitSignature(b, units, table));
    const rule = same ? engine.builtins?.fold : engine.builtins?.convert;
    if (rule) gestures.push(gestureOf({ rule, ast: b }, ctx));
  }
  const last = light(ast.k.at(-1), table);
  const goal =
    !!ctx.answerKey &&
    (keyOf(last) === ctx.answerKey || ast.k.some((m) => keyOf(light(m, table)) === ctx.answerKey));
  const hora = Math.min(
    LIMITS.horaCap,
    gestures.reduce((s, g) => s + (g.implicit ? 0 : g.hora || 0), 0),
  );
  return done({
    ...base,
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from: lastKept,
    gestures,
    hora,
    message: goal ? MSG.goal : gestures.length ? `Juste : ${labelsOf(gestures)}.` : 'Juste.',
  });
}

// ── Feuille « remplir un tableau » (plugin tables, lot I8) ─────────────────
// L'état du tableau (table-sheets.js) est rangé dans chaque ligne gardée ; la feuille n'a pas de ligne lue par
// le lecteur d'expressions : le départ est le tableau de l'énoncé, chaque ligne un tableau ou une lecture.
function createTabSheet(engine, model, kernel, goal, target) {
  const tabs = createTableSheet(model, engine.table);
  const ctx = {
    engine,
    table: engine.table,
    model,
    kernel,
    goal,
    target,
    tabs,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: tableStartText(tabs),
    ast: null,
    L: null,
    key: 'tab:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  return { ctx, lines: [start], done: false };
}

function currentTab(state) {
  for (let i = state.lines.length - 1; i >= 0; i--)
    if (state.lines[i].tab) return state.lines[i].tab;
  return state.ctx.tabs;
}

function judgeTabLine(state, text, t0) {
  const v = judgeTableLine(currentTab(state), text);
  return {
    gestures: [],
    hora: 0,
    from: state.lines.length - 1,
    ...v,
    text,
    latex: null,
    ast: null,
    ms: Math.round((now() - t0) * 100) / 100,
  };
}

// ── Conversion dans une feuille « formule » (physique, lot P1) ───────────
// `kernel.units` = { grandeur: unité de la formule } (« I: 'A' », « m: 'kg' »). Une ligne « I = 250 mA = 0,25 A »
// (ou « I = 250 mA » seule) est une chaîne de grandeurs : même nature, même valeur que la grandeur de l'exercice ;
// finir dans l'unité de la formule = « Convertir ». Une ligne sans unité suit le chemin ordinaire (null ici).
function judgeConversionLine(engine, state, lineText, done) {
  const { ctx } = state;
  const m = String(lineText).match(/^\s*([^=]+?)\s*=\s*([\s\S]+)$/);
  if (!m) return null;
  const head = m[1].trim();
  const unitName = ctx.kernel.units[head];
  if (!unitName || !(head in ctx.world)) return null;
  const units = engine.quantities.units;
  const qtable = (ctx.qtable ??= withIdentifiers(engine.table, unitIdentifiers(units)));
  const read = (t) => {
    try {
      return quantityOf(parse(groupJuxtaposed(t, units), qtable), units, qtable);
    } catch {
      return null;
    }
  };
  const members = m[2].split('=').map((t) => t.trim());
  // une conversion écrit un NOMBRE suivi d'une unité (« 250 mA ») ; « m × g » est une formule
  if (!members.some((t) => /\d\s*[\p{L}°Ωµ€]/u.test(t))) return null;
  const qs = members.map(read);
  // aucune unité écrite : une ligne ordinaire de la feuille
  if (!qs.some((q) => q && !q.mismatch && Object.keys(q.dim).length)) return null;
  const lastKept = state.lines.length - 1;
  // pour la suite de la feuille, la ligne vaut « I = 0,25 » (la grandeur dans l'unité de la formule)
  let ast = null;
  try {
    const w = ctx.world[head];
    const wt = typeof w === 'number' ? String(w).replace('.', ',') : `${w.p}/${w.q}`;
    ast = parse(`${head} = ${wt}`, engine.table);
  } catch {
    ast = null;
  }
  const base = { text: lineText, latex: null, ast, from: lastKept };
  const wrong = (title, message) =>
    done({ ...base, ast: null, status: 'wrong', kind: null, title, message });
  if (qs.some((q) => !q || q.mismatch))
    return wrong('Une grandeur', 'Écris chaque valeur avec son unité : « 250 mA ».');
  const unit = read(`1 ${unitName}`);
  if (!unit) return null;
  const want = { v: toF(ctx.world[head]) * unit.v, dim: unit.dim };
  for (const q of qs) {
    if (!sameDim(q.dim, want.dim))
      return wrong(
        'Pas la même nature',
        `${head} est ${dimensionName(want.dim, engine)} : ${dimensionName(q.dim, engine)} ne convient pas.`,
      );
    if (!sameQuantity(q, want)) {
      const h = ratioHint(q.v, want.v);
      return wrong(
        'La conversion',
        h?.factor
          ? `C’est ${String(h.factor).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} fois trop ${h.bigger ? 'grand' : 'petit'} : vérifie la conversion.`
          : `Ce n’est pas la valeur de ${head}.`,
      );
    }
  }
  for (let i = 0; i < state.lines.length; i++)
    if (state.lines[i].text === lineText)
      return done({ ...base, status: 'useless', kind: 'kept', message: MSG.dup });
  // la dernière écriture est-elle dans l'unité de la formule ?
  const inUnit = (t) => {
    const one = read(t.replace(/^[\d\s,.]+/u, '1 '));
    return !!one && sameDim(one.dim, unit.dim) && Math.abs(one.v - unit.v) <= 1e-12 * unit.v;
  };
  const last = members.at(-1);
  const sig = (t) => t.replace(/^[\d\s,.]+/u, '').trim();
  const changed = members.length > 1 && sig(members[0]) !== sig(last);
  if (!inUnit(last))
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      title: 'Juste, mais…',
      message: `La formule attend ${head} en ${unitName} : convertis.`,
    });
  if (!changed)
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      title: 'Donnée recopiée',
      message: `${head} est déjà en ${unitName} : utilise la formule.`,
    });
  const rule = engine.builtins?.convert;
  return done({
    ...base,
    status: 'step',
    kind: 'step',
    gestures: [
      {
        ruleId: rule?.id ?? 'grandeurs.convertir',
        label: rule?.label ?? 'Convertir',
        why:
          rule?.why ??
          'On change d’unité : la grandeur ne change pas, le nombre qui la mesure, si.',
        hora: rule?.hora ?? 2,
      },
    ],
    hora: rule?.hora ?? 2,
    message: `Juste : ${head} converti en ${unitName}.`,
  });
}

// ── Tableau d'avancement (plugin avancement, lot P2) ─────────────────────
// Comme les tableaux : l'état (progress-sheets.js) est rangé dans chaque ligne gardée ; le départ est l'équation.
function createAdvSheet(engine, model, kernel, goal, target) {
  const adv = createAdvState(model, engine.table);
  const ctx = {
    engine,
    table: engine.table,
    model,
    kernel,
    goal,
    target,
    adv,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: advStartText(model),
    ast: null,
    L: null,
    key: 'adv:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  return { ctx, lines: [start], done: false };
}

function currentAdv(state) {
  for (let i = state.lines.length - 1; i >= 0; i--)
    if (state.lines[i].adv) return state.lines[i].adv;
  return state.ctx.adv;
}

function judgeAdvSheetLine(state, text, t0) {
  const v = judgeAdvLine(currentAdv(state), text);
  return {
    from: state.lines.length - 1,
    ...v,
    text,
    latex: null,
    ast: null,
    ms: Math.round((now() - t0) * 100) / 100,
  };
}

// ── Calcul matriciel (plugin matrix) ─────────────────────────────────────
// Le juge de l'ancien moteur, transcrit (matrix-sheets.js) ; son état est rangé dans chaque ligne gardée.
function createMatSheet(engine, model) {
  const mats = createMatrixSheet(model);
  const kernel = model.kernel;
  const ctx = {
    engine,
    table: engine.table,
    model,
    kernel,
    goal: kernel.goal,
    target: kernel.goal?.target ?? null,
    mats,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: matrixStartText(model),
    ast: null,
    L: null,
    key: 'mat:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  return { ctx, lines: [start], done: false };
}

function judgeMatLine(state, text, t0) {
  let ks = state.ctx.mats;
  for (let i = state.lines.length - 1; i >= 0; i--)
    if (state.lines[i].mat) {
      ks = state.lines[i].mat;
      break;
    }
  const v = judgeMatrixLine(ks, text);
  return {
    gestures: [],
    hora: 0,
    from: state.lines.length - 1,
    ...v,
    text,
    latex: null,
    ast: null,
    ms: Math.round((now() - t0) * 100) / 100,
  };
}

// ── Trace d'un programme Python (plugin python) ─────────────────────────────
// Le juge de l'ancien moteur, transcrit (python-sheets.js) ; son état est rangé dans chaque ligne gardée.
function createProgSheet(engine, model) {
  const progs = createPythonSheet(model);
  const kernel = model.kernel;
  const ctx = {
    engine,
    table: engine.table,
    model,
    kernel,
    goal: kernel.goal,
    target: kernel.goal?.target ?? null,
    progs,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: pythonStartText(model),
    ast: null,
    L: null,
    key: 'prog:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  return { ctx, lines: [start], done: false };
}

function judgeProgLine(state, text, t0) {
  let ks = state.ctx.progs;
  for (let i = state.lines.length - 1; i >= 0; i--)
    if (state.lines[i].prog) {
      ks = state.lines[i].prog;
      break;
    }
  const v = judgePythonLine(ks, text);
  return {
    gestures: [],
    hora: 0,
    from: state.lines.length - 1,
    ...v,
    text,
    latex: null,
    ast: null,
    ms: Math.round((now() - t0) * 100) / 100,
  };
}

// ── Analyse de Terminale et de prépa (ode, integral, partial, riemann) ──────
// Les juges de l'ancien moteur, transcrits (analysis-legacy-sheets.js) ; leur état est rangé dans chaque ligne.
function createAnaSheet(engine, model) {
  const ana = createLegacyAnalysisSheet(model);
  const kernel = model.kernel;
  const ctx = {
    engine,
    table: engine.table,
    model,
    kernel,
    goal: kernel.goal,
    target: kernel.goal?.target ?? null,
    ana,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: legacyAnalysisStartText(model),
    ast: null,
    L: null,
    key: 'ana:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  return { ctx, lines: [start], done: false };
}

function judgeAnaLine(state, text, t0) {
  let ana = state.ctx.ana;
  for (let i = state.lines.length - 1; i >= 0; i--)
    if (state.lines[i].ana) {
      ana = state.lines[i].ana;
      break;
    }
  const v = judgeLegacyAnalysisLine(ana, text);
  return {
    gestures: [],
    hora: 0,
    from: state.lines.length - 1,
    ...v,
    text,
    latex: null,
    ast: null,
    ms: Math.round((now() - t0) * 100) / 100,
  };
}

// ── Systèmes, récurrences, encadrements : l'ancien moteur complet, rejoué ────
// L'état de l'ancien juge est reconstruit à chaque ligne à partir des verdicts gardés (legacy-replay-sheets.js).
function createReplaySheet(engine, model) {
  const kernel = model.kernel;
  const ctx = {
    engine,
    table: engine.table,
    model,
    kernel,
    goal: kernel.goal,
    target: kernel.goal?.target ?? null,
    replay: legacyReplaySort(model),
    path: [],
    answerKey: null,
  };
  // la forme lue des lignes ne sert pas au jugement (l'ancien moteur juge le texte) : seulement au clavier (ses
  // nombres et ses morceaux), à la recopie et à la ligne source
  const startText = legacyReplayStartText(model);
  const start = {
    index: 0,
    text: startText,
    ast: replayAst(engine, startText),
    L: null,
    key: 'replay:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  return { ctx, lines: [start], done: false };
}

function replayAst(engine, text) {
  try {
    return parse(String(text), engine.table);
  } catch {
    return null;
  }
}

function judgeReplayLine(state, text, t0) {
  let verdicts = [];
  for (let i = state.lines.length - 1; i >= 0; i--)
    if (state.lines[i].legacy) {
      verdicts = state.lines[i].legacy;
      break;
    }
  const v = judgeLegacyReplayLine(state.ctx.model, verdicts, text);
  return {
    gestures: [],
    hora: 0,
    from: state.lines.length - 1,
    ...v,
    text,
    latex: null,
    ast: replayAst(state.ctx.engine, text),
    ms: Math.round((now() - t0) * 100) / 100,
  };
}

// ── Étude de fonction : tableau de variations (plugin variations, lot I9) ─────
// Départ « f(x) = E » (dériver d'abord, par une sous-feuille d'analyse) ou « f′(x) = E » (f′ donnée). Une ligne
// « ⊞{…} » est le tableau : signes de f′ (facteurs, puis f′), puis la ligne de f ; juste, il tranche les affirmations
// (kernel.vclaims, une par choix du QCM ; `single` = élimination). L'état est rangé dans chaque ligne gardée (`vs`).
function createVarSheet(engine, model, kernel, opts) {
  const table = engine.table;
  const def = definitionOf(kernel.start);
  if (!def) throw new Error(`Départ illisible : « ${kernel.start} »`);
  const f = def.name,
    x = def.v;
  const P = (s) => {
    try {
      return parse(String(s), table);
    } catch {
      return null;
    }
  };
  const isPrimeLine = (l) => {
    const d = definitionOf(l);
    return d && d.name === f && d.primes === 1 && d.v === x;
  };
  let fExpr = null,
    fprime = null,
    givenLine = null;
  if (def.primes === 0) fExpr = P(def.expr);
  else if (def.primes === 1) fprime = P(def.expr);
  for (const g of model.givens ?? [])
    if (isPrimeLine(g)) {
      givenLine = g;
      fprime = P(definitionOf(g).expr);
    }
  let sub = null;
  if (fExpr) {
    const path = (kernel.path ?? []).filter((l) => !isSignTableText(l));
    const answer = [...path].reverse().find(isPrimeLine) ?? givenLine;
    if (answer) {
      sub = createSheet(
        engine,
        {
          symbols: {},
          givens: [],
          goal: [`${f}′(${x})`],
          traps: [],
          kernel: {
            plugin: 'analysis',
            goal: { kind: 'differentiate', target: `${f}′(${x})` },
            start: kernel.start,
            answer,
            path,
          },
        },
        opts,
      );
    }
  }
  const claims = kernel.vclaims ?? [];
  const vars = {
    f,
    x,
    fExpr,
    claims,
    single: !!kernel.single,
    vs: { sub, fprime, table: null, decisions: claims.map(() => null) },
  };
  const ctx = {
    engine,
    table: sub ? sub.ctx.table : table,
    baseTable: table,
    model,
    kernel,
    goal: kernel.goal,
    target: kernel.goal?.target ?? null,
    vars,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: kernel.start,
    ast: null,
    L: null,
    key: 'var:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  const lines = [start];
  if (givenLine)
    lines.push({ ...start, index: 1, text: givenLine, key: 'var:given', status: 'given' });
  return { ctx, lines, done: false };
}

function currentVars(state) {
  for (let i = state.lines.length - 1; i >= 0; i--) if (state.lines[i].vs) return state.lines[i].vs;
  return state.ctx.vars.vs;
}

function judgeVarLine(engine, state, text, t0) {
  const { ctx } = state;
  const V = ctx.vars;
  const vs = currentVars(state);
  const table = ctx.baseTable;
  const fin = (v) => ({
    gestures: [],
    hora: 0,
    from: state.lines.length - 1,
    text,
    latex: null,
    ast: null,
    ...v,
    ms: Math.round((now() - t0) * 100) / 100,
  });
  const { f, x } = V;
  if (isSignTableText(text)) {
    const t = parseSignTable(text);
    if (!t) {
      const msg = 'Je ne lis pas ce tableau : ouvre-le de nouveau depuis la palette.';
      return fin({
        status: 'parse_error',
        kind: null,
        title: 'Tableau illisible',
        message: msg,
        error: { pos: 0, len: 1, msg },
      });
    }
    if (!vs.fprime)
      return fin({
        status: 'wrong',
        kind: null,
        title: `Calcule d’abord ${f}′(${x})`,
        message: `Le sens de variation de ${f} se lit sur le signe de ${f}′ : dérive ${f}, puis dresse le tableau.`,
      });
    const exp = expectedVarTable(vs.fprime, x, table, V.fExpr);
    if (!exp)
      return fin({
        status: 'wrong',
        kind: null,
        title: `Factorise ${f}′(${x})`,
        message: `Pour lire le signe de ${f}′(${x}), écris-la comme un produit (ou un quotient) de facteurs dont tu connais le signe.`,
      });
    if (vs.table === text)
      return fin({
        status: 'useless',
        kind: 'kept',
        title: 'Déjà dressé',
        message: 'Ce tableau est déjà sur ta feuille : lis-le pour trancher les choix.',
      });
    const issue =
      checkSignTable(exp.sign, t, table, `${f}′(${x})`) ?? checkVarRow(exp, t, table, { f, x });
    if (issue)
      return fin({
        status: 'wrong',
        kind: null,
        title: issue.title,
        message: issue.message,
        ...(issue.row !== undefined
          ? { cell: { row: issue.row, ...(issue.col !== undefined ? { col: issue.col } : {}) } }
          : {}),
      });
    const truths = V.claims.map((c) => {
      try {
        return claimTruth(exp, c, { x, fExpr: V.fExpr, table });
      } catch {
        return null;
      }
    });
    const decisions = decisionsOf(truths, V.single);
    const decided = decisions.flatMap((d, index) =>
      d === null || vs.decisions[index] !== null ? [] : [{ index, truth: d }],
    );
    const done = decisions.every((d) => d !== null);
    return fin({
      status: done ? 'goal' : 'step',
      kind: 'step',
      gestures: [
        {
          ruleId: 'variations.table@1',
          label: 'Tableau de variations',
          why: `Le signe de ${f}′ donne le sens de variation de ${f}.`,
          hora: 3,
        },
      ],
      hora: 3,
      decided,
      message: done ? 'Tu tiens la réponse !' : 'Juste : Tableau de variations.',
      vs: { ...vs, table: text, decisions },
    });
  }
  // une ligne de calcul : dériver f (sous-feuille d'analyse)
  if (vs.sub) {
    const v = judge(engine, vs.sub, text);
    const out = { ...v, ms: Math.round((now() - t0) * 100) / 100 };
    if (!KEPT.has(v.status)) return out;
    const sub = commit(vs.sub, v);
    let fprime = vs.fprime;
    const d = definitionOf(text);
    if (d && d.name === f && d.primes === 1 && d.v === x) {
      try {
        const a = parse(d.expr, table);
        if ([...symbols(a)].every((s) => s === x)) fprime = a;
      } catch {
        /* écriture avec des noms : f′ n'est pas encore écrite seule */
      }
    }
    // dériver n'est qu'une étape : le but est de trancher les affirmations
    return {
      ...out,
      status: v.status === 'goal' ? 'step' : v.status,
      message:
        v.status === 'goal'
          ? `Juste : ${labelsOf(v.gestures) || 'Calculer'}. Dresse maintenant le tableau de variations de ${f}.`
          : v.message,
      vs: { ...vs, sub, fprime },
    };
  }
  return fin({
    status: 'useless',
    kind: 'kept',
    message: `L’énoncé donne ${f}′ : dresse le tableau de variations de ${f}.`,
  });
}

// ── Analyse en un point : tangente, primitive avec une condition (lot I11) ──────
// Tangente (`goal.kind = 'tangent'`, `at`) : dériver f (sous-feuille d'analyse), « f(a) = … », « f′(a) = … »,
// « y = f′(a) × (x − a) + f(a) » (Équation de la tangente), les valeurs remplacées (Remplacer), puis les calculs
// (sous-feuille « simplifier » partie de cette ligne). Condition (`antiderivative` + `condition` « G(0) = 3 ») :
// une primitive « G(x) = … + C » (sous-feuille), « G(0) = … » (Valeur en un point), « C = 3 » (Utiliser la
// condition), « G(x) = … + 3 » (Remplacer). Vérité en valeurs approchées (x, et C, tirés en quelques points).
const POINT_HEAD = /^\s*([A-Za-z])(′*)\(([^()]+)\)\s*$/u;
function pointGesture(engine, builtin, label) {
  const r = engine.builtins?.[builtin];
  return r
    ? { ruleId: r.id, label: label ?? r.label, why: r.why, hora: r.hora ?? 0 }
    : { ruleId: `analysis.${builtin}@1`, label: label ?? builtin, hora: 1 };
}
function createPointSheet(engine, model, kernel, opts) {
  const def = definitionOf(kernel.start);
  if (!def) throw new Error(`Départ illisible : « ${kernel.start} »`);
  const tangent = kernel.goal?.kind === 'tangent';
  const f = def.name,
    x = def.v;
  // les têtes en un point écrites dans le modèle (« f(e) », « G(0) ») se lisent comme des noms
  const texts = [kernel.start, kernel.answer, kernel.condition, ...(kernel.path ?? [])];
  const heads = new Set(allHeads(x));
  if (tangent && kernel.at != null) for (const p of ['', '′']) heads.add(`${f}${p}(${kernel.at})`);
  for (const t of texts)
    for (const m of String(t ?? '').matchAll(/([A-Za-z](?:′|')*)\(([^()\s]+)\)/gu))
      heads.add(m[0].replace(/'/g, '′'));
  engine = { ...engine, table: withIdentifiers(engine.table, [...heads]) };
  const table = engine.table;
  const P = (s) => parse(String(s), table);
  const path = kernel.path ?? [];
  let sub = null,
    target = null,
    cond = null;
  if (tangent) {
    const isPrime = (l) => {
      const d = definitionOf(l);
      return d && d.name === f && d.primes === def.primes + 1 && d.v === x;
    };
    const answer = [...path].reverse().find(isPrime);
    if (!answer) throw new Error('Tangente : aucune ligne « f′(x) = … » dans le corrigé.');
    sub = createSheet(
      engine,
      {
        symbols: {},
        givens: [],
        goal: [`${f}′(${x})`],
        traps: [],
        kernel: {
          plugin: 'analysis',
          goal: { kind: 'differentiate', target: `${f}′(${x})` },
          start: kernel.start,
          answer,
          path: path.filter(isPrime),
        },
      },
      opts,
    );
  } else {
    target = kernel.goal?.target;
    const tdef = definitionOf(`${target} = 0`);
    const cm = POINT_HEAD.exec(String(kernel.condition ?? '').split('=')[0] ?? '');
    if (!tdef || !cm) throw new Error(`Condition illisible : « ${kernel.condition} »`);
    const family = path.find((l) => {
      const d = definitionOf(l);
      return d && `${d.name}(${d.v})` === target && /\bC\b/.test(d.expr);
    });
    if (!family) throw new Error('Condition : aucune primitive « … + C » dans le corrigé.');
    sub = createSheet(
      engine,
      {
        symbols: {},
        givens: [],
        goal: [target],
        traps: [],
        kernel: {
          plugin: 'analysis',
          goal: { kind: 'antiderivative', target },
          start: kernel.start,
          answer: family,
          path: [family],
        },
      },
      opts,
    );
    cond = {
      head: `${cm[1]}${cm[2]}(${cm[3]})`,
      text: String(kernel.condition),
      at: P(cm[3]),
      value: P(String(kernel.condition).split('=').slice(1).join('=')),
      name: tdef.name,
    };
  }
  const answerAst = P(kernel.answer);
  const pt = {
    tangent,
    f,
    x,
    fExpr: def.primes === 0 ? P(def.expr) : null,
    at: tangent ? P(kernel.at) : (cond?.at ?? null),
    cond,
    target,
    answer: answerAst,
    ps: { sub, fprime: null, family: null, vals: {}, C: null, y: null },
  };
  const ctx = {
    engine,
    table,
    baseTable: table,
    model,
    kernel,
    goal: kernel.goal,
    target: tangent ? 'y' : target,
    point: pt,
    path: [],
    answerKey: null,
  };
  const start = {
    index: 0,
    text: kernel.start,
    ast: null,
    L: null,
    key: 'pt:start',
    status: 'start',
    kind: null,
    from: null,
    gestures: [],
    hora: 0,
  };
  const lines = [start];
  if (cond) lines.push({ ...start, index: 1, text: cond.text, key: 'pt:cond', status: 'given' });
  return { ctx, lines, done: false };
}

function currentPoint(state) {
  for (let i = state.lines.length - 1; i >= 0; i--) if (state.lines[i].pt) return state.lines[i].pt;
  return state.ctx.point.ps;
}

// valeur numérique d'une écriture (variable x et constante C fixées), NaN si elle ne s'évalue pas
function ptValue(ast, env, table) {
  try {
    return toF(evaluate(ast, env, table));
  } catch {
    return NaN;
  }
}
const ptClose = (a, b) =>
  Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= 1e-7 * Math.max(1, Math.abs(b));

function judgePointLine(engine, state, text, t0) {
  const { ctx } = state;
  const T = ctx.point;
  const ps = currentPoint(state);
  const table = ctx.table;
  const { f, x } = T;
  const fin = (v) => ({
    gestures: [],
    from: state.lines.length - 1,
    text,
    latex: null,
    ast: null,
    ...v,
    hora: v.hora ?? (v.gestures ?? []).reduce((s, g) => s + (g.hora || 0), 0),
    ms: Math.round((now() - t0) * 100) / 100,
  });
  for (let i = state.lines.length - 1; i > 0; i--)
    if (state.lines[i].text === text)
      return fin({ status: 'useless', kind: 'kept', from: i, message: MSG.dup });
  let ast;
  try {
    ast = parse(text, table);
  } catch (e) {
    if (!(e instanceof ParseError)) throw e;
    return fin({
      status: 'parse_error',
      kind: null,
      message: e.message,
      error: { pos: e.pos, len: e.len, msg: e.message },
    });
  }
  const eqT = Object.entries(table.nodes).find(([, sp]) => sp.relation === 'eq')?.[0];
  const isEq = ast.t === eqT && ast.k.length >= 2;
  const headName = isEq && ast.k[0].t === 'Sym' ? ast.k[0].name : null;
  const base = { text: toText(ast, table), latex: toLatex(ast, table), ast };
  const wrong = (title, message) => fin({ ...base, status: 'wrong', kind: null, title, message });
  const xs = [0.37, 1.21, 2.53, 3.9];
  const aVal = T.at ? ptValue(T.at, {}, table) : NaN;
  const Cs = [0.31, 1.73];
  // f(x) et f′(x) en valeurs (dérivée numérique centrée quand f′ n'est pas encore écrite)
  const fAt = (v) => (T.fExpr ? ptValue(T.fExpr, { [x]: v }, table) : NaN);
  const fpAt = (v) =>
    ps.fprime ? ptValue(ps.fprime, { [x]: v }, table) : (fAt(v + 1e-5) - fAt(v - 1e-5)) / 2e-5;
  const famAt = (v, c) => (ps.family ? ptValue(ps.family, { [x]: v, C: c }, table) : NaN);

  // 1. une ligne « f′(x) = … », « G(x) = … + C », « u(x) = … » : la sous-feuille
  const d = definitionOf(text);
  const isFinal = !T.tangent && d && `${d.name}(${d.v})` === T.target && !/\bC\b/.test(d.expr);
  if (d && d.v === x && !isFinal && !(T.tangent && d.name === 'y')) {
    const v = judge(engine, ps.sub, text);
    const out = { ...v, ms: Math.round((now() - t0) * 100) / 100 };
    if (!KEPT.has(v.status)) return out;
    const sub = commit(ps.sub, v);
    const next = { ...ps, sub };
    try {
      const e = parse(d.expr, table);
      const free = [...symbols(e)].filter((s) => !table.syntax.constants?.[s]);
      if (T.tangent && d.name === f && d.primes === 1 && free.every((s) => s === x))
        next.fprime = e;
      if (!T.tangent && `${d.name}(${d.v})` === T.target && free.every((s) => s === x || s === 'C'))
        next.family = e;
    } catch {
      /* écriture avec des noms */
    }
    return {
      ...out,
      status: v.status === 'goal' ? 'step' : v.status,
      message: v.status === 'goal' ? `Juste : ${labelsOf(v.gestures) || 'Calculer'}.` : v.message,
      pt: next,
    };
  }
  if (!isEq) return wrong('Écris une égalité', 'Écris une ligne « … = … ».');
  const members = ast.k.slice(1);

  // 2. une valeur en un point : « f(e) = ln(e) = 1 », « G(0) = 0² − 0 + C = C »
  const pm = headName ? POINT_HEAD.exec(headName) : null;
  if (pm && pm[3] !== x) {
    const name = pm[1],
      primes = pm[2].length;
    let atAst;
    try {
      atAst = parse(pm[3], table);
    } catch {
      return wrong('Point illisible', `Je ne lis pas le point de ${headName}.`);
    }
    const a = ptValue(atAst, {}, table);
    let ref = null; // valeur attendue, fonction de C
    if (T.tangent && name === f && primes === 0) ref = () => fAt(a);
    else if (T.tangent && name === f && primes === 1) {
      if (!ps.fprime)
        return fin({
          ...base,
          status: 'unjustified',
          kind: 'kept',
          title: `Calcule d’abord ${f}′(${x})`,
          message: `Pour calculer ${headName}, écris d’abord ${f}′(${x}).`,
        });
      ref = () => fpAt(a);
    } else if (!T.tangent && `${name}(${x})` === T.target && primes === 0) {
      if (!ps.family)
        return wrong(
          'La primitive d’abord',
          `Écris d’abord une primitive ${T.target} = … + C, puis calcule ${headName}.`,
        );
      ref = (c) => famAt(a, c);
    } else return wrong('Pas de cette fonction', `${headName} ne sert pas ici.`);
    for (const m of members) {
      const ok = Cs.every((c) => ptClose(ptValue(m, { C: c }, table), ref(c)));
      if (!ok)
        return wrong(
          'Pas la bonne valeur',
          `Remplace ${x} par ${pm[3]} dans ${f === name ? f : name}, puis calcule.`,
        );
    }
    const vals = { ...ps.vals, [headName]: members.at(-1) };
    return fin({
      ...base,
      status: 'step',
      kind: 'step',
      gestures: [pointGesture(engine, 'point_value', `Calculer ${headName}`)],
      message: `Juste : Calculer ${headName}.`,
      pt: { ...ps, vals },
    });
  }

  // 3. la constante : « C = 3 », « C = 5 − 1 = 4 »
  if (!T.tangent && headName === 'C') {
    const vC = T.cond && ps.family ? solveC() : null;
    function solveC() {
      const a = ptValue(T.cond.at, {}, table);
      const e0 = famAt(a, 0),
        e1 = famAt(a, 1);
      const k = ptValue(T.cond.value, {}, table);
      return Math.abs(e1 - e0) > 1e-12 ? (k - e0) / (e1 - e0) : NaN;
    }
    if (!Number.isFinite(vC))
      return wrong('La primitive d’abord', `Écris d’abord une primitive ${T.target} = … + C.`);
    for (const m of members)
      if (!ptClose(ptValue(m, {}, table), vC))
        return wrong(
          'Pas la bonne constante',
          `Utilise ${T.cond.text} : calcule ${T.cond.head} avec C, puis résous.`,
        );
    const known = ps.vals[T.cond.head];
    if (!known)
      return fin({
        ...base,
        status: 'unjustified',
        kind: 'kept',
        message: `Juste, mais d’où vient-elle ? Calcule d’abord ${T.cond.head}.`,
        pt: { ...ps, C: members.at(-1) },
      });
    return fin({
      ...base,
      status: 'step',
      kind: 'step',
      gestures: [pointGesture(engine, 'use_condition', `Utiliser ${T.cond.text}`)],
      message: `Juste : Utiliser ${T.cond.text}.`,
      pt: { ...ps, C: members.at(-1) },
    });
  }

  // 4. la réponse d'une primitive avec condition : « G(x) = x² − x + 3 »
  if (isFinal) {
    const R = members.at(-1);
    const vC = ps.C ? ptValue(ps.C, {}, table) : NaN;
    const ansR = T.answer.k?.[1];
    const ok = xs.every((v) =>
      ptClose(ptValue(R, { [x]: v }, table), ptValue(ansR, { [x]: v }, table)),
    );
    if (!ok) return wrong('Ce n’est pas juste', `Vérifie ta primitive et la valeur de C.`);
    if (!Number.isFinite(vC) || !ps.family)
      return fin({
        ...base,
        status: 'unjustified',
        kind: 'kept',
        message: 'C’est la réponse, mais d’où vient-elle ? Trouve d’abord C avec la condition.',
      });
    return fin({
      ...base,
      status: 'goal',
      kind: 'step',
      gestures: [pointGesture(engine, 'substitute', 'Remplacer C')],
      message: MSG.goal,
    });
  }

  // 5. la tangente : « y = f′(a) × (x − a) + f(a) », puis les valeurs, puis les calculs
  if (T.tangent && headName === 'y') {
    if (ps.y) {
      // après le remplacement : une sous-feuille « simplifier »
      const v = judge(engine, ps.y, text);
      const out = { ...v, ms: Math.round((now() - t0) * 100) / 100 };
      if (!KEPT.has(v.status)) return out;
      return { ...out, pt: { ...ps, y: commit(ps.y, v) } };
    }
    const fa = fAt(aVal),
      fpa = fpAt(aVal);
    const R = members.at(-1);
    const nameVals = {};
    for (const s of symbols(R)) {
      const m = POINT_HEAD.exec(s);
      if (!m || m[1] !== f) continue;
      const a = ptValue(parse(m[3], table), {}, table);
      if (!ptClose(a, aVal))
        return wrong('Pas le bon point', `La tangente est prise en ${ctx.kernel.at}.`);
      nameVals[s] = m[2].length ? fpa : fa;
    }
    const tangentOk = xs.every((v) =>
      ptClose(ptValue(R, { ...nameVals, [x]: v }, table), fpa * (v - aVal) + fa),
    );
    if (!tangentOk)
      return wrong(
        'Ce n’est pas la tangente',
        `La tangente en ${ctx.kernel.at} a pour équation y = ${f}′(a) × (x − a) + ${f}(a), avec a = ${ctx.kernel.at}.`,
      );
    const hasNames = Object.keys(nameVals).length > 0;
    const known = (n) => !!ps.vals[n];
    const fa0 = `${f}(${ctx.kernel.at})`,
      fpa0 = `${f}′(${ctx.kernel.at})`;
    const gestures = [];
    let fromTemplate = false;
    for (let i = state.lines.length - 1; i > 0; i--)
      if (state.lines[i].ptTemplate) fromTemplate = true;
    if (hasNames) gestures.push(pointGesture(engine, 'tangent_equation'));
    else {
      if (!fromTemplate) gestures.push(pointGesture(engine, 'tangent_equation'));
      if (!known(fa0) || !known(fpa0))
        return fin({
          ...base,
          status: 'unjustified',
          kind: 'kept',
          message: `Juste, mais d’où viennent ces nombres ? Calcule d’abord ${fpa0} et ${fa0}.`,
        });
      gestures.push(pointGesture(engine, 'substitute'));
    }
    const next = { ...ps };
    if (!hasNames) {
      next.y = createSheet(
        engine,
        {
          symbols: {},
          givens: [],
          goal: ['y'],
          traps: [],
          kernel: {
            plugin: 'algebra',
            goal: { kind: 'simplify', target: 'y' },
            start: text,
            answer: ctx.kernel.answer,
            path: [],
          },
        },
        { level: ps.sub?.ctx?.level },
      );
    }
    const goal = !hasNames && keyOf(light(ast, table)) === keyOf(light(T.answer, table));
    return fin({
      ...base,
      status: goal ? 'goal' : 'step',
      kind: 'step',
      gestures,
      message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
      pt: next,
      ptTemplate: hasNames,
    });
  }
  return wrong('Ce n’est pas juste', MSG.wrong.default);
}

// ── Tableau de signes d'une inéquation (lot I9) ─────────────────────────────
// La dernière ligne gardée compare une expression à 0 ; le tableau « ⊞{…} » (facteurs, puis l'expression) se
// vérifie case par case. Juste, la ligne garde la relation étudiée (la conclusion se lit dessus).
function judgeSolveSignTable(engine, state, text, done) {
  const { ctx } = state;
  const table = ctx.table;
  const x = ctx.target;
  const lastKept = state.lines.length - 1;
  const base = { text, latex: null, ast: null, from: lastKept };
  const t = parseSignTable(text);
  if (!t) {
    const msg = 'Je ne lis pas ce tableau : ouvre-le de nouveau depuis la palette.';
    return done({
      ...base,
      status: 'parse_error',
      kind: null,
      title: 'Tableau illisible',
      message: msg,
      error: { pos: 0, len: 1, msg },
    });
  }
  if (state.lines.some((l) => l.signTable === text))
    return done({
      ...base,
      status: 'useless',
      kind: 'kept',
      title: 'Déjà dressé',
      message: 'Ce tableau est déjà sur ta feuille : lis-le pour conclure.',
    });
  const rel = state.lines[lastKept]?.ast;
  const ev = rel ? table.nodes[rel.t]?.eval : null;
  const isZero = (n) => n?.t === 'Num' && n.p === 0;
  let subject = null;
  if (['lt', 'le', 'gt', 'ge'].includes(ev) && rel.k.length === 2) {
    if (isZero(rel.k[1])) subject = rel.k[0];
    else if (isZero(rel.k[0])) subject = rel.k[1];
  }
  if (!subject)
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      title: 'Compare à 0 d’abord',
      message:
        'Un tableau de signes étudie le signe d’une expression : écris d’abord ta ligne sous la forme « … > 0 » (ou < 0, ≥ 0, ≤ 0).',
    });
  const exp = expectedSignTable(subject, x, table);
  if (!exp)
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      title: 'Factorise d’abord',
      message:
        'Pour lire le signe, écris d’abord ta ligne comme un produit (ou un quotient) de facteurs dont tu connais le signe.',
    });
  const issue = checkSignTable(exp, t, table, null);
  if (issue)
    return done({
      ...base,
      status: 'wrong',
      kind: null,
      title: issue.title,
      message: issue.message,
      ...(issue.row !== undefined
        ? { cell: { row: issue.row, ...(issue.col !== undefined ? { col: issue.col } : {}) } }
        : {}),
    });
  const rule = engine.builtins?.sign_table_fill;
  const gestures = rule ? [gestureOf({ rule, ast: rel }, ctx)] : [];
  return done({
    ...base,
    ast: rel,
    status: 'step',
    kind: 'step',
    gestures,
    hora: gestures.reduce((a, g) => a + (g.hora ?? 0), 0),
    message: 'Juste : Tableau de signes. Lis maintenant les solutions sur sa dernière ligne.',
    signTable: text,
  });
}

function visitTree(n, f) {
  f(n);
  if (!isLeaf(n)) for (const c of n.k) visitTree(c, f);
}

/** Décision de chaque affirmation d'une feuille « vérifier chaque affirmation » (true / false / null), ou null. */
export function sheetClaimDecisions(state) {
  const { ctx } = state;
  if (ctx.tabs) return tableDecisions(currentTab(state));
  if (ctx.adv) return advDecisions(currentAdv(state));
  if (ctx.vars) return currentVars(state).decisions;
  // affirmations de limites : une limite établie (« x³ ÷ eˣ → 0 ») tranche l'affirmation sur cette expression
  if (ctx.limit?.claims) {
    const S = limitState(state, ctx.table);
    const truths = ctx.limit.claims.map((c) =>
      c && S.known.has(c.key) ? sameLimit(S.known.get(c.key), c.value) : null,
    );
    return decisionsOf(truths, ctx.limit.single);
  }
  return ctx.claims ? claimDecisions(ctx.claims, state.lines, ctx.table, ctx.single) : null;
}

export function commit(state, verdict) {
  if (!verdict || !KEPT.has(verdict.status)) return state;
  const table = state.ctx.table;
  // une ligne peut servir de départ sous une autre écriture (feuille d'analyse : « f(x) = R » → « f′(x) = (R)′ »)
  const astC = verdict.searchAst ?? verdict.ast;
  const L = astC ? light(astC, table) : null;
  const line = {
    index: state.lines.length,
    text: verdict.text,
    ast: verdict.ast,
    L,
    key: L ? keyOf(L) : `text:${verdict.text}`,
    status: verdict.status,
    kind: verdict.kind,
    from: verdict.from,
    gestures: verdict.gestures,
    hora: verdict.hora,
    ...(verdict.part ? { part: verdict.part } : {}),
    ...(verdict.limit ? { limit: verdict.limit } : {}),
    ...(verdict.ints ? { ints: verdict.ints } : {}),
    ...(verdict.cx ? { cx: verdict.cx } : {}),
    ...(verdict.geo ? { geo: verdict.geo } : {}),
    ...(verdict.rank ? { rank: verdict.rank } : {}),
    ...(verdict.quad ? { quad: verdict.quad } : {}),
    ...(verdict.cond ? { cond: true } : {}),
    ...(verdict.tab ? { tab: verdict.tab } : {}),
    ...(verdict.adv ? { adv: verdict.adv } : {}),
    ...(verdict.mat ? { mat: verdict.mat } : {}),
    ...(verdict.prog ? { prog: verdict.prog } : {}),
    ...(verdict.ana ? { ana: verdict.ana } : {}),
    ...(verdict.legacy ? { legacy: verdict.legacy } : {}),
    ...(verdict.vs ? { vs: verdict.vs } : {}),
    ...(verdict.signTable ? { signTable: verdict.signTable } : {}),
    ...(verdict.pt ? { pt: verdict.pt } : {}),
    ...(verdict.ptTemplate ? { ptTemplate: true } : {}),
  };
  return { ...state, lines: [...state.lines, line], done: state.done || verdict.status === 'goal' };
}

// « Calculer, puis Calculer » → « Calculer (×2) »
function labelsOf(gestures) {
  const out = [];
  for (const g of gestures) {
    const last = out.at(-1);
    if (last && last.label === g.label) last.n++;
    else out.push({ label: g.label, n: 1 });
  }
  return out.map((x) => (x.n > 1 ? `${x.label} (×${x.n})` : x.label)).join(', puis ');
}

// Étape active (CONTRACT §8) : indice dans kernel.path de la prochaine ligne du corrigé à atteindre,
// après la plus avancée déjà rejointe (forme légère) ; expected = son texte ; done = but atteint.
// Une relation lue dans l'autre sens est la même ligne pour le corrigé : « v − v₀ = at » rejoint « at = v − v₀ »
// (a = b ↔ b = a, a < b ↔ b > a…). Les rôles viennent de la table (eval), pas de noms de nœuds en dur.
const MIRROR = { eq: 'eq', ne: 'ne', lt: 'gt', gt: 'lt', le: 'ge', ge: 'le' };
function formKeys(L, table) {
  const keys = [keyOf(L)];
  const ev = table.nodes[L?.t]?.eval;
  if (MIRROR[ev] && L.k?.length === 2) {
    const R = roles(table);
    keys.push(keyOf({ ...L, t: R[MIRROR[ev]] ?? L.t, k: [L.k[1], L.k[0]] }));
  }
  return keys;
}
export function progress(engine, state) {
  engine = state?.ctx?.engine ?? engine;
  const { ctx } = state;
  const keys = new Set(state.lines.flatMap((l) => (l.L ? formKeys(l.L, engine.table) : [l.key])));
  let i = -1;
  ctx.path.forEach((p, j) => {
    if (keys.has(p.key)) i = j;
  });
  const done = !!state.done || (ctx.answerKey != null && keys.has(ctx.answerKey));
  const step = done ? ctx.path.length : i + 1;
  return { step, expected: done ? null : (ctx.path[step]?.text ?? null), done };
}

// ── Pour l'app (lot V3) : ce que la feuille établit, pour cocher les choix du QCM ──────────────────
// Valeurs écrites des grandeurs (« x = 3,5 », « T ≈ 0,22 », « 16 = n ») : nom → valeurs numériques.
export function sheetStatedValues(state) {
  const table = state.ctx.table;
  const out = {};
  for (const l of state.lines) {
    if (!l.L) continue;
    for (const a of atomsOf(l.L, table)) {
      for (const [s, e] of [
        [a.l, a.r],
        [a.r, a.l],
      ]) {
        if (s?.t !== 'Sym' || !e) continue;
        let v;
        try {
          v = evaluate(e, {}, table);
        } catch {
          v = undefined;
        }
        const x = v == null ? NaN : typeof v === 'number' ? v : toF(v);
        if (Number.isFinite(x)) (out[s.name] ??= []).push(x);
      }
    }
  }
  // feuille « reste » (congruences) : la grandeur cherchée (« reste ») vaut le reste de la réponse, une fois la
  // réponse écrite — un choix « 4 » du QCM se coche quand « 3¹⁰⁰ ≡ 4 [7] » est établi
  const goal = state.ctx.model?.goal;
  const ans = /≡\s*(−?-?\d+)\s*\[\s*\d+\s*\]\s*$/.exec(String(state.ctx.kernel?.answer ?? ''));
  if (
    state.ctx.kernel?.plugin === 'congruence' &&
    state.ctx.goal?.kind === 'residue' &&
    Array.isArray(goal) &&
    goal.length === 1 &&
    ans &&
    (state.done || state.lines.some((l) => l.status === 'goal'))
  )
    (out[goal[0]] ??= []).push(Number(ans[1].replace('−', '-')));
  // feuille « grandeurs et unités » (lot M5) : la grandeur cherchée (« R ») vaut la réponse écrite, exprimée
  // dans l'unité des propositions (`units.valueUnit`, sinon la dernière unité de `units.unit`) — un choix
  // « 5 h 15 min » (315) se coche quand « … = 5 h 15 min » est la réponse, comme avec l'ancien moteur
  const qv = quantityGoalValue(state);
  if (qv != null && Array.isArray(goal) && goal.length === 1) (out[goal[0]] ??= []).push(qv);
  // feuille « nombres complexes » qui calcule une écriture (« (√3 + i)^12 = 4096 ») : la grandeur cherchée (« z »,
  // absente des lignes) vaut le dernier membre de la réponse quand il est réel, une fois la réponse établie
  if (
    state.ctx.kernel?.plugin === 'complex' &&
    Array.isArray(goal) &&
    goal.length === 1 &&
    !(goal[0] in out) &&
    (state.done || state.lines.some((l) => l.status === 'goal'))
  ) {
    const last = String(state.ctx.kernel?.answer ?? '')
      .split('=')
      .at(-1)
      ?.trim();
    const m = last && /^−?-?\d+(?:,\d+)?$/.exec(last);
    if (m)
      (out[goal[0]] ??= []).push(
        Number(last.replace('−', '-').replace('-', '-').replace(',', '.')),
      );
  }
  return out;
}

function quantityGoalValue(state) {
  const { ctx } = state;
  const units = ctx.quantities?.units;
  const spec = ctx.model?.units;
  if (!units || !spec) return null;
  const goalLine = [...state.lines].reverse().find((l) => l.status === 'goal' && l.ast);
  if (!goalLine) return null;
  const table = ctx.table;
  const members = isRelation(goalLine.ast, table) ? goalLine.ast.k : [goalLine.ast];
  const q = quantityOf(members.at(-1), units, table);
  const unitName = String(
    spec.valueUnit ??
      String(spec.unit ?? '')
        .trim()
        .split(/\s+/)
        .at(-1) ??
      '',
  );
  let u = null;
  try {
    u = quantityOf(parse(groupJuxtaposed(unitName, units), table), units, table);
  } catch {
    u = null;
  }
  if (!q || q.mismatch || !u || u.mismatch || !u.v) return null;
  const v = (typeof q.v === 'number' ? q.v : toF(q.v)) / (typeof u.v === 'number' ? u.v : toF(u.v));
  return Number.isFinite(v) ? Math.round(v * 1e9) / 1e9 : null;
}

// Une ligne (un choix du QCM écrit en ligne) est-elle écrite sur la feuille ET une réponse finale ?
export function sheetHasAnswer(engine, state, text) {
  engine = state?.ctx?.engine ?? engine;
  const table = engine.table;
  const { ctx } = state;
  // programme Python : l'état de la trace de la dernière ligne gardée
  if (ctx.progs) {
    const last = [...state.lines].reverse().find((l) => l.prog);
    return pythonHasAnswer(last?.prog ?? ctx.progs, text);
  }
  // équations différentielles, intégrales, dérivées partielles, sommes de Riemann : l'ancien juge
  if (ctx.ana) {
    const last = [...state.lines].reverse().find((l) => l.ana);
    return legacyAnalysisHasAnswer(last?.ana ?? ctx.ana, text);
  }
  let L;
  try {
    L = light(parse(String(text), table), table);
  } catch {
    return false;
  }
  const key = keyOf(L);
  // développement limité : la ligne gardée qui écrit ce DL (le reste « o(…) » est relu à part)
  if (ctx.limit?.answer?.op === 'dl' && /\bo\(/.test(String(text))) {
    try {
      const k = keyOf(light(parse(extractSmallO(String(text)).text, table), table));
      return (
        k === ctx.limit.answer.key &&
        state.lines.some((l) => KEPT.has(l.status) && l.limit?.dl === k)
      );
    } catch {
      return false;
    }
  }
  let written = state.lines.some((l) => l.L && formKeys(l.L, table).includes(key));
  // au bout d'une chaîne : « (1 − i)⁶ = … = 8i » établit « (1 − i)⁶ = 8i » (les feuilles de complexes, de
  // congruences… ne gardent pas la chaîne dans l'arbre de la ligne : on relit son texte)
  if (!written && table.nodes[L.t]?.relation === 'eq' && L.k.length === 2) {
    const [a, b] = L.k.map(keyOf);
    const memberKey = (t) => {
      try {
        return keyOf(light(parse(t, table), table));
      } catch {
        return null;
      }
    };
    written = state.lines.some((l) => {
      if (!KEPT.has(l.status) || typeof l.text !== 'string' || !l.text.includes('=')) return false;
      const parts = l.text.split(/=(?![^(]*\))/).map((x) => x.trim());
      if (parts.length < 3) return false;
      return memberKey(parts[0]) === a && memberKey(parts[parts.length - 1]) === b;
    });
    if (written) return true;
  }
  if (!written) return false;
  try {
    return key === ctx.answerKey || isGoal(L, key, ctx);
  } catch {
    return false;
  }
}

// Étapes sautées : pour une ligne juste mais non justifiée (« d'où ça vient ? »), les lignes du corrigé qui
// mènent de la dernière étape rejointe à la ligne de l'élève, chacune avec le geste qui la justifie.
// `state` est la feuille AVANT d'y ajouter la ligne. Chaque étape est rejugée pour obtenir ses gestes ;
// `bridges` dit si, une fois ces étapes écrites, la ligne de l'élève devient justifiée.
export function skippedSteps(engine, state, lineText) {
  engine = state?.ctx?.engine ?? engine;
  const { ctx } = state;
  const table = engine.table;
  let ast;
  try {
    ast = parse(lineText, table);
  } catch {
    return { steps: [], bridges: false };
  }
  const Lx = light(ast, table),
    key = keyOf(Lx);
  const ok = (v) => v.status === 'step' || v.status === 'goal';
  const gOut = (g) => ({
    ruleId: g.ruleId,
    label: g.label,
    why: g.why,
    ...(g.implicit ? { implicit: true } : {}),
  });

  // 1. la sous-suite minimale du corrigé, SI elle mène à la ligne de l'élève
  let from = progress(engine, state).step;
  if (from < ctx.path.length) {
    // la ligne de l'élève est une ligne du corrigé : on s'arrête avant elle ; sinon on peut aller au bout
    const mine = new Set(formKeys(Lx, table));
    const at = ctx.path.findIndex((p, j) => j >= from && mine.has(p.key));
    const limit = at >= 0 ? at : ctx.path.length;
    const steps = [];
    let st = state;
    // à chaque tour, la ligne du corrigé la plus avancée que l'élève peut justifier à son niveau
    while (!ok(judge(engine, st, lineText)) && from < limit) {
      let pick = null;
      for (let j = limit - 1; j >= from; j--) {
        const v = judge(engine, st, ctx.path[j].text);
        if (ok(v) || j === from) {
          pick = { j, v };
          break;
        }
      }
      const p = ctx.path[pick.j];
      if (!KEPT.has(pick.v.status)) break;
      st = commit(st, pick.v);
      // ligne du corrigé elle-même trop grosse pour ce niveau : ses gestes, cherchés sans limite de niveau
      let gs = pick.v.gestures;
      if (!gs.length && pick.v.status === 'unjustified') {
        const prev = {
          ...state,
          lines: st.lines.slice(0, -1),
          ctx: { ...ctx, maxGestures: LIMITS.maxDepth },
        };
        const res = search(engine, prev, p.L, p.key, {});
        if (res) gs = res.chain.map((c) => gestureOf(c, ctx));
      }
      steps.push({
        text: p.text,
        latex: toLatex(p.ast, table),
        ast: p.ast,
        gestures: gs.map(gOut),
      });
      from = pick.j + 1;
    }
    if (steps.length && ok(judge(engine, st, lineText))) return { steps, bridges: true };
  }

  // 2. sinon, les lignes intermédiaires de SA propre chaîne (sans limite de niveau), découpées en paquets
  //    autorisés à son niveau (règles distinctes non implicites ≤ maxGestures)
  const free = { ...state, ctx: { ...ctx, maxGestures: LIMITS.maxDepth } };
  const res = search(engine, free, Lx, key, {});
  if (!res || res.chain.length < 2) return { steps: [], bridges: false };
  const steps = [];
  let used = new Set(),
    pack = [];
  res.chain.forEach((c, i) => {
    const counts = !isImplicit(c.rule, ctx) && !used.has(c.rule);
    if (counts && used.size >= ctx.maxGestures && pack.length) {
      const prev = res.chain[i - 1].ast;
      steps.push({
        text: toText(prev, table),
        latex: toLatex(prev, table),
        ast: prev,
        gestures: pack.map((x) => gOut(gestureOf(x, ctx))),
      });
      used = new Set();
      pack = [];
    }
    if (!isImplicit(c.rule, ctx)) used.add(c.rule);
    pack.push(c);
  });
  if (!steps.length) return { steps: [], bridges: false };
  // vérification : une fois ces lignes écrites, la sienne est justifiée
  let st = state;
  for (const x of steps) {
    const v = judge(engine, st, x.text);
    if (!KEPT.has(v.status)) return { steps: [], bridges: false };
    st = commit(st, v);
  }
  return ok(judge(engine, st, lineText)) ? { steps, bridges: true } : { steps: [], bridges: false };
}

// ── Outils et clavier (CONTRACT §9.3, §9.4) ──────────────────────────────
export { toolsFor, PROBES, probe };
export const neutralPair = neutralPairFor;

// Outil « Remplacer » sur une feuille « formule » : la lettre touchée est une donnée de l'énoncé (symbols[].given) →
// les valeurs de TOUTES les données, écrites comme l'énoncé (« 4 », « 0,3 ») ; l'élève choisit celle de sa lettre.
// Une lettre qui n'est pas une donnée (l'inconnue, P) : aucun choix.
export function substituteChoices(engine, state, name) {
  const model = state?.ctx?.model;
  const syms = model?.symbols || {};
  if (!syms[name]?.given) return [];
  const texts = [];
  for (const sym of Object.values(syms)) {
    if (!sym?.given) continue;
    const t =
      sym.value != null
        ? String(sym.value)
        : Array.isArray(sym.exact) && sym.exact.length
          ? String(sym.exact[0]).replace(/\s*÷\s*/g, '/')
          : null;
    if (t != null && !texts.includes(t)) texts.push(t);
  }
  const num = (t) => {
    const [a, b] = t.replace(',', '.').replace('−', '-').split('/');
    return b ? +a / +b : +a;
  };
  return texts.sort((a, b) => num(a) - num(b));
}

// Outil « Calculer » de l'éditeur : la valeur exacte d'un morceau de ligne choisi par l'élève (« 2 × 5 » → 10).
// Le calcul est fait ici, jamais dans l'interface. Pas de lettres, résultat rationnel exact, sinon un refus expliqué.
export function calculate(engine, exprText) {
  let ast;
  try {
    ast = parse(exprText, engine.table);
  } catch (e) {
    return { ok: false, message: 'Sélectionne un calcul complet (par exemple 2 × 5).' };
  }
  if (isRelation(ast, engine.table))
    return { ok: false, message: 'Sélectionne un calcul, pas une égalité entière.' };
  const letters = [...symbols(ast)].filter((s) => engine.table.syntax.constants[s] == null);
  if (letters.length)
    return {
      ok: false,
      message: `Ce calcul contient ${letters.length > 1 ? 'des lettres' : 'une lettre'} (${letters.join(', ')}) : il ne se réduit pas à un nombre.`,
    };
  let v = null;
  try {
    v = exactValue(ast, engine.table);
  } catch {
    v = null;
  }
  if (!v) return { ok: false, message: 'Ce calcul ne tombe pas juste : garde-le écrit tel quel.' };
  if (v.q === 0) return { ok: false, message: 'Division par zéro : ce calcul n’a pas de valeur.' };
  const res = num(v.p, v.q);
  return { ok: true, ast: res, text: toText(res, engine.table), same: isLeaf(ast) };
}
export function toolChoices(engine, state, toolKey, lineText, targetPath) {
  engine = state?.ctx?.engine ?? engine;
  const tool = (engine.tools || []).find((t) => t.key === toolKey || t.id === toolKey);
  if (!tool) return null;
  // sans feuille de lignes (case de tableau) : pas d'étape attendue, les choix ne viennent que du calcul
  return toolChoicesFor(
    engine,
    tool,
    lineText,
    targetPath,
    state ? progress(engine, state).expected : null,
  );
}
// opts.source : AST (ou texte) de la ligne dont on veut les bouts — départ, ligne gardée ou ligne 🛑 ; défaut : la dernière
export function palette(engine, state, opts = {}) {
  engine = state?.ctx?.engine ?? engine;
  const pr = progress(engine, state);
  const last = state.lines[state.lines.length - 1];
  let source = opts.source ?? null;
  const P = (x) => {
    if (typeof x !== 'string') return x;
    try {
      return parse(x, engine.table);
    } catch {
      return null;
    }
  };
  source = Array.isArray(source) ? source.map(P).filter(Boolean) : P(source);
  return paletteFor(
    engine,
    state.ctx.model,
    pr.done ? null : pr.expected,
    last?.ast ?? null,
    state.lines[0]?.ast ?? null,
    state.lines.map((l) => l.ast).filter(Boolean),
    state.ctx.path.map((p) => p.ast),
    source,
  );
}

// Bouts d'une ligne d'énoncé (formules et nombres du texte), sans feuille de lignes : pour les exercices « bloc ».
export function bitsOf(engine, texts) {
  const table = engine.table,
    asts = [];
  for (const t of texts || []) {
    try {
      asts.push(parse(String(t), table));
    } catch {
      /* texte non mathématique */
    }
  }
  const nums = new Set();
  for (const a of asts) numAtomsOf(a, table).nums.forEach((n) => nums.add(n));
  return { numbers: [...nums], chunks: chunksOf(engine, asts, 8) };
}

// Prochaine ligne du corrigé après la plus avancée déjà écrite, et le « pourquoi » du geste.
export function hint(engine, state) {
  engine = state?.ctx?.engine ?? engine;
  if (state.done) return null;
  const { ctx } = state;
  // diviseurs d'un entier : la première ligne du corrigé qui montre une paire pas encore écrite (l'ordre des
  // paires est libre, la position dans le corrigé ne dit rien)
  const divNs = ctx.claims ? divisorNumbers(ctx.claims) : [];
  if (divNs.length) {
    const have = divisorPairKeys(divNs, state.lines, engine.table);
    const p = ctx.path.find((l) =>
      l.ast
        ? [...divisorPairKeys(divNs, [{ ast: l.ast, status: 'step' }], engine.table)].some(
            (k) => !have.has(k),
          )
        : false,
    );
    if (p)
      return {
        line: p.text,
        why: 'Les diviseurs vont par paires : a × b = n. Essaie a = 1, 2, 3… jusqu’à ce que les paires se répètent.',
        gestures: [],
      };
  }
  const next = ctx.path[progress(engine, state).step];
  if (!next) return null;
  const res = search(engine, state, next.L, next.key, {});
  const gestures = res ? res.chain.map((c) => gestureOf(c, ctx)) : [];
  const why =
    gestures
      .map((g) => g.why)
      .filter(Boolean)
      .join(' ') || 'Écris la ligne suivante, une étape à la fois.';
  return { line: next.text, why, gestures };
}

// ── Vérité ────────────────────────────────────────────────────────────────
function isTrue(ast, key, ctx, where = []) {
  const ck = where.length ? `${key}|${where.map(keyOf).join('&')}` : key;
  if (ctx.truth.has(ck)) return ctx.truth.get(ck);
  const { table, target, hyps, fixed } = ctx;
  const W = (n) => (ctx.degrees && n ? degreeWorld(n, ctx) : n);
  const answer = W(ctx.answer);
  let r =
    !!ctx.ref &&
    equivRelation(W(ctx.ref), W(ast), {
      table,
      target,
      hyps,
      fixed,
      answer,
      ...(where.length ? { where } : {}),
    });
  // feuille « formule » : une égalité dont toutes les lettres ont une valeur dans le monde de l'exercice
  if (!r && ctx.goal.kind === 'evaluate' && worldDecides(ast, ctx))
    r = holds(W(ast), table, { fixed: ctx.world, hyps });
  ctx.truth.set(ck, r);
  return r;
}

// « Â = arccos(1 ÷ 2) » depuis « cos(Â) = 1 ÷ 2 » (Utiliser arccos), puis « Â = 60 » (Valeur exacte)
const INVERSE = { cos: 'arccos', sin: 'arcsin', tan: 'arctan' };
function angleGesture(engine, state, Lx, key, ctx) {
  if (!ctx.degrees) return null;
  const table = ctx.table,
    x = ctx.target,
    B = engine.builtins ?? {};
  const fn = (m) =>
    table.nodes[m.t]?.eval === 'call' ? table.syntax.functions[m.name]?.eval : null;
  const sp = table.nodes[Lx.t];
  if (sp?.relation !== 'eq' || Lx.k.length !== 2) return null;
  const [a, b] = Lx.k[0].t === 'Sym' && Lx.k[0].name === x ? Lx.k : [Lx.k[1], Lx.k[0]];
  if (!(a.t === 'Sym' && a.name === x) || symbols(b).has(x)) return null;
  const inv = fn(b);
  let rule = null,
    from = -1;
  for (let i = state.lines.length - 1; i >= 0 && !rule; i--) {
    const l = state.lines[i];
    if (l.kind === 'kept') continue;
    const calls = trigCalls(l.L, table).map((c) => table.syntax.functions[c.name].eval);
    if (inv && Object.values(INVERSE).includes(inv) && calls.some((f) => INVERSE[f] === inv))
      rule = B[`angle_${inv}`];
    else if (!inv && [...symbols(b)].every((v) => table.syntax.constants?.[v])) {
      const prev = l.L.k?.find((m) => Object.values(INVERSE).includes(fn(m) ?? ''));
      if (table.nodes[l.L.t]?.relation === 'eq' && prev) rule = B.angle_value;
    }
    if (rule) from = i;
  }
  if (!rule) return null;
  const goal = isGoal(Lx, key, ctx);
  const gestures = [gestureOf({ rule, ast: Lx }, ctx)];
  return {
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from,
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
  };
}

// Monde des degrés : l'angle cherché se mesure en degrés — cos(Â) se lit cos(Â°), arccos(c) se lit en degrés
function degreeWorld(n, ctx) {
  const table = ctx.table,
    x = ctx.target;
  const fn = (m) =>
    table.nodes[m.t]?.eval === 'call' ? table.syntax.functions[m.name]?.eval : null;
  const nodeOf = (ev) => Object.keys(table.nodes).find((k) => table.nodes[k].eval === ev);
  const toDeg = (m) => {
    if (m.t === 'Sym' && m.name === x) return { t: nodeOf('deg'), k: [m] };
    return isLeaf(m) ? m : { ...m, k: m.k.map(toDeg) };
  };
  const walk = (m) => {
    if (isLeaf(m)) return m;
    const f = fn(m);
    if (f === 'cos' || f === 'sin' || f === 'tan') return { ...m, k: m.k.map(toDeg) };
    if (f === 'arccos' || f === 'arcsin' || f === 'arctan')
      return { t: nodeOf('mul'), k: [m, parse('180 ÷ π', table)] };
    // « 23° » hors d'un cos : c'est déjà un nombre de degrés
    if (table.nodes[m.t]?.eval === 'deg') return walk(m.k[0]);
    return { ...m, k: m.k.map(walk) };
  };
  return walk(n);
}

function worldDecides(ast, ctx) {
  if (!isRelation(ast, ctx.table) || hasExtended(ast, ctx.table)) return false;
  const names = [...symbols(ast)].filter((n) => !ctx.table.syntax.constants[n]);
  return names.length > 0 && names.every((n) => n in ctx.world);
}

function indeterminateLine(ast, ctx) {
  const table = ctx.table,
    I = table.lex.indeterminate;
  if (!I || ctx.goal.kind === 'solve' || !isRelation(ast, table) || ast.k.length !== 2) return null;
  const [l, r] = ast.k;
  if (!(l.t === 'Sym' && l.name === ctx.target) || !hasExtended(r, table)) return null;
  const fi = indeterminateOf(r, table, ctx.fixed);
  return fi
    ? {
        title: I.title,
        why: String(I.why).replace('{fi}', fi),
        misconception_id: 'forme_indeterminee',
      }
    : null;
}
// une opération d'analyse encore à faire : une dérivée (u)′, une limite, un calcul sur l'infini (3 × (+∞)).
// Poids (« dette ») : 2 + 3k² pour une dérivée de k opérations (la formule d'un produit crée de la structure, mais
// des dérivées plus petites), 1 + 3k pour une limite (+10 si elle est une forme indéterminée) — découper une dérivée
// ou une limite, ou lever une indétermination, fait baisser la dette. Le contenu d'une dérivée ou d'une limite ne
// compte pas dans les autres dettes (produits non développés, calculs) : c'est le geste d'analyse qui le transforme.
function maskCalculus(n, table) {
  if (isLeaf(n)) return n;
  if (isDer(n, table) || isLim(n, table)) return { t: 'Sym', name: '□' };
  const k = n.k.map((c) => maskCalculus(c, table));
  return k.every((c, i) => c === n.k[i]) ? n : { ...n, k };
}
function pendingCalculus(n, table) {
  let d = 0;
  const ops = (x) => {
    let c = 0;
    const go = (y) => {
      if (!isLeaf(y)) {
        c++;
        y.k.forEach(go);
      }
    };
    go(x);
    return c;
  };
  const go = (x) => {
    if (isLeaf(x)) return;
    if (isDer(x, table)) {
      const k = ops(x.k[0]);
      d += 2 + 3 * k * k;
      return;
    }
    if (isLim(x, table)) {
      const u = x.k[0],
        v = varOf(u, table);
      const pt = pointOf(x.k[1], table);
      const r = v ? limitSym(u, v, pt.point, table, {}, { cc: true, side: pt.side }) : null;
      d += 1 + 3 * ops(u) + (r?.fi ? 10 : 0);
      return;
    }
    // un calcul sur l'infini ou sur 0⁺ / 0⁻ (7 ÷ 0⁺) ; « 0⁺ » seul est une valeur
    if (
      x.t !== roles(table).neg &&
      (hasInf(x, table) ||
        (hasSide(x, table) && !['right', 'left'].includes(table.nodes[x.t]?.eval)))
    )
      d += 1;
    x.k.forEach(go);
  };
  go(n);
  return d;
}

// Valeurs établies par les lignes gardées : « r = 3 », « 3 = r », « CM = 1,21 » (une grandeur seule d'un côté, un
// nombre de l'autre), hors grandeur cherchée et hors données déjà connues.
function establishedValues(lines, ctx) {
  const { table } = ctx;
  const out = {};
  for (const line of lines) {
    const L = line.L;
    if (!L || isLeaf(L) || table.nodes[L.t]?.relation !== 'eq' || L.k.length !== 2) continue;
    for (const [a, b] of [
      [L.k[0], L.k[1]],
      [L.k[1], L.k[0]],
    ]) {
      if (a.t !== 'Sym' || ctx.K.given?.[a.name]) continue;
      if (![...symbols(b)].some((x) => !table.syntax.constants[x])) {
        // un nombre, ou une valeur exacte qui n'est pas rationnelle (« F(1) = exp(1) », « S = 2π »)
        const v = exactValue(b, table);
        const f = v ? null : toF(evaluate(b, {}, table));
        if (v || (f !== null && Number.isFinite(f))) out[a.name] = b;
      } else if (
        line.status !== 'start' &&
        line.status !== 'given' &&
        (multiGoal(ctx) || [...symbols(b)].every((x) => !ctx.K.given?.[x])) &&
        // la grandeur cherchée exprimée en fonction d'une autre INCONNUE (« x = 3y ») se remplace aussi (système)
        (a.name !== ctx.target ||
          [...symbols(b)].every((x) => !ctx.K.given?.[x] && !ctx.table.syntax.constants?.[x])) &&
        !symbols(b).has(a.name) &&
        !(a.name in out)
      ) {
        // grandeur exprimée en fonction des autres (« y = 5 − 2x ») : se remplace ensuite (système)
        out[a.name] = b;
      }
    }
  }
  return Object.keys(out).length ? out : null;
}

function definedValues(lines, known, ctx) {
  const { table } = ctx;
  const given = ctx.K.given ?? {};
  const isKnown = (x) => !!(given[x] || known?.[x] || table.syntax.constants?.[x]);
  const goals = new Set([ctx.target, ...(ctx.model?.goal ?? [])].filter(Boolean));
  const out = {};
  for (const line of lines) {
    const L = line.L;
    if (!L || isLeaf(L) || table.nodes[L.t]?.relation !== 'eq' || L.k.length !== 2) continue;
    for (const [a, b] of [
      [L.k[0], L.k[1]],
      [L.k[1], L.k[0]],
    ]) {
      // la grandeur cherchée ne se remplace jamais par sa valeur (ce serait la réponse)
      if (a.t !== 'Sym' || given[a.name] || a.name in out || goals.has(a.name)) continue;
      const syms = [...symbols(b)];
      if (!syms.length || syms.includes(a.name) || !syms.every(isKnown)) continue;
      const v = ctx.world[a.name];
      if (v === undefined) continue;
      const alts = [];
      const f = String(toF(v));
      if (/^-?\d+(\.\d{1,6})?$/.test(f)) {
        const d = ctx.tryP?.(f.replace('.', ',').replace('-', '−'));
        if (d) alts.push(light(d, table));
      }
      for (const e of ctx.model?.symbols?.[a.name]?.exact ?? []) {
        const x = ctx.tryP?.(String(e));
        if (x) alts.push(light(x, table));
      }
      if (alts.length) out[a.name] = alts;
    }
  }
  return Object.keys(out).length ? out : null;
}

// « 2x + 5 » écrit seul sur une feuille simplify / evaluate : lu « A = 2x + 5 ».
function wrapExpression(ast, ctx, table) {
  if (isRelation(ast, table) || !ctx.target || ctx.goal.kind === 'solve') return ast;
  if (symbols(ast).has(ctx.target)) return ast;
  const eqType = Object.keys(table.nodes).find((t) => table.nodes[t].relation === 'eq');
  return eqType ? { t: eqType, k: [{ t: 'Sym', name: ctx.target }, ast] } : ast;
}

// ── Recherche de la chaîne de gestes ──────────────────────────────────────
// Faisceau par profondeur, toutes les lignes gardées comme sources (la plus récente d'abord) :
// une chaîne courte depuis une ligne ancienne passe avant une chaîne longue depuis la dernière.
// Les candidats d'une profondeur sont triés par distance (différence symétrique des multi-ensembles
// de sous-arbres) à la ligne visée et seuls les meilleurs (LIMITS.beam) sont développés.
// Niveaux (CONTRACT §11) : le COÛT d'une chaîne = nombre de RÈGLES DISTINCTES non implicites au niveau de
// la feuille (même dénominateur sur deux fractions = 1 geste ; les HORA restent comptés par application) ;
// coût ≤ maxGestures, profondeur totale ≤ LIMITS.maxDepth (implicites compris).
function search(engine, state, targetL, targetKey, { trap = false } = {}) {
  const { ctx } = state;
  const tBag = bag(targetL);
  // variables libres des règles : liées à partir de la ligne visée (CONTRACT §8), plus les rapports
  // entiers entre les nombres de la ligne visée et ceux des lignes gardées (k = 5 dans 2/3 → 10/15)
  const free = freeCandidates(engine, targetL);
  addRatioCandidates(free, targetL, state.lines, ctx.table);
  let fsig = '';
  for (const [r, list] of free)
    if (list.length)
      fsig += r.id + ':' + list.map((b) => Object.values(b).map(keyOf).join(',')).join(';') + '|';
  // feuille « formule » : une grandeur dont la valeur est écrite sur une ligne gardée (« r = 3 ») se remplace
  // ensuite comme une donnée de l'énoncé (« a = u + 3r → a = u + 3 × 3 »)
  const known =
    ctx.goal.kind === 'evaluate'
      ? establishedValues(state.lines, ctx)
      : ctx.kernel.analysis
        ? analysisParts(state.lines)
        : null;
  if (known)
    fsig +=
      'kn:' +
      Object.entries(known)
        .map(([n, v]) => n + '=' + keyOf(v))
        .join(',') +
      '|';
  // feuille d'affirmations : « Écrire autrement » lit les écritures détaillées dans la ligne visée
  const unfold = ctx.K.unfold ? targetL : null;
  if (unfold) fsig += 'uf:' + targetKey + '|';
  // une grandeur définie sur une ligne (« E(X) = p », « C = y₀ ») par des grandeurs toutes connues se remplace
  // aussi par sa VALEUR (« S = E(X) + V(X) → S = 0,4 + 0,24 ») : écriture exacte du modèle et écriture décimale
  const alt =
    ctx.goal.kind === 'evaluate' && ctx.world ? definedValues(state.lines, known, ctx) : null;
  if (alt) fsig += 'alt:' + Object.keys(alt).join(',') + '|';
  const K = fsig
    ? {
        ...ctx.K,
        ...(free.size ? { free } : {}),
        ...(known ? { given: { ...ctx.K.given, ...known } } : {}),
        ...(alt ? { givenAlt: { ...ctx.K.givenAlt, ...alt } } : {}),
        ...(unfold ? { unfoldTarget: unfold } : {}),
      }
    : ctx.K;
  const leveled = !!ctx.levelOrder && !trap;
  const maxCost = trap ? Infinity : ctx.maxGestures;
  const maxDepth = leveled ? LIMITS.maxDepth : LIMITS.depth;
  const seen = new Set(state.lines.map((l) => l.key));
  let frontier = [];
  for (let i = state.lines.length - 1; i >= 0; i--)
    frontier.push({
      from: i,
      L: state.lines[i].L,
      chain: [],
      cost: 0,
      used: [],
      // famille de gestes (« develop ») : seulement quand la ligne ne transforme qu'UN terme de la ligne d'origine
      group: changedTerms(state.lines[i].L, targetL, ctx.table) <= 1,
    });
  // analyse (dérivées, limites) : chaque geste propose beaucoup de variantes (une par dérivée / limite, et « toutes ») ;
  // le faisceau est trois fois plus large pour ne pas perdre la bonne chaîne
  const wide = pendingCalculus(state.lines.at(-1).L, ctx.table) > 0;
  let count = 0;
  // Calculs compris dans un geste (« (x + 5)(x + 2) → x² + x × 2 + 5x + 5 × 2 » = Développer) : une écriture qui
  // a la même forme « tout calculé » que la ligne visée ne s'en distingue que par des calculs. Repli seulement :
  // un geste qui atteint exactement la ligne visée passe avant.
  const calcOn = !trap && !!engine.builtins?.fold;
  const reduceCalc = calcOn && !hasReducibleFraction(targetL, ctx.table);
  const tCalc = calcOn ? calcKey(targetL, ctx.table, reduceCalc) : null;
  let pureCalc = null,
    calcHit = null;
  // un calcul fait baisser le nombre d'opérations (mettre au même dénominateur l'augmente : c'est un geste)
  const tOps = opsCount(targetL);
  // la ligne visée a encore des calculs à faire (ce n'est pas son écriture « tout calculé »)
  // (écritures en lettres seulement : sur des nombres, calculer reste un geste selon le niveau, §11)
  const tPartial =
    calcOn &&
    !numericOnly(targetL, ctx.table) &&
    keyOf(light(calcNorm(targetL, ctx.table), ctx.table)) !== targetKey;
  if (calcOn) {
    for (let i = state.lines.length - 1; i >= 0 && !pureCalc; i--) {
      const l = state.lines[i].L;
      if (tOps < opsCount(l) && calcKey(l, ctx.table, reduceCalc) === tCalc)
        pureCalc = { from: i, chain: [{ rule: engine.builtins.fold, ast: targetL }] };
    }
  }
  // (hors analyse : le faisceau des dérivées est large, ce test coûterait trop)
  const monoTarget = trap || ctx.kernel.analysis ? null : monoKey(targetL, ctx.table);
  const monoSig = monoTarget ? termSig(targetL, ctx.table) : null;
  const monoTargetF = monoTarget ? monoKey(targetL, ctx.table, true) : null;
  for (let depth = 1; depth <= maxDepth; depth++) {
    const next = [];
    if (calcHit) return calcHit;
    for (const st of frontier) {
      // coût épuisé : seuls les gestes implicites et les règles déjà employées restent possibles
      const only = !trap && st.cost >= maxCost ? withMates(engine, st.used, st.group) : undefined;
      const cands = expand(
        engine,
        st.L,
        ctx,
        trap && depth === 1 ? 'trap' : 'rewrite',
        K,
        fsig,
        only,
      );
      for (const c of cands) {
        const add = trap || isImplicit(c.rule, ctx) || usedHas(st.used, c.rule, st.group) ? 0 : 1;
        const cost = st.cost + add;
        if (cost > maxCost) continue;
        const k = keyOf(c.ast);
        if (k === targetKey) return { from: st.from, chain: [...st.chain, c] };
        // même ligne à l'écriture des monômes près (« x × x » écrit « x² », « 5 × x » écrit « 5x ») : le geste seul
        if (
          monoTarget &&
          c.rule.builtin !== 'fold' &&
          termSig(c.ast, ctx.table) === monoSig &&
          (monoKey(c.ast, ctx.table) === monoTarget ||
            // famille « développer » : calculer les coefficients des monômes fait partie du geste
            (!!c.rule.gesture &&
              st.from === state.lines.length - 1 &&
              monoKey(c.ast, ctx.table, true) === monoTargetF))
        )
          return { from: st.from, chain: [...st.chain, { ...c, ast: targetL }] };
        if (
          calcOn &&
          !calcHit &&
          c.rule.builtin !== 'fold' &&
          (reduceCalc || tPartial ? tOps <= opsCount(c.ast) : tOps < opsCount(c.ast)) &&
          calcKey(c.ast, ctx.table, reduceCalc) === tCalc
        ) {
          // les calculs restent un geste « Calculer » (sauf s'il est implicite au niveau de l'élève) : jamais au-delà
          // du nombre de gestes permis par ligne
          // calculs PARTIELS (la ligne visée garde des calculs à faire : « x² + 8x + 4² ») : écrire une partie
          // des produits en appliquant la règle fait partie du geste (comme l'ancien moteur)
          const fold = engine.builtins.fold,
            extra =
              isImplicit(fold, ctx) || [...st.used, c.rule].includes(fold) || tPartial ? 0 : 1;
          if (cost + extra <= maxCost)
            calcHit = {
              from: st.from,
              chain: [
                ...st.chain,
                c,
                ...(extra || isImplicit(fold, ctx) ? [{ rule: fold, ast: targetL }] : []),
              ],
            };
        }
        if (seen.has(k)) continue;
        seen.add(k);
        if (++count > LIMITS.budget)
          return (
            calcHit ??
            pureCalc ??
            (calcOn ? fallbackGesture(engine, state, targetL, tCalc, tOps, K) : null)
          );
        if (depth < maxDepth)
          next.push({
            from: st.from,
            L: c.ast,
            chain: [...st.chain, c],
            cost,
            used: add ? [...st.used, c.rule] : st.used,
            group: st.group,
            h: dist(c.ast, tBag, fsig ? ctx.table : null),
          });
      }
    }
    // faisceau GLOBAL (toutes sources confondues) : le coût ne croît pas avec le nombre de lignes
    next.sort((a, b) => a.h - b.h || a.cost - b.cost || b.from - a.from);
    frontier = next.slice(0, (depth >= 2 ? LIMITS.beamDeep : LIMITS.beam) * (wide ? 3 : 1));
  }
  return (
    calcHit ?? pureCalc ?? (calcOn ? fallbackGesture(engine, state, targetL, tCalc, tOps, K) : null)
  );
}

// Ensemble de solutions : des relations « x ⋈ nombre » reliées par « et » / « ou » (encadrements compris).
function isSolutionSet(L, x, table) {
  const sp = table.nodes[L.t];
  if (sp?.connective) return L.k.every((c) => isSolutionSet(c, x, table));
  if (!['lt', 'le', 'gt', 'ge', 'eq', 'ne'].includes(sp?.relation) || L.k.length !== 2)
    return false;
  const [a, b] = L.k;
  const isX = (n) => n.t === 'Sym' && n.name === x;
  return (isX(a) && !symbols(b).size) || (isX(b) && !symbols(a).size);
}

// Degré d'une écriture en x (Infinity si ce n'est pas un polynôme simple)
function degreeIn(n, x, table) {
  if (n.t === 'Num') return 0;
  if (n.t === 'Sym') return n.name === x ? 1 : table.syntax.constants?.[n.name] ? 0 : Infinity;
  if (isLeaf(n)) return Infinity;
  const ev = table.nodes[n.t]?.eval;
  const ds = n.k.map((c) => degreeIn(c, x, table));
  if (ev === 'add') return Math.max(...ds);
  if (ev === 'mul') return ds.reduce((a, b) => a + b, 0);
  if (ev === 'neg') return ds[0];
  if (ev === 'div') return ds[1] === 0 ? ds[0] : Infinity;
  if (ev === 'pow' && n.k[1].t === 'Num' && n.k[1].q === 1 && n.k[1].p >= 0)
    return ds[0] * n.k[1].p;
  return ds.every((d) => d === 0) ? 0 : Infinity;
}

// Une inégalité dont on lit le signe : produit / quotient de facteurs de degré ≤ 1 face à 0, ou x² face à un nombre
function signStudiable(L, x, table) {
  if (!['lt', 'le', 'gt', 'ge'].includes(table.nodes[L.t]?.relation) || L.k.length !== 2)
    return false;
  const [a, b] = L.k;
  const zero = (n) => n.t === 'Num' && n.p === 0;
  const side = zero(b) ? a : zero(a) ? b : null;
  const factored = (n) => {
    const ev = table.nodes[n.t]?.eval;
    if (!isLeaf(n) && (ev === 'mul' || ev === 'div')) return n.k.every(factored);
    if (!isLeaf(n) && ev === 'neg') return factored(n.k[0]);
    if (!isLeaf(n) && ev === 'pow' && n.k[1].t === 'Num' && n.k[1].q === 1)
      return degreeIn(n.k[0], x, table) <= 1;
    return degreeIn(n, x, table) <= 1;
  };
  if (side && symbols(side).has(x) && factored(side)) return true;
  // fonction carré : « x² < 9 »
  const sq = (n) =>
    !isLeaf(n) &&
    table.nodes[n.t]?.eval === 'pow' &&
    n.k[0].t === 'Sym' &&
    n.k[0].name === x &&
    n.k[1].t === 'Num' &&
    n.k[1].p === 2;
  return (sq(a) && !symbols(b).size) || (sq(b) && !symbols(a).size);
}

// Replis quand aucune suite de gestes n'atteint la ligne visée :
//   - Écrire autrement : même forme « tout calculé », plus d'opérations (« 25 → 5² », « 9x² → (3x)² ») ;
//   - une règle du pack lue à l'envers : la ligne visée contient le RÉSULTAT d'une règle dont l'écriture d'avant
//     ne diffère d'une ligne gardée que par des calculs et des termes réunis (« x² − 7x → x(x − 7) » = Facteur
//     commun ; « x² − 2x + 1 → (x − 1)² » = identité remarquable).
// les membres d'une relation ne font intervenir que des nombres (et la grandeur seule d'un membre)
function numericOnly(ast, table) {
  if (!table.nodes[ast.t]?.relation)
    return ![...symbols(ast)].some((v) => !table.syntax.constants?.[v]);
  return ast.k.every(
    (m) => m.t === 'Sym' || ![...symbols(m)].some((v) => !table.syntax.constants?.[v]),
  );
}

// Forme « tout calculé » où un décimal se lit comme le nombre qu'il vaut (« 3,5 » = « 7 ÷ 2 ») ; avec reduce, une
// fraction de deux entiers se lit réduite (« 15 ÷ 25 » = « 3 ÷ 5 »)
function calcKey(L, table, reduce = false) {
  const divT = Object.keys(table.nodes).find((k) => table.nodes[k].eval === 'div');
  const ratio = (n) => {
    if (n.t === 'Num') return n.q !== 1 ? num(n.p, n.q) : n;
    if (isLeaf(n)) return n;
    const k = n.k.map(ratio);
    if (reduce && n.t === divT && k.length === 2 && k[0].t === 'Num' && k[1].t === 'Num') {
      const v = ratDiv(k[0], k[1]);
      if (v) return v;
    }
    return { ...n, k };
  };
  return keyOf(ratio(calcNorm(L, table)));
}
function ratDiv(a, b) {
  const g = (x, y) => (y ? g(y, x % y) : Math.abs(x));
  let p = a.p * b.q,
    q = a.q * b.p;
  if (!Number.isSafeInteger(p) || !Number.isSafeInteger(q) || q === 0) return null;
  if (q < 0) [p, q] = [-p, -q];
  const d = g(p, q) || 1;
  return num(p / d, q / d);
}
// une fraction de deux entiers qui se simplifie encore (« 10/15 ») : mettre au même dénominateur n'est pas un calcul
function hasReducibleFraction(L, table) {
  const divT = Object.keys(table.nodes).find((k) => table.nodes[k].eval === 'div');
  const g = (x, y) => (y ? g(y, x % y) : Math.abs(x));
  const walk = (n) => {
    if (n.t === 'Num') return n.q !== 1 && g(n.p, n.q) > 1;
    if (isLeaf(n)) return false;
    if (
      n.t === divT &&
      n.k[0].t === 'Num' &&
      n.k[1].t === 'Num' &&
      n.k[0].q === 1 &&
      n.k[1].q === 1
    )
      return g(n.k[0].p, n.k[1].p) > 1;
    return n.k.some(walk);
  };
  return walk(L);
}

function fallbackGesture(engine, state, targetL, tCalc, tOps, K) {
  const { ctx } = state,
    table = ctx.table;
  const unfold = engine.builtins?.unfold;
  if (unfold) {
    for (let i = state.lines.length - 1; i >= 0; i--) {
      const l = state.lines[i].L;
      if (tOps > opsCount(l) && calcKey(l, table, !hasReducibleFraction(targetL, table)) === tCalc)
        return { from: i, chain: [{ rule: unfold, ast: targetL }] };
    }
  }
  // une ligne « ensemble de solutions » équivalente à la ligne étudiée dans un tableau de signes juste (lot I9)
  const read = engine.builtins?.sign_table_read;
  const sv = ctx.target ? lineVar(targetL, ctx) : null;
  if (read && sv && isSolutionSet(targetL, sv, table)) {
    for (let i = state.lines.length - 1; i >= 0; i--) {
      const l = state.lines[i];
      if (!l.signTable || !l.L) continue;
      if (equivRelation(l.L, targetL, { table, target: sv, hyps: ctx.hyps, fixed: ctx.fixed }))
        return { from: i, chain: [{ rule: read, ast: targetL }] };
    }
  }
  if (ctx.maxGestures < 1) return null;
  // Tableau de signes : un produit / quotient de facteurs du premier degré (ou x² face à un nombre) comparé, puis
  // l'ensemble des solutions écrit (« (x − 1)(x − 4) < 0 → 1 < x < 4 »)
  const signs = engine.builtins?.sign_table;
  const v = ctx.target ? lineVar(targetL, ctx) : null;
  if (signs && v && isSolutionSet(targetL, v, table)) {
    for (let i = state.lines.length - 1; i >= 0; i--) {
      const l = state.lines[i].L;
      if (!signStudiable(l, v, table)) continue;
      if (equivRelation(l, targetL, { table, target: v, hyps: ctx.hyps, fixed: ctx.fixed })) {
        return { from: i, chain: [{ rule: signs, ast: targetL }] };
      }
    }
  }
  // factoriser une écriture polynomiale (« 3(x² − 2x − 3) → 3(x − 3)(x + 1) ») : plus de facteurs en x
  const pf =
    ctx.kernel?.analysis || ctx.form === 'factored'
      ? polyFactorGesture(engine, state, targetL)
      : null;
  if (pf) return pf;
  // dériver en une ligne, calculs compris (« ((x − 3)² + 1)′ → 2(x − 3) ») : une seule formule du pack
  const dv = ctx.kernel?.analysis
    ? (deriveInOneLine(engine, state, targetL, tCalc, K) ?? analysisCalc(engine, state, targetL))
    : null;
  if (dv) return dv;
  const sh = shapeGesture(engine, state, targetL);
  if (sh) return sh;
  const lineKeys = state.lines.map((l) => calcCollectKey(l.L, table));
  const pres = rulePreimages(targetL, engine, K);
  for (const pre of pres) {
    const k = calcCollectKey(pre.ast, table);
    const i = lineKeys.lastIndexOf(k);
    if (i >= 0) return { from: i, chain: [{ rule: pre.rule, ast: targetL }] };
  }
  // une variable du motif a absorbé plusieurs termes (« 3 × (x² − 2x − 3) » lu k × (a + b), b = −2x − 3) :
  // l'écriture d'avant les répartit (« 3x² + 3 × (−2x − 3) » = « 3x² − 6x − 9 »)
  const R = roles(table);
  for (const pre of pres) {
    const P = prepareRule(pre.rule, table);
    if (P.lhs?.t !== R.add || P.rhs?.t !== R.mul) continue; // une factorisation : somme → produit
    const k = calcCollectKey(spreadNumbers(pre.ast, table), table);
    const i = lineKeys.lastIndexOf(k);
    if (i >= 0) return { from: i, chain: [{ rule: pre.rule, ast: targetL }] };
  }
  // facteur commun NÉGATIF (« −2x − 6 → −2(x + 3) ») : le signe sort avec le facteur, « −(a + b) » = « −a − b »
  for (const pre of pres) {
    const P = prepareRule(pre.rule, table);
    if (P.lhs?.t !== R.add || P.rhs?.t !== R.mul) continue;
    const pushed = pushNeg(pre.ast, table);
    if (pushed === pre.ast) continue;
    const i = lineKeys.lastIndexOf(calcCollectKey(spreadNumbers(pushed, table), table));
    if (i >= 0) return { from: i, chain: [{ rule: pre.rule, ast: targetL }] };
  }
  return null;
}

// « −(a + b) » → « −a − b », « −(−a) » → « a » (même objet si rien ne change)
function pushNeg(L, table) {
  const R = roles(table);
  const neg = (n) => (!isLeaf(n) && n.t === 'Neg' ? n.k[0] : { t: 'Neg', k: [n] });
  const go = (n) => {
    if (isLeaf(n)) return n;
    const k = n.k.map(go);
    if (n.t === 'Neg' && !isLeaf(k[0]) && k[0].t === R.add) return { t: R.add, k: k[0].k.map(neg) };
    if (n.t === 'Neg' && !isLeaf(k[0]) && k[0].t === 'Neg') return k[0].k[0];
    return k.every((c, j) => c === n.k[j]) ? n : { ...n, k };
  };
  return go(L);
}
// Factoriser (builtin poly_factor) : une écriture « tête = produit » dont les facteurs sont des polynômes en une
// lettre, de même valeur qu'une ligne gardée (même tête) qui a MOINS de facteurs de degré ≥ 1.
function polyFactorGesture(engine, state, targetL) {
  const rule = engine.builtins?.poly_factor;
  const table = state.ctx.table;
  if (!rule || isLeaf(targetL) || !sameHeadRel(targetL, table)) return null;
  const R = roles(table);
  const right = targetL.k[1];
  const vars = [...symbols(right)].filter((s) => !table.syntax.constants?.[s]);
  if (vars.length !== 1) return null;
  const v = vars[0];
  const xFactors = (n) => polyFactorCount(n, v, table);
  const nt = xFactors(right);
  if (nt < 2) return null;
  const xs = [-1.7, 0.4, 1.3, 2.9];
  const val = (n, x) => ptValue(n, { [v]: x }, table);
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i].L;
    if (!l || !sameHead(l, targetL) || !sameHeadRel(l, table)) continue;
    const nl = xFactors(l.k[1]);
    if (nl < 0 || nl >= nt) continue;
    if (xs.every((x) => ptClose(val(right, x), val(l.k[1], x))))
      return { from: i, chain: [{ rule, ast: targetL }] };
  }
  return null;
}
// nombre de facteurs de degré ≥ 1 en v d'un produit (puissances comptées), −1 si un facteur n'est pas un polynôme
function polyFactorCount(n, v, table) {
  const R = roles(table);
  while (!isLeaf(n) && n.t === 'Neg') n = n.k[0]; // le signe n'est pas un facteur en x
  const fs = !isLeaf(n) && n.t === R.mul ? n.k : [n];
  let c = 0;
  for (const f of fs) {
    const d = polyDeg(f, v, table);
    if (d === null) return -1;
    if (d >= 1) c += !isLeaf(f) && f.t === R.pow && f.k[1].t === 'Num' ? f.k[1].p : 1;
  }
  return c;
}
function sameHeadRel(L, table) {
  return table.nodes[L.t]?.relation === 'eq' && L.k.length === 2;
}

// k × (a + b + …) → k × a + k × b + … quand k est un nombre (répartir un facteur numérique)
function spreadNumbers(L, table) {
  const R = roles(table);
  const go = (n) => {
    if (isLeaf(n)) return n;
    const k = n.k.map(go);
    if (n.t === R.mul && k.length === 2) {
      const iNum = k.findIndex((c) => c.t === 'Num'),
        iSum = k.findIndex((c) => !isLeaf(c) && c.t === R.add);
      if (iNum >= 0 && iSum >= 0 && iNum !== iSum)
        return { t: R.add, k: k[iSum].k.map((c) => ({ t: R.mul, k: [k[iNum], c] })) };
    }
    return { ...n, k };
  };
  return light(go(L), table);
}

// ── Dériver en une ligne (lot I10) ──────────────────────────────────────────
// Chaque (u)′ d'une ligne gardée est remplacée par sa dérivée : linéarité et dérivées usuelles (builtins
// derive_linear / derive_ref), plus AU PLUS une formule du pack (règle `rewrite` dont le motif est une dérivée :
// produit, quotient, composée…). La ligne visée doit en être l'écriture, aux calculs près. Le geste rendu est cette
// formule, sinon « Dérivées usuelles ».
function derivationFormulas(engine) {
  if (engine._derForms) return engine._derForms;
  const table = engine.table;
  return (engine._derForms = engine.rules.filter((r) => {
    if (r.kind !== 'rewrite' || !r.lhs) return false;
    const P = prepareRule(r, table);
    return !!P.lhs && table.nodes[P.lhs.t]?.eval === 'derive';
  }));
}
function hasDerNode(n, table) {
  if (isLeaf(n)) return false;
  return isDer(n, table) || n.k.some((c) => hasDerNode(c, table));
}
export function derivePlan(engine, L, K, maxFormulas = 1) {
  const table = engine.table;
  const lin = engine.builtins?.derive_linear,
    ref = engine.builtins?.derive_ref;
  const forms = derivationFormulas(engine);
  const used = [];
  let budget = maxFormulas,
    steps = 0;
  // une dérivée (u)′ → son écriture sans dérivée, ou null
  const solve = (d, depth) => {
    if (depth > 6 || ++steps > 400) return null;
    if (ref) {
      for (const c of rewriteOnce(d, ref, engine, K))
        if (!hasDerNode(c, table)) {
          used.push(ref);
          return c;
        }
    }
    if (lin) {
      const c = rewriteOnce(d, lin, engine, K)[0];
      if (c && keyOf(c) !== keyOf(d)) {
        const mark = used.length,
          b0 = budget;
        const r = resolve(c, depth + 1);
        if (r) return (used.push(lin), r);
        used.length = mark;
        budget = b0;
      }
    }
    // c ÷ v (c constant) se dérive aussi comme c × v⁻¹ : « (1 ÷ (x − 2)²)′ » par la dérivée de uⁿ
    const R = roles(table);
    const u0 = d.k[0];
    if (!isLeaf(u0) && u0.t === R.div && !symbols(u0.k[0]).size && !d._pow) {
      const den = u0.k[1];
      const pw =
        !isLeaf(den) && den.t === R.pow && den.k[1].t === 'Num'
          ? { t: R.pow, k: [den.k[0], num(-den.k[1].p, den.k[1].q)] }
          : { t: R.pow, k: [den, num(-1)] };
      const alt = { ...d, k: [light({ t: R.mul, k: [u0.k[0], pw] }, table)], _pow: true };
      const r = solve(alt, depth + 1);
      if (r) return r;
    }
    if (budget > 0) {
      for (const f of forms) {
        for (const c of rewriteOnce(d, f, engine, K)) {
          const mark = used.length,
            b0 = budget;
          budget--;
          const r = resolve(c, depth + 1);
          if (r) return (used.push(f), r);
          used.length = mark;
          budget = b0;
        }
      }
    }
    return null;
  };
  const resolve = (n, depth) => {
    if (isLeaf(n)) return n;
    if (isDer(n, table)) return solve(light(n, table), depth);
    const k = [];
    for (const c of n.k) {
      const r = resolve(c, depth);
      if (!r) return null;
      k.push(r);
    }
    return { ...n, k };
  };
  if (!hasDerNode(L, table)) return null;
  const out = resolve(L, 0);
  if (!out) return null;
  const formula = used.find((r) => r !== lin && r !== ref);
  return { ast: light(out, table), rule: formula ?? (used.includes(ref) ? ref : lin) };
}
// Forme « produit de puissances » pour comparer une dérivée aux calculs près : a ÷ b → a × b⁻¹, −u → −1 × u,
// éléments neutres retirés (0 × u, u × 1, u + 0, u¹), facteurs numériques multipliés, (uᵃ)ᵇ → uᵃᵇ — jamais rien
// de développé ni de factorisé.
function powerForm(L, table) {
  const R = roles(table);
  const isN = (n, p) => n.t === 'Num' && n.p === p && n.q === 1;
  const go = (n) => {
    if (isLeaf(n)) return n;
    const k = n.k.map(go);
    if (n.t === R.neg) {
      const u = k[0];
      if (u.t === 'Num') return num(-u.p, u.q);
      return go({ t: R.mul, k: [num(-1), u] });
    }
    if (n.t === R.div) return go({ t: R.mul, k: [k[0], { t: R.pow, k: [k[1], num(-1)] }] });
    if (n.t === R.sqrt) return go({ t: R.pow, k: [k[0], num(1, 2)] });
    if (n.t === R.pow) {
      const [b, e] = k;
      if (isN(e, 1)) return b;
      if (isN(e, 0)) return num(1);
      // (a × b)ᵉ → aᵉ × bᵉ
      if (!isLeaf(b) && b.t === R.mul && e.t === 'Num')
        return go({ t: R.mul, k: b.k.map((c) => ({ t: R.pow, k: [c, e] })) });
      if (!isLeaf(b) && b.t === R.pow && b.k[1].t === 'Num' && e.t === 'Num')
        return go({ t: R.pow, k: [b.k[0], Q.mul(b.k[1], e)] });
      if (b.t === 'Num' && e.t === 'Num' && e.q === 1 && Math.abs(e.p) <= 12 && b.p !== 0) {
        let v = num(1);
        for (let i = 0; i < Math.abs(e.p); i++) v = Q.mul(v, b);
        return e.p < 0 ? num(v.q, v.p) : v;
      }
      return { ...n, k };
    }
    if (n.t === R.mul) {
      let coef = num(1);
      const rest = [];
      const flat = (c) => (!isLeaf(c) && c.t === R.mul ? c.k.forEach(flat) : rest.push(c));
      k.forEach(flat);
      const others = [];
      for (const c of rest) {
        if (c.t === 'Num') coef = Q.mul(coef, c);
        else others.push(c);
      }
      if (coef.p === 0) return num(0);
      // mêmes bases réunies : u × u⁻² → u⁻¹
      const byBase = new Map();
      for (const c of others) {
        const isP = !isLeaf(c) && c.t === R.pow && c.k[1].t === 'Num';
        const base = isP ? c.k[0] : c,
          e = isP ? c.k[1] : num(1);
        const kk = keyOf(base);
        const prev = byBase.get(kk);
        byBase.set(kk, prev ? { base, e: Q.add(prev.e, e) } : { base, e });
      }
      const parts = [...byBase.values()]
        .filter((x) => x.e.p !== 0)
        .map((x) => (isN(x.e, 1) ? x.base : { t: R.pow, k: [x.base, x.e] }));
      if (!isN(coef, 1) || !parts.length) parts.unshift(coef);
      return parts.length === 1 ? parts[0] : { t: R.mul, k: parts };
    }
    if (n.t === R.add) {
      const terms = [];
      const flat = (c) => (!isLeaf(c) && c.t === R.add ? c.k.forEach(flat) : terms.push(c));
      k.forEach(flat);
      let c0 = num(0);
      const others = [];
      for (const c of terms) {
        if (c.t === 'Num') c0 = Q.add(c0, c);
        else others.push(c);
      }
      if (c0.p !== 0 || !others.length) others.push(c0);
      return others.length === 1 ? others[0] : { t: R.add, k: others };
    }
    return { ...n, k };
  };
  let x = L,
    prev = null;
  for (let i = 0; i < 4; i++) {
    x = light(go(calcNorm(light(go(x), table), table)), table);
    const key = keyOf(x);
    if (key === prev) break;
    prev = key;
  }
  return prev ?? keyOf(x);
}
const sameHead = (a, b) =>
  !isLeaf(a) && !isLeaf(b) && a.k?.[0] && b.k?.[0] && keyOf(a.k[0]) === keyOf(b.k[0]);
// Remplacer les parties nommées par leur écriture (u(x), v′(x)), dans une ligne gardée ; null si rien ne change
function withPartsReplaced(L, parts, table) {
  if (!parts) return null;
  let changed = false;
  const go = (n) => {
    if (n.t === 'Sym' && n.name in parts) {
      changed = true;
      return parts[n.name];
    }
    if (isLeaf(n)) return n;
    return { ...n, k: n.k.map(go) };
  };
  const out = go(L);
  return changed ? light(out, table) : null;
}
// Feuille d'analyse : une ligne sans dérivée qui ne diffère d'une ligne gardée (même tête) que par des calculs sur
// les puissances (« (1 ÷ x) ÷ ln(x) → 1 ÷ (x × ln(x)) », « (√x)² → x »), les parties nommées éventuellement
// remplacées : « Calculer » (et « Remplacer »).
function analysisCalc(engine, state, targetL) {
  const table = state.ctx.table;
  const fold = engine.builtins?.fold,
    sub = engine.builtins?.substitute,
    collect = engine.builtins?.collect;
  if (!fold || hasDerNode(targetL, table) || isLeaf(targetL)) return null;
  const tKey = powerForm(targetL, table);
  const parts = analysisParts(state.lines);
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i].L;
    if (!l || hasDerNode(l, table) || !sameHead(l, targetL)) continue;
    const head = l.k[0];
    const body = withPartsReplaced(l.k[1], parts, table);
    const cand = body ? { ...l, k: [head, body] } : l;
    if (body && !sub) continue;
    let last = null;
    if (powerForm(cand, table) === tKey) last = fold;
    else if (collect && calcCollectKey(cand, table) === calcCollectKey(targetL, table))
      last = collect;
    if (!last) continue;
    return {
      from: i,
      chain: body
        ? [
            { rule: sub, ast: targetL },
            { rule: last, ast: targetL },
          ]
        : [{ rule: last, ast: targetL }],
    };
  }
  return null;
}
function deriveInOneLine(engine, state, targetL, tCalc, K) {
  const table = state.ctx.table;
  if (hasDerNode(targetL, table)) return null;
  let tKey = null;
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i].L;
    if (!l) continue;
    // une fois la dérivée écrite (sans (u)′), la suite se justifie à partir d'elle
    if (!hasDerNode(l, table)) {
      if (i > 0 && sameHead(l, targetL)) return null;
      continue;
    }
    const plan = derivePlan(engine, l, K);
    if (!plan?.rule) continue;
    tKey ??= powerForm(targetL, table);
    if (powerForm(plan.ast, table) === tKey)
      return { from: i, chain: [{ rule: plan.rule, ast: targetL }] };
  }
  return null;
}

// ── Forme d'une équation / inéquation en x (lot I3) ─────────────────────────
// Rang : 30 x au dénominateur · 25 x des deux côtés (ou une constante non nulle en face d'un degré ≥ 2) ·
// 20 « somme = 0 » de degré ≥ 2 · 10 « produit = 0 », « puissance = c » · 5 premier degré · 3 « … ou … » du
// premier degré · 0 ensemble de solutions. Une ligne ÉQUIVALENTE qui fait baisser le rang d'un cran du cours
// compte comme le geste correspondant (dénominateur chassé, tout d'un côté, factoriser, produit nul…).
function polyDeg(n, x, table) {
  const ev = table.nodes[n.t]?.eval;
  if (n.t === 'Num') return 0;
  if (n.t === 'Sym') return n.name === x ? 1 : 0;
  if (isLeaf(n)) return 0;
  const ks = n.k.map((c) => polyDeg(c, x, table));
  if (ks.some((d) => d === null)) return null;
  if (ev === 'add') return Math.max(...ks);
  if (ev === 'neg') return ks[0];
  if (ev === 'mul') return ks.reduce((a, b) => a + b, 0);
  if (ev === 'div') return ks[1] === 0 ? ks[0] : null;
  if (ev === 'pow')
    return ks[1] === 0 && n.k[1].t === 'Num' && n.k[1].q === 1 && n.k[1].p >= 0
      ? ks[0] * n.k[1].p
      : ks[0] === 0 && ks[1] === 0
        ? 0
        : null;
  return ks.every((d) => d === 0) ? 0 : null;
}
function hasXDen(n, x, table) {
  if (isLeaf(n)) return false;
  if (table.nodes[n.t]?.eval === 'div' && symbols(n.k[1]).has(x)) return true;
  return n.k.some((c) => hasXDen(c, x, table));
}
function hasSqrtX(n, x, table) {
  if (isLeaf(n)) return false;
  if (table.nodes[n.t]?.eval === 'sqrt' && symbols(n.k[0]).has(x)) return true;
  return n.k.some((c) => hasSqrtX(c, x, table));
}
function eqShape(L, x, table) {
  const sp = table.nodes[L.t];
  if (sp?.connective) {
    if (isSolutionSet(L, x, table)) return { rank: 0 };
    const subs = L.k.map((c) => eqShape(c, x, table));
    return subs.every((s0) => s0 && s0.rank <= 5) ? { rank: 3 } : null;
  }
  if (!['lt', 'le', 'gt', 'ge', 'eq', 'ne'].includes(sp?.relation) || L.k.length !== 2) return null;
  if (isSolutionSet(L, x, table)) return { rank: 0 };
  if (hasXDen(L, x, table)) {
    const [a0, b0] = L.k;
    const quotientZero = (u, v) => table.nodes[u.t]?.eval === 'div' && v.t === 'Num' && v.p === 0;
    return { rank: 30, kind: quotientZero(a0, b0) || quotientZero(b0, a0) ? 'quotient' : null };
  }
  if (hasSqrtX(L, x, table)) return { rank: 26, kind: 'sqrt' };
  const [a, b] = L.k;
  const da = polyDeg(a, x, table),
    db = polyDeg(b, x, table);
  if (da === null || db === null) return null;
  const deg = Math.max(da, db);
  if (deg <= 1) return { rank: 5 };
  const isZero = (n) => n.t === 'Num' && n.p === 0;
  const ev = (n) => table.nodes[n.t]?.eval;
  let xs = da > 0 ? a : b;
  const other = da > 0 ? b : a;
  if (ev(xs) === 'neg') xs = xs.k[0];
  if (ev(xs) === 'mul') {
    const withX = xs.k.filter((c) => polyDeg(c, x, table) > 0);
    if (withX.length === 1 && xs.k.length > 1) xs = withX[0];
    else if (withX.length >= 2) xs = { ...xs, k: withX };
  }
  if (da > 0 && db > 0) return { rank: 25 };
  const product = ev(xs) === 'mul' && xs.k.filter((c) => polyDeg(c, x, table) > 0).length >= 2;
  const power = ev(xs) === 'pow' && polyDeg(xs.k[0], x, table) === 1;
  if (isZero(other)) {
    if (product) return { rank: 10, kind: 'product' };
    if (power || (ev(xs) === 'mul' && xs.k.filter((c) => polyDeg(c, x, table) > 0).length === 1))
      return { rank: 10, kind: 'power' };
    return { rank: 20 };
  }
  if (power) return { rank: 10, kind: 'root' };
  return { rank: 25 };
}
// décimales écrites d'un nombre (« 3,32 » → 2), null si ce n'est pas un décimal écrit
function writtenPlaces(n) {
  if (n?.t !== 'Num') return null;
  if (n.dec) return (String(n.dec).split(/[,.]/)[1] ?? '').length;
  return Number(n.q) === 1 ? 0 : null;
}
const roundsTo = (e, target, places) => {
  const f = 10 ** places;
  return Math.abs(Math.round(e * f + 1e-9 * Math.sign(e)) / f - target) < 0.5 / f / 100;
};

// Seuil « par tests de rangs » (kernel.method / kernel.test) : « 1000 × 0,8^10 ≈ 107,4 » teste n = 10 ;
// « n ≥ 11 » se conclut quand le rang 10 ne convient pas et le rang 11 convient.
function rankTest(engine, state, ast, Lx, key, ctx) {
  const table = ctx.table,
    n = ctx.target;
  if (ctx.kernel.method !== 'test' && !ctx.kernel.test) return null;
  const start = state.lines[0].L;
  const rel = table.nodes[Lx.t]?.relation;
  if (!rel || Lx.k.length !== 2 || !isRelation(start, table)) return null;
  const B = engine.builtins ?? {};
  const tests = state.lines.flatMap((l) => (l.rank ? [l.rank] : []));
  const holdsAt = (k) => evaluate(start, { ...(ctx.fixed ?? {}), [n]: num(k) }, table) === true;
  // conclusion « n ≥ c »
  const [a, b] = Lx.k;
  if (
    a.t === 'Sym' &&
    a.name === n &&
    b.t === 'Num' &&
    Number(b.q) === 1 &&
    ['ge', 'gt', 'le', 'lt'].includes(rel)
  ) {
    if (!equivRelation(start, Lx, { table, target: n, hyps: ctx.hyps, fixed: ctx.fixed }))
      return null;
    const c = Number(b.p) + (rel === 'gt' ? 1 : rel === 'lt' ? -1 : 0);
    const up = rel === 'ge' || rel === 'gt';
    const ok =
      tests.some((t) => t.k === c && t.holds) &&
      tests.some((t) => t.k === (up ? c - 1 : c + 1) && !t.holds);
    if (!ok) return null;
    const rule = B.integer_bound;
    const gestures = rule ? [gestureOf({ rule, ast: Lx }, ctx)] : [];
    const goal = isGoal(Lx, key, ctx);
    return {
      status: goal ? 'goal' : 'step',
      kind: 'step',
      gestures,
      hora: gestures.reduce((s0, g) => s0 + (g.hora || 0), 0),
      message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
    };
  }
  // test d'un rang : le membre de gauche du départ avec n = k, et sa valeur (exacte ou arrondie)
  if (!['eq', 'approx'].includes(rel) || symbols(Lx).has(n)) return null;
  const sideKey = keyOf(light(ast.k[0], table));
  for (const side of start.k) {
    if (!symbols(side).has(n)) continue;
    for (let k = 0; k <= 400; k++) {
      const sub = (m) =>
        m.t === 'Sym' && m.name === n ? num(k) : isLeaf(m) ? m : { ...m, k: m.k.map(sub) };
      if (keyOf(light(sub(side), table)) !== sideKey) continue;
      const e = toF(evaluate(ast.k[0], { ...(ctx.fixed ?? {}) }, table));
      const t = toF(evaluate(ast.k[1], {}, table));
      const places = writtenPlaces(ast.k[1]);
      const good =
        rel === 'eq'
          ? Math.abs(e - t) <= 1e-9 * Math.max(1, Math.abs(e))
          : places !== null && roundsTo(e, t, places);
      if (!good)
        return {
          status: 'wrong',
          kind: null,
          message: 'Ce n’est pas la bonne valeur : refais le calcul pour ce rang.',
        };
      const rule = B.test_rank;
      const gestures = rule ? [gestureOf({ rule, ast: Lx }, ctx)] : [];
      return {
        status: 'step',
        kind: 'step',
        gestures,
        hora: gestures.reduce((s0, g) => s0 + (g.hora || 0), 0),
        message: `Juste : ${labelsOf(gestures)}.`,
        rank: { k, holds: holdsAt(k) },
      };
    }
  }
  return null;
}

function approxSolution(engine, state, ast, Lx, key, ctx) {
  const table = ctx.table,
    x = ctx.target;
  if (table.nodes[ast.t]?.relation !== 'approx' || ast.k.length !== 2) return null;
  const [a, b] = ast.k;
  if (!(a.t === 'Sym' && a.name === x) || symbols(b).has(x)) return null;
  const bw = ctx.degrees ? degreeWorld(b, ctx) : b;
  const places = writtenPlaces(bw);
  if (places === null) return null;
  const target = toF(evaluate(bw, {}, table));
  const exact = [];
  const collect = (L) => {
    const sp = table.nodes[L.t];
    if (sp?.connective) return L.k.forEach(collect);
    if (sp?.relation !== 'eq' || L.k.length !== 2) return;
    const [l, r] = L.k;
    if (l.t === 'Sym' && l.name === x && !symbols(r).has(x)) {
      const e = toF(
        evaluate(ctx.degrees ? degreeWorld(r, ctx) : r, { ...(ctx.fixed ?? {}) }, table),
      );
      if (Number.isFinite(e)) exact.push(e);
    }
  };
  for (const l of state.lines) if (l.status !== 'given') collect(l.L);
  if (!exact.length) return null;
  const ok = exact.some((e) => roundsTo(e, target, places));
  const rule = engine.builtins?.round;
  if (!ok)
    return {
      status: 'wrong',
      kind: null,
      message:
        'Ce n’est pas le bon arrondi : arrondis la valeur exacte au nombre de décimales demandé.',
    };
  const goal = isGoal(Lx, key, ctx);
  const gestures = rule ? [gestureOf({ rule, ast: Lx }, ctx)] : [];
  return {
    status: goal ? 'goal' : 'step',
    kind: 'step',
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
  };
}
// ── Conditions d'existence (lot I3) ──────────────────────────────────────────
// « x > 0 » pour ln(x), « x ≥ 0 » pour √x, « x − 3 ≥ 0 » quand √(x + 3) = x − 3 : écrite, une condition restreint
// l'étude (une ligne juste seulement sous les conditions est juste) ; la réponse en tient compte (« 0 < x < 1 »).
const conditionsOf = (state) => state.lines.filter((l) => l.cond).map((l) => l.L);

function conditionLine(engine, state, Lx, ctx) {
  if (!ctx.conds?.length || !relationParts(Lx, ctx.table)) return null;
  const table = ctx.table;
  const hit = ctx.conds.some((c) =>
    equivRelation(c, Lx, {
      table,
      target: ctx.target,
      hyps: ctx.hyps,
      fixed: ctx.fixed,
      domain: null,
    }),
  );
  if (!hit) return null;
  const rule = engine.builtins?.condition;
  const gestures = rule ? [gestureOf({ rule, ast: Lx }, ctx)] : [];
  return {
    status: 'step',
    kind: 'step',
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: `Juste : ${labelsOf(gestures)}.`,
    cond: true,
  };
}

function conditionKeep(engine, state, Lx, key, where, ctx) {
  const table = ctx.table;
  const andT = Object.keys(table.nodes).find((t) => table.nodes[t].connective === 'and');
  const rule = engine.builtins?.condition_keep;
  if (!andT || !rule) return null;
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.cond || l.quad || !relationParts(l.L, table)) continue;
    const both = { t: andT, k: [...where, l.L] };
    if (
      !equivRelation(both, Lx, {
        table,
        target: ctx.target,
        hyps: ctx.hyps,
        fixed: ctx.fixed,
        domain: null,
      })
    )
      continue;
    const goal = isGoal(Lx, key, ctx);
    const gestures = [gestureOf({ rule, ast: Lx }, ctx)];
    return {
      status: goal ? 'goal' : 'step',
      kind: 'step',
      from: i,
      gestures,
      hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
      message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
    };
  }
  return null;
}

// ── Inconnue auxiliaire (lot I3) ────────────────────────────────────────────
const replaceSym = (n, name, by) =>
  n.t === 'Sym' && n.name === name
    ? by
    : isLeaf(n)
      ? n
      : { ...n, k: n.k.map((c) => replaceSym(c, name, by)) };

// « Poser X = eˣ » : la première ligne en X (juste, déjà vérifiée) ; « Remplacer X par eˣ » : une ligne en x qui
// est une ligne en X déjà écrite où X est remplacé.
function auxGesture(engine, state, Lx, key, inAux, ctx) {
  const table = ctx.table,
    B = engine.builtins ?? {};
  const name = ctx.aux.name;
  let rule = null,
    from = state.lines.length - 1;
  if (inAux) {
    if (!state.lines.some((l) => symbols(l.L).has(name))) rule = B.aux_pose;
  } else {
    for (let i = state.lines.length - 1; i >= 0 && !rule; i--) {
      const l = state.lines[i];
      if (!symbols(l.L).has(name)) continue;
      if (keyOf(light(replaceSym(l.ast, name, ctx.aux.sub), table)) === key) {
        rule = B.aux_back;
        from = i;
      }
    }
  }
  if (!rule) return null;
  const goal = isGoal(Lx, key, ctx);
  const gestures = [gestureOf({ rule, ast: Lx }, ctx)];
  return {
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from,
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
  };
}

// ── Second degré (lot I3) ───────────────────────────────────────────────────
// Sur une feuille « résoudre », les grandeurs du trinôme se nomment : Δ (discriminant), x₁, x₂ (racines), x₀ (racine
// double) — l'indice suit le nom de l'inconnue (p₁, BC₂). Elles se calculent pour le trinôme de la dernière
// équation de degré 2 écrite. Une fois les racines écrites, l'ensemble des solutions se lit (« Les solutions sont
// les racines », « Signe du trinôme »), puis on garde celles qui conviennent (hypothèses de l'exercice).
const QUAD_INDEX = { '₀': '0', '₁': '1', '₂': '2' };
function quadRole(name, x) {
  if (name === 'Δ') return 'delta';
  const idx = name.length === x.length + 1 && name.startsWith(x) ? QUAD_INDEX[name.at(-1)] : null;
  return idx ?? null;
}
function trinomialOf(state, ctx) {
  const table = ctx.table,
    x = ctx.target;
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i];
    if (l.quad) continue;
    const L = l.L;
    if (!table.nodes[L.t]?.relation || L.k.length !== 2) continue;
    const [u, v] = L.k;
    const du = polyDeg(u, x, table),
      dv = polyDeg(v, x, table);
    if (du === null || dv === null || Math.max(du, dv) !== 2) continue;
    const f = (t) => {
      const e = { ...(ctx.fixed ?? {}), [x]: num(t) };
      return toF(evaluate(u, e, table)) - toF(evaluate(v, e, table));
    };
    const c = f(0),
      a = (f(1) + f(-1)) / 2 - c,
      b = (f(1) - f(-1)) / 2;
    if (![a, b, c].every(Number.isFinite) || Math.abs(a) < 1e-12) continue;
    if (Math.abs(f(2) - (4 * a + 2 * b + c)) > 1e-9 * Math.max(1, Math.abs(f(2)))) continue;
    const d = b * b - 4 * a * c;
    const delta = Math.abs(d) < 1e-12 * Math.max(1, b * b) ? 0 : d;
    const roots =
      delta < 0
        ? []
        : delta === 0
          ? [-b / (2 * a)]
          : [(-b - Math.sqrt(delta)) / (2 * a), (-b + Math.sqrt(delta)) / (2 * a)];
    return { a, b, c, delta, roots, from: i };
  }
  return null;
}
const nearly = (u, v) => Math.abs(u - v) <= 1e-9 * Math.max(1, Math.abs(u), Math.abs(v));

function quadLine(engine, state, ast, ctx) {
  const table = ctx.table,
    x = ctx.target;
  if (table.nodes[ast.t]?.relation !== 'eq' || ast.k.length !== 2) return null;
  const [head, rhs] = ast.k;
  if (head.t !== 'Sym' || symbols(rhs).has(x)) return null;
  const role = quadRole(head.name, x);
  if (!role) return null;
  const tri = trinomialOf(state, ctx);
  if (!tri) return null;
  const B = engine.builtins ?? {};
  const deltaLine = state.lines.find((l) => l.quad?.name === 'Δ');
  if (symbols(rhs).has('Δ') && !deltaLine)
    return { status: 'wrong', kind: null, message: 'Calcule d’abord Δ.' };
  const env = { ...(ctx.fixed ?? {}), ...(deltaLine ? { Δ: deltaLine.quad.value } : {}) };
  const v = toF(evaluate(rhs, env, table));
  if (!Number.isFinite(v)) return null;
  let ok;
  if (role === 'delta') ok = nearly(v, tri.delta);
  else if (role === '0') ok = tri.delta === 0 && nearly(v, tri.roots[0]);
  else ok = tri.delta > 0 && tri.roots.some((r) => nearly(v, r));
  if (!ok)
    return {
      status: 'wrong',
      kind: null,
      message:
        role === 'delta'
          ? 'Ce n’est pas le discriminant : Δ = b² − 4ac.'
          : tri.delta < 0
            ? 'Δ < 0 : le trinôme n’a pas de racine réelle.'
            : 'Ce n’est pas une racine du trinôme : x₁ = (−b − √Δ) ÷ (2a), x₂ = (−b + √Δ) ÷ (2a).',
    };
  const again = state.lines.some((l) => l.quad?.name === head.name);
  const rule = again ? B.fold : role === 'delta' ? B.quad_discriminant : B.quad_root;
  const gestures = rule ? [gestureOf({ rule, ast: light(ast, table) }, ctx)] : [];
  return {
    status: 'step',
    kind: 'step',
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: `Juste : ${labelsOf(gestures)}.`,
    quad: { name: head.name, value: v },
  };
}

// valeurs des bornes d'un ensemble de solutions (« x = 2 ou x = 3 », « −2 < x < 0,5 ») et nature (eq / ineq)
function solutionBounds(L, x, ctx) {
  const table = ctx.table;
  const out = { values: [], eq: true };
  const walk = (n) => {
    const sp = table.nodes[n.t];
    if (sp?.connective) return n.k.forEach(walk);
    if (sp?.relation !== 'eq') out.eq = false;
    for (const side of n.k)
      if (!(side.t === 'Sym' && side.name === x))
        out.values.push(toF(evaluate(side, { ...(ctx.fixed ?? {}) }, table)));
  };
  walk(L);
  return out;
}

function quadConclusion(engine, state, Lx, key, ctx) {
  const table = ctx.table,
    x = ctx.target,
    B = engine.builtins ?? {};
  if (!isSolutionSet(Lx, x, table)) return null;
  const roots = state.lines.filter((l) => l.quad && l.quad.name !== 'Δ').map((l) => l.quad.value);
  const mine = solutionBounds(Lx, x, ctx);
  if (!mine.values.every(Number.isFinite)) return null;
  const inSet = (vals, set) => vals.every((v) => set.some((r) => nearly(v, r)));
  let rule = null,
    from = state.lines.length - 1;
  // garder ce qui convient : une partie des solutions déjà écrites (ou des racines)
  if (mine.eq) {
    for (let i = state.lines.length - 1; i >= 0 && !rule; i--) {
      const l = state.lines[i];
      if (l.quad || !isSolutionSet(l.L, x, table)) continue;
      const prev = solutionBounds(l.L, x, ctx);
      if (prev.eq && prev.values.length > mine.values.length && inSet(mine.values, prev.values)) {
        rule = B.keep_valid;
        from = i;
      }
    }
  }
  if (!rule && roots.length) {
    const uniq = roots.filter((r, i) => roots.findIndex((s) => nearly(r, s)) === i);
    if (mine.eq && inSet(mine.values, uniq) && inSet(uniq, mine.values)) rule = B.quad_solutions;
    else if (mine.eq && inSet(mine.values, uniq) && mine.values.length < uniq.length)
      rule = B.keep_valid;
    else if (!mine.eq && inSet(mine.values, uniq)) rule = B.quad_sign;
  }
  if (!rule) return null;
  const goal = isGoal(Lx, key, ctx);
  const gestures = [gestureOf({ rule, ast: Lx }, ctx)];
  return {
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from,
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
  };
}

// ── Équations trigonométriques (kernel.method = 'trig') ─────────────────────────────────────────────────
// Les étapes du cours : valeur remarquable (« sin(x) = sin(π ÷ 6) »), familles de solutions (« x = π ÷ 6 + 2kπ ou
// … »), lecture sur le cercle dans l'intervalle de l'énoncé, garder les solutions de l'intervalle, diviser par
// cos(x). La ligne est déjà vraie (même ensemble de solutions) : on nomme seulement le passage.
const TRIG_FNS = new Set(['cos', 'sin', 'tan']);
function trigCalls(n, table, out = []) {
  if (isLeaf(n)) return out;
  if (table.nodes[n.t]?.eval === 'call' && TRIG_FNS.has(table.syntax.functions[n.name]?.eval))
    out.push(n);
  n.k.forEach((c) => trigCalls(c, table, out));
  return out;
}
// « x = π ÷ 3 ou x = −π ÷ 3 », « π ÷ 2 ≤ x ≤ 3π ÷ 2 » : x face à des nombres (π compris)
function isConstSet(L, x, table) {
  const sp = table.nodes[L.t];
  if (sp?.connective) return L.k.every((c) => isConstSet(c, x, table));
  if (!['lt', 'le', 'gt', 'ge', 'eq', 'ne'].includes(sp?.relation)) return false;
  const isX = (n) => n.t === 'Sym' && n.name === x;
  const isC = (n) => [...symbols(n)].every((v) => table.syntax.constants?.[v]);
  if (!L.k.some(isX)) return false;
  return L.k.every((n) => isX(n) || isC(n));
}
const intVarsOf = (ctx) =>
  (ctx.hyps ?? [])
    .filter((h) => h.of !== ctx.target && (h.kind === 'integer' || h.kind === 'natural'))
    .map((h) => h.of);
function trigShape(L, ctx) {
  const table = ctx.table,
    x = ctx.target;
  if (!symbols(L).has(x)) return null;
  const ks = intVarsOf(ctx);
  if (ks.some((k) => symbols(L).has(k))) return { kind: 'family', ops: opsCount(L) };
  if (isConstSet(L, x, table)) return { kind: 'set', ops: opsCount(L) };
  const atoms = atomsOf(L, table);
  const calls = trigCalls(L, table);
  if (!calls.length) return { kind: 'other' };
  const fns = new Set(calls.map((c) => table.syntax.functions[c.name].eval));
  if (atoms.length === 1) {
    const { l, r } = atoms[0];
    const isF = (n) =>
      table.nodes[n.t]?.eval === 'call' && TRIG_FNS.has(table.syntax.functions[n.name]?.eval);
    if (isF(l) && isF(r) && l.name === r.name && symbols(l.k[0]).has(x) && !symbols(r.k[0]).has(x))
      return { kind: 'value', fns };
  }
  return { kind: 'trig', fns };
}
// coefficient de x devant lequel on isole (« 2x = … » → 2) : la première écriture d'un membre en x
function leftOfFamily(L, ctx) {
  const a = atomsOf(L, ctx.table)[0];
  return a ? keyOf(a.l) : null;
}

function trigGesture(engine, state, Lx, key, ctx) {
  if (ctx.kernel?.method !== 'trig') return null;
  const B = engine.builtins ?? {};
  const to = trigShape(Lx, ctx);
  if (!to) return null;
  const hasDomain = (ctx.hyps ?? []).some((h) => h.kind === 'domain');
  let rule = null,
    from = -1;
  for (let i = state.lines.length - 1; i >= 0 && !rule; i--) {
    const l = state.lines[i];
    if (l.kind === 'kept' || l.status === 'useless') continue;
    const fr = trigShape(l.L, ctx);
    if (!fr) continue;
    if (to.kind === 'value' && fr.kind === 'trig') rule = B.trig_value;
    else if (to.kind === 'trig' && fr.kind === 'trig' && to.fns.has('tan') && !fr.fns.has('tan'))
      rule = B.trig_divide;
    else if (to.kind === 'family' && (fr.kind === 'value' || fr.kind === 'trig'))
      rule = B.trig_families;
    else if (to.kind === 'family' && fr.kind === 'family') {
      if (leftOfFamily(Lx, ctx) !== leftOfFamily(l.L, ctx)) rule = B.trig_isolate;
      else if (to.ops < fr.ops) rule = engine.builtins?.fold;
    } else if (to.kind === 'set' && hasDomain && (fr.kind === 'value' || fr.kind === 'trig'))
      rule = B.trig_circle;
    else if (to.kind === 'set' && fr.kind === 'family') rule = B.trig_interval;
    else if (to.kind === 'set' && fr.kind === 'set' && to.ops < fr.ops)
      rule = engine.builtins?.fold;
    if (rule) from = i;
  }
  if (!rule) return null;
  const goal = isGoal(Lx, key, ctx);
  const gestures = [gestureOf({ rule, ast: Lx }, ctx)];
  return {
    status: goal ? 'goal' : 'step',
    kind: 'step',
    from,
    gestures,
    hora: gestures.reduce((s0, g) => s0 + (g.implicit ? 0 : g.hora || 0), 0),
    message: goal ? MSG.goal : `Juste : ${labelsOf(gestures)}.`,
  };
}

function shapeAdvances(fromL, toL, ctx) {
  if (!ctx.target || ctx.goal?.kind !== 'solve') return false;
  const v = lineVar(toL, ctx);
  if (lineVar(fromL, ctx) !== v) return false;
  const a = eqShape(fromL, v, ctx.table),
    b = eqShape(toL, v, ctx.table);
  return !!a && !!b && a.rank >= 10 && b.rank < a.rank;
}
// l'inconnue d'une ligne : l'inconnue auxiliaire si la ligne est écrite en X, sinon celle de l'exercice
const lineVar = (L, ctx) => (ctx.aux && symbols(L).has(ctx.aux.name) ? ctx.aux.name : ctx.target);

function shapeGesture(engine, state, targetL) {
  const { ctx } = state,
    table = ctx.table,
    x = lineVar(targetL, ctx);
  if (!x || ctx.goal?.kind !== 'solve') return null;
  const tb = eqShape(targetL, x, table);
  if (!tb) return null;
  const B = engine.builtins ?? {};
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const l = state.lines[i].L;
    const ta = eqShape(l, x, table);
    if (!ta || tb.rank >= ta.rank) continue;
    let name = null;
    if (
      ta.rank === 30 &&
      ta.kind === 'quotient' &&
      tb.rank <= 10 &&
      (tb.rank >= 3 || ctx.maxGestures >= 2)
    )
      name = 'zero_quotient';
    else if (ta.rank === 30 && tb.rank >= 5) name = 'clear_denominator';
    else if (ta.kind === 'sqrt' && tb.rank < 26) name = 'square_sides';
    else if (ta.rank === 25 && tb.rank === 20) name = 'move_terms';
    else if ((ta.rank === 25 || ta.rank === 20) && tb.rank === 10) name = 'poly_factor';
    // produit nul : « A = 0 ou B = 0 » (résoudre chaque équation est un autre geste, D7)
    // (au lycée, maxGestures ≥ 2 : résoudre les facteurs du premier degré dans la foulée est permis)
    else if (
      ta.rank === 10 &&
      ta.kind === 'product' &&
      tb.rank <= 5 &&
      (tb.rank >= 3 || ctx.maxGestures >= 2)
    )
      name = 'zero_product';
    else if (ta.rank === 10 && ta.kind !== 'product' && tb.rank <= 5)
      name = ta.kind === 'power' ? 'zero_power' : 'root_power';
    if (!name || !B[name]) continue;
    const where = conditionsOf(state);
    if (
      !equivRelation(l, targetL, {
        table,
        target: x,
        hyps: ctx.hyps,
        fixed: ctx.fixed,
        ...(where.length ? { where } : {}),
      })
    )
      continue;
    return { from: i, chain: [{ rule: B[name], ast: targetL }] };
  }
  return null;
}

function numbersOf(n, out = new Set()) {
  if (n.t === 'Num') {
    if (n.q === 1 && n.p !== 0) out.add(Math.abs(n.p));
  } else if (!isLeaf(n)) n.k.forEach((c) => numbersOf(c, out));
  return out;
}
function addRatioCandidates(free, targetL, lines, table) {
  // seulement pour une règle dont le motif peut s'appliquer à une ligne gardée (type de sa racine présent)
  const types = new Set();
  for (const l of lines)
    subtrees(l.L, (n) => {
      if (!isLeaf(n)) types.add(n.t);
    });
  const one = [...free.keys()].filter(
    (r) => (r.free || []).length === 1 && r.lhs && types.has(prepareRule(r, table).lhs.t),
  );
  if (!one.length) return;
  const tn = numbersOf(targetL),
    sn = new Set();
  for (const l of lines) numbersOf(l.L, sn);
  const ks = new Set();
  for (const u of tn)
    for (const w of sn) if (u % w === 0 && u / w >= 2 && u / w <= 100) ks.add(u / w);
  for (const r of one) {
    const v = r.free[0],
      list = free.get(r);
    const have = new Set(list.map((b) => keyOf(b[v])));
    for (const k of [...ks].sort((a, b) => a - b).slice(0, 8))
      if (!have.has(String(k))) list.push({ [v]: num(k) });
  }
}

function expand(engine, L, ctx, mode, K = ctx.K, fsig = '', only) {
  const ck =
    mode +
    '|' +
    (only === undefined ? '' : 'I:' + only.map((r) => r.id).join(',') + '|') +
    fsig +
    '|' +
    keyOf(L);
  let out = ctx.expand.get(ck);
  if (out) return out;
  const filter = only === undefined ? null : (r) => only.includes(r) || isImplicit(r, ctx);
  out = rewriteAll(L, engine, K, mode, filter);
  if (mode === 'rewrite') {
    for (const rule of Object.values(engine.builtins || {})) {
      const s = STRATEGIES[rule.builtin];
      if (!s || (filter && !filter(rule))) continue;
      for (const ast of s(L, engine.table, K)) out.push({ ast, rule });
    }
  }
  if (ctx.expand.size > 20000) ctx.expand.clear();
  ctx.expand.set(ck, out);
  return out;
}

function subtrees(n, f) {
  f(n);
  if (!isLeaf(n)) for (const c of n.k) subtrees(c, f);
}
function bag(L) {
  const m = new Map();
  subtrees(L, (n) => {
    const k = keyOf(n);
    m.set(k, (m.get(k) || 0) + 1);
  });
  return m;
}
// distance = différence symétrique des multi-ensembles de sous-arbres (clés canoniques) ; on prend aussi
// celle de la forme « tous calculs faits », pour qu'un geste dont le résultat se calcule vers la cible
// (2/3 → (2 × 5)/(3 × 5) vers 10/15) ne soit pas écarté du faisceau
function dist(L, tb, table) {
  const d0 = dist0(L, tb);
  if (!table) return d0;
  const F = foldAll(L, table);
  return F === L ? d0 : Math.min(d0, dist0(F, tb) + 1);
}
function dist0(L, tb) {
  const b = bag(L);
  let d = 0;
  for (const [k, c] of b) d += Math.abs(c - (tb.get(k) || 0));
  for (const [k, c] of tb) if (!b.has(k)) d += c;
  return d;
}

function gestureOf({ rule, ast }, ctx) {
  let g = { ruleId: rule.id, label: rule.label || rule.key, why: rule.why, hora: rule.hora ?? 0 };
  if (rule.builtin === 'formula') {
    const p = ctx.preds.find((x) => x.key === keyOf(ast));
    if (p)
      g = {
        ruleId: rule.id,
        label: p.label || rule.label || rule.key,
        why: p.why || rule.why,
        hora: p.hora ?? rule.hora ?? 0,
      };
  }
  if (isImplicit(rule, ctx)) {
    g.implicit = true;
    g.hora = 0;
  }
  return g;
}

// ── Mesure de progrès (« dette ») et but ──────────────────────────────────
// solve    : par relation atomique, « cible ⋈ expr » isolée → 0,1 × taille(expr) + 2 × calculs restants ;
//            sinon 10 × occurrences de la cible + taille des membres qui la contiennent + 1 si la cible
//            n'est qu'à droite + 2 × calculs / regroupements restants.
// simplify / evaluate : « cible = expr » → 4 × produits non développés + 2 × calculs restants
//            + 2 × regroupements possibles + 2 × grandeurs données non remplacées + 0,1 × taille ;
//            pas de la forme « cible = … » : +50. Une ligne qui n'est pas une relation : 1000.
function atomsOf(n, table, out = []) {
  const rp = relationParts(n, table);
  if (!rp) return out;
  if (rp.kind === 'conn') {
    n.k.forEach((c) => atomsOf(c, table, out));
    return out;
  }
  for (let i = 0; i + 1 < n.k.length; i++) out.push({ l: n.k[i], r: n.k[i + 1] });
  return out;
}
function occW(n, name, table, w = 1) {
  if (n.t === 'Sym') return n.name === name ? w : 0;
  if (isLeaf(n)) return 0;
  const e = n.k[1];
  if (table.nodes[n.t]?.eval === 'pow' && e?.t === 'Num' && e.q === 1 && e.p >= 2)
    return occW(n.k[0], name, table, w * (e.p + 1));
  // l'inconnue en exposant (« 1,04ⁿ ») pèse plus : la faire descendre (composer par ln) est un progrès
  if (table.nodes[n.t]?.eval === 'pow' && !symbols(n.k[0]).has(name))
    return occW(e, name, table, w * 3);
  return n.k.reduce((s, c) => s + occW(c, name, table, w), 0);
}
function occ(n, name) {
  let c = 0;
  subtrees(n, (x) => {
    if (x.t === 'Sym' && x.name === name) c++;
  });
  return c;
}
const isTarget = (n, ctx) => n.t === 'Sym' && n.name === ctx.target;

// Factorielles d'un quotient dont les arguments diffèrent d'un entier (« (n + 1)! ÷ n! », « 10! ÷ 8! ») : l'écart
// mesure les facteurs à sortir avant de simplifier. Faire apparaître la factorielle du bas (« (n + 1) × n! ÷ n! »)
// est donc un progrès.
function factGap(n, table) {
  const R = roles(table);
  const fact = Object.keys(table.nodes).find((t) => table.nodes[t].eval === 'fact');
  if (!fact) return 0;
  const facts = (x) => {
    const out = [];
    visit(x, (y) => {
      if (y.t === fact) out.push(y.k[0]);
    });
    return out;
  };
  let g = 0;
  visit(n, (x) => {
    if (x.t !== R.div) return;
    const top = facts(x.k[0]),
      bot = facts(x.k[1]);
    for (const a of top)
      for (const b of bot) {
        const d = intGap(a, b, table);
        if (d) g += Math.min(6, Math.abs(d));
      }
  });
  return g;
}

function measure(L, ctx) {
  const table = ctx.table;
  if (!isRelation(L, table)) return 1000;
  const T = ctx.goal.kind === 'solve' ? lineVar(L, ctx) : ctx.target;
  let d = 0;
  for (const a of atomsOf(L, table)) {
    if (ctx.goal.kind === 'solve') {
      const ol = occ(a.l, T),
        or = occ(a.r, T);
      const fl = features(a.l, table),
        fr = features(a.r, table);
      if (a.l.t === 'Sym' && a.l.name === T && or === 0)
        d += 0.1 * fr.size + 2 * fr.foldable + 2 * fr.collectable;
      else {
        // une puissance de l'inconnue pèse son degré + 1 (x² − 4 = 0 est plus loin du but que (x − 2)(x + 2) = 0)
        d +=
          10 * (occW(a.l, T, table) + occW(a.r, T, table)) +
          (ol ? fl.size : 0) +
          (or ? fr.size : 0) +
          (ol === 0 && or > 0 ? 1 : 0) +
          2 * (fl.foldable + fr.foldable + fl.collectable + fr.collectable);
      }
    } else {
      const iso = isTarget(a.l, ctx) && occ(a.r, T) === 0;
      const e = iso ? a.r : a.l;
      const f = features(maskCalculus(e, table), table);
      const givenLeft = [...symbols(e)].filter((s) => ctx.given[s]).length;
      // « développe » : développer est LE progrès — un produit ou une puissance non développés pèsent plus que les
      // calculs que leur développement fait apparaître ((a + b)² → a² + 2ab + b² crée 3 calculs mais avance)
      const wExp = ctx.form === 'expanded' ? 20 : 4;
      d +=
        (iso ? 0 : 50) +
        wExp * f.nonExpanded +
        2 * f.foldable +
        2 * f.collectable +
        3 * givenLeft +
        6 * factGap(e, table) +
        0.1 * f.size +
        pendingCalculus(e, table);
      if (!iso) d += 0.1 * features(a.r, table).size;
    }
  }
  return d;
}

// Feuille « formule » qui cherche plusieurs grandeurs (model.goal = ['x', 'y']) : la réponse n'est atteinte que
// quand CHACUNE a sa valeur finale écrite (comme l'ancien moteur) ; la première trouvée est une étape.
function multiGoal(ctx) {
  return ctx.goal.kind === 'evaluate' && (ctx.model.goal ?? []).length > 1;
}
function finalValueFor(L, name, ctx) {
  const table = ctx.table,
    rel = table.nodes[L.t]?.relation;
  if (!['eq', 'approx'].includes(rel) || L.k.length !== 2) return false;
  const [a, b] =
    L.k[0].t === 'Sym' && L.k[0].name === name
      ? L.k
      : L.k[1].t === 'Sym' && L.k[1].name === name
        ? [L.k[1], L.k[0]]
        : [];
  if (!a || symbols(b).size) return false;
  const f = features(b, table);
  return !f.foldable && !f.collectable;
}
function allGoalsFound(state, Lx, ctx) {
  const names = ctx.model.goal;
  const lines = [...state.lines.map((l) => l.L), Lx];
  if (!names.some((n) => finalValueFor(Lx, n, ctx))) return false;
  return names.every((n) => lines.some((L) => L && finalValueFor(L, n, ctx)));
}

function isGoal(L, key, ctx) {
  if (key === ctx.answerKey) return true;
  const table = ctx.table,
    T = ctx.target;
  // réponse écrite à l'envers (« 16 = n ») : c'est la même réponse que « n = 16 »
  if (
    table.nodes[L.t]?.relation === 'eq' &&
    L.k.length === 2 &&
    isTarget(L.k[1], ctx) &&
    !isTarget(L.k[0], ctx)
  ) {
    const S = { ...L, k: [L.k[1], L.k[0]] };
    if (keyOf(S) === ctx.answerKey) return true;
    L = S;
  }
  // réponse arrondie (« d ≈ 2,83 ») : la valeur exacte n'est pas encore la réponse, il reste à arrondir
  const approx = (n) => !!n && table.nodes[n.t]?.relation === 'approx';
  if (approx(ctx.answerL) && !approx(L)) return false;
  const atoms = atomsOf(L, table);
  if (!atoms.length || !T) return false;
  for (const a of atoms) {
    if (!isTarget(a.l, ctx) || occ(a.r, T) > 0) return false;
    const f = features(a.r, table);
    if (f.foldable || f.collectable || pendingCalculus(a.r, table)) return false;
    // une somme de vecteurs encore réductible par Chasles (ou un −AB⃗) n'est pas une réponse finale
    if (
      ctx.vectors &&
      (STRATEGIES.chasles(a.r, table).length || STRATEGIES.vector_opposite(a.r, table).length)
    )
      return false;
    if (ctx.goal.kind !== 'solve' && atoms.length !== 1) return false;
    if (ctx.goal.kind === 'simplify' && ctx.form === 'expanded' && f.nonExpanded) return false;
    if (ctx.goal.kind === 'evaluate' && [...symbols(a.r)].some((s) => !table.syntax.constants[s]))
      return false;
  }
  // « Â = arccos(1 ÷ 2) » n'est pas encore « Â = 60 » : il reste la valeur exacte
  if (ctx.degrees && ctx.answerL) {
    const arcs = (n) =>
      trigCalls(n, table).length +
      (function c(m) {
        if (isLeaf(m)) return 0;
        const f = table.nodes[m.t]?.eval === 'call' ? table.syntax.functions[m.name]?.eval : null;
        return (
          (Object.values(INVERSE).includes(f ?? '') ? 1 : 0) + m.k.reduce((s0, k) => s0 + c(k), 0)
        );
      })(n);
    if (arcs(L) > arcs(ctx.answerL)) return false;
  }
  // une famille « + 2kπ » n'est pas la réponse quand on demande les solutions d'un intervalle
  const ks = intVarsOf(ctx);
  if (
    ctx.answerL &&
    ks.some((k) => symbols(L).has(k)) &&
    !ks.some((k) => symbols(ctx.answerL).has(k))
  )
    return false;
  // feuille à conditions d'existence : la réponse doit être équivalente SANS elles (« x < 1 » n'est pas « 0 < x < 1 »)
  if (
    ctx.conds?.length &&
    ctx.answerL &&
    !equivRelation(ctx.answerL, L, {
      table,
      target: T,
      hyps: ctx.hyps,
      fixed: ctx.fixed,
      domain: null,
    })
  )
    return false;
  // forme factorisée : au moins autant de facteurs en x que la réponse (« (X − 1)(X² + X − 2) » se factorise encore)
  if (
    ctx.form === 'factored' &&
    ctx.answerL &&
    sameHeadRel(L, table) &&
    sameHeadRel(ctx.answerL, table)
  ) {
    const vs = [...symbols(ctx.answerL.k[1])].filter((x) => !table.syntax.constants[x]);
    if (vs.length === 1) {
      const want = polyFactorCount(ctx.answerL.k[1], vs[0], table),
        got = polyFactorCount(L.k[1], vs[0], table);
      if (want > 0 && got >= 0 && got < want) return false;
    }
  }
  // forme canonique : chaque lettre n'apparaît pas plus souvent que dans la réponse (« −2(x² − 2x) + 1 » n'est
  // pas encore « −2(x − 1)² + 3 »)
  if (ctx.form === 'canonical' && ctx.answerL) {
    const letters = [...symbols(ctx.answerL)].filter((v) => !table.syntax.constants[v] && v !== T);
    if (letters.some((v) => occ(L, v) > occ(ctx.answerL, v))) return false;
  }
  // même forme finale que la réponse, et pas plus lourde qu'elle (3 nœuds de marge)
  return measure(L, ctx) <= ctx.answerMeasure + 0.35;
}

export { measure as _measure, isGoal as _isGoal };

// Le moteur vu par une feuille (table étendue des fonctions abstraites de l'exercice), pour l'interface.
export function sheetEngine(engine, state) {
  return state?.ctx?.engine ?? engine;
}

// ── Comparaisons d'une feuille « formule » (« Δ > 0 », model.checks) ─────────────────────────────────────
// Même règle que l'ancien moteur (lot v5) : la comparaison se juge avec les valeurs ÉCRITES sur la feuille ;
// seule une comparaison de l'exercice (checks) rapporte des HORA, une autre vraie est « juste, mais à côté ».
const CMP_FLIP = { Lt: 'Gt', Gt: 'Lt', Le: 'Ge', Ge: 'Le' };
function cmpKeys(a, table) {
  const flip = { ...a, t: CMP_FLIP[a.t], k: [a.k[1], a.k[0]] };
  return [keyOf(light(a, table)), keyOf(light(flip, table))];
}
function writtenValue(state, name, table) {
  for (let i = state.lines.length - 1; i >= 0; i--) {
    const a = state.lines[i].ast;
    if (a?.t !== 'Eq') continue;
    const k = a.k;
    if (
      k[0].t === 'Sym' &&
      k[0].name === name &&
      [...symbols(k.at(-1))].every((n) => table.syntax.constants[n])
    )
      return true;
  }
  return false;
}
function comparisonLine(state, ast, ctx) {
  const table = ctx.table;
  const names = [...symbols(ast)].filter((n) => !table.syntax.constants[n]);
  if (!names.every((n) => n in ctx.world)) return null;
  const missing = names.find((n) => !writtenValue(state, n, table));
  if (missing)
    return {
      status: 'unjustified',
      kind: 'kept',
      message: `Calcule d’abord ${missing} : écris sa valeur avant de la comparer.`,
    };
  const [l, r] = ast.k.map((x) => toF(evaluate(x, ctx.world, table)));
  if (!Number.isFinite(l) || !Number.isFinite(r)) return null;
  const ok = { Lt: l < r, Gt: l > r, Le: l <= r, Ge: l >= r }[ast.t];
  if (!ok)
    return { status: 'wrong', kind: null, message: 'Ce n’est pas juste : compare les valeurs.' };
  const key = keyOf(light(ast, table));
  const c = ctx.checks.find((x) => x.keys.includes(key));
  if (!c)
    return { status: 'useless', kind: 'kept', message: 'Juste, mais ça ne t’avance pas ici.' };
  return {
    status: 'step',
    kind: 'step',
    gestures: [{ ruleId: 'check', label: c.label, why: c.why, hora: c.hora ?? 1 }],
    hora: c.hora ?? 1,
    message: `Juste : ${c.label}.`,
  };
}

// ── Écriture des monômes ──────────────────────────────────────────────────
// « x × x » et « x² », « 5 × x », « x × 5 » et « 5x » sont la même écriture d'un monôme : la clé les confond.
// Les nombres d'un produit ne sont jamais calculés (« 2 × 3x » reste 2 × 3 × x, pas 6x).
// Signature bon marché d'une ligne : nombre de termes de chaque membre (filtre avant monoKey).
function termSig(L, table) {
  const n = (m) => (table.nodes[m.t]?.eval === 'add' ? m.k.length : 1);
  return table.nodes[L.t]?.relation ? L.k.map(n).join(',') : String(n(L));
}

function monoKey(L, table, fold = false) {
  const ev = (n) => table.nodes[n.t]?.eval;
  const factors = (n, out) => {
    if (ev(n) === 'mul') {
      for (const k of n.k) if (!factors(k, out)) return false;
      return true;
    }
    if (n.t === 'Num') return (out.nums.push(n), true);
    if (n.t === 'Sym' && !table.syntax.constants[n.name])
      return ((out.pow[n.name] = (out.pow[n.name] ?? 0) + 1), true);
    if (ev(n) === 'pow' && n.k[1].t === 'Num' && n.k[1].q === 1 && n.k[1].p > 0 && n.k[1].p <= 4) {
      // fold : « 3² » est le nombre 9
      if (fold && n.k[0].t === 'Num') {
        let v = n.k[0];
        for (let i = 1; i < n.k[1].p; i++) v = Q.mul(v, n.k[0]);
        return (out.nums.push(v), true);
      }
      if (n.k[0].t === 'Sym' && !table.syntax.constants[n.k[0].name]) {
        out.pow[n.k[0].name] = (out.pow[n.k[0].name] ?? 0) + n.k[1].p;
        return true;
      }
      // « (3x)² » = « 3x × 3x » (une puissance d'un monôme)
      if (ev(n.k[0]) === 'mul') {
        for (let i = 0; i < n.k[1].p; i++) if (!factors(n.k[0], out)) return false;
        return true;
      }
    }
    return false;
  };
  const walk = (n) => {
    if (isLeaf(n)) return n;
    if (ev(n) === 'mul' || ev(n) === 'pow') {
      const out = { nums: [], pow: {} };
      if (factors(n, out) && (Object.keys(out.pow).length || (fold && out.nums.length > 0))) {
        // fold : le coefficient du monôme est calculé (« 4 × 3x » = « 12x », « 4 × 2 » = « 8 »)
        if (fold && out.nums.length) out.nums = [out.nums.reduce((a, b) => Q.mul(a, b))];
        // les nombres restent écrits (« 3 × 3 » n'est pas calculé) : seul l'ordre et les lettres sont réunis
        // « 1x » s'écrit « x »
        out.nums = out.nums
          .filter((x) => !(x.p === 1 && x.q === 1))
          .sort((a, b) => a.p / a.q - b.p / b.q);
        const names = Object.keys(out.pow).sort();
        const parts = names.map((x) =>
          out.pow[x] === 1
            ? { t: 'Sym', name: x }
            : {
                t: 'Pow',
                k: [
                  { t: 'Sym', name: x },
                  { t: 'Num', p: out.pow[x], q: 1 },
                ],
              },
        );
        const k = [...out.nums, ...parts];
        return k.length === 1 ? k[0] : { t: 'Mul', k };
      }
    }
    return { ...n, k: n.k.map(walk) };
  };
  return keyOf(light(walk(L), table));
}
